import axios, { AxiosError, AxiosInstance } from 'axios';
import type {
  AuthTokenResponse,
  LoginRequest,
  RegisterRequest,
  User,
  UserCreate,
  UserUpdate,
  ChangePasswordRequest,
  Entity,
  GroupAccount,
  EntityAccount,
  TrialBalance,
  TrialBalanceSummary,
  TrialBalanceUpload,
  ConsolidationRun,
  ConsolidationResult,
  JournalEntry,
  FinancialStatement,
  FinancialLineItem,
  NciReport,
  NciPipelineResult,
  Currency,
  FxRate,
  Period,
  IntercompanyTransaction,
  IntercompanySummary,
  IntercompanyReconciliationReport,
  IntercompanyEliminationTestResult,
  EntityCodeMapping,
  IcDispute,
  ValidateCodesResponse,
  PaginatedResponse,
  ApiError,
  SkillExecution,
  AuditLog,
  AuditSummary,
  TaxComputeResponse,
  EntityTaxDetail,
  QfzpEntityStatus,
  TPTransaction,
  TPSummary,
  Anomaly,
  AnomalySummary,
  BenfordAnalysis,
  ExportRequest,
  ERPConnector,
  Notification,
  ApprovalWorkflow,
  NLQueryResponse,
  XBRLMapping,
  XBRLValidationResult,
  BackgroundTask,
  SSOProvider,
  MFASetup,
  TaxReturn,
  ESGMetric,
  ESGDashboardData,
  ESGTarget,
  ForecastResult,
  GaapConversion,
  MarketplaceSkill,
  AuditBlock,
  AuditChainStats,
  ScenarioResult,
  TrialBalanceTranslationResponse,
  TranslationRequest,
  ConsolidationReviewMatrix,
  ConsolidationReviewSummary,
  EntityTypeLookup,
  EntityTypeLookupCreate,
  EntityStatusLookup,
  EntityStatusLookupCreate,
  ConsolidationMethodLookup,
  ConsolidationMethodLookupCreate,
  EntityHierarchy,
  EntityHierarchyCreate,
  HierarchyTreeNode,
  HierarchyNodeCreate,
  ConsolidationStatusSummary,
  TBLockResponse,
  SmartList,
  SmartListEntry,
  SmartListWithEntries,
  SmartListCreate,
  SmartListUsage,
  UdaDefinition,
  UdaDefinitionCreate,
  UdaDefinitionWithMembers,
  UdaMemberItem,
} from '@/types';
import { SkillStatus } from '@/types';
import { GATEWAY_TIMEOUT_MESSAGE, gatewayErrorMessage } from './gatewayError';

// --- Axios Instance ----------------------------------------------------------

/** Resolve API base URL. Bare absolute origins (e.g. https://app.example.com) become …/api/v1 so POSTs hit the API, not the SPA (which often returns 405 for POST). */
function resolveApiBaseUrl(): string {
  const raw = import.meta.env.VITE_API_BASE_URL;
  const fallback = '/api/v1';
  if (raw == null || String(raw).trim() === '') {
    return fallback;
  }
  let s = String(raw).trim().replace(/\/$/, '');
  if (s.startsWith('/')) {
    if (s === '/api') {
      return fallback;
    }
    return s;
  }
  try {
    const u = new URL(s);
    const p = (u.pathname || '').replace(/\/$/, '') || '';
    if (p === '' || p === '/') {
      u.pathname = '/api/v1';
      return u.toString().replace(/\/$/, '');
    }
    return s;
  } catch {
    return s;
  }
}

const BASE_URL = resolveApiBaseUrl();

const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// ---------------------------------------------------------------------------
// In-flight GET de-duplication
// ---------------------------------------------------------------------------
// Screens often resolve the same reference data from several places at once — e.g. the
// Data page loads each dimension's members for the pool AND its code↔id map, and both
// hit the same endpoint. Collapse identical CONCURRENT GETs into a single network
// request; the entry is removed once it settles, so subsequent calls still fetch fresh
// (no stale data). GETs are idempotent, so this is safe. Binary/stream downloads are
// skipped (a shared body can only be read once).
const _inflightGets = new Map<string, Promise<unknown>>();
const _rawGet = apiClient.get.bind(apiClient);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(apiClient as any).get = (url: string, config?: any): Promise<any> => {
  const responseType = config?.responseType;
  if (responseType && responseType !== 'json') return _rawGet(url, config);
  const key = `${url}::${config?.params ? JSON.stringify(config.params) : ''}`;
  const existing = _inflightGets.get(key);
  if (existing) return existing as Promise<any>;
  const p = _rawGet(url, config).finally(() => {
    _inflightGets.delete(key);
  });
  _inflightGets.set(key, p as Promise<unknown>);
  return p;
};

// Request interceptor: attach JWT
// Reporting reads legitimately outrun the global 30s on wide grids (a full
// BS/ICP preview computes thousands of rollup cells server-side — functional
// team 2026-09-21, staging report 25: "timeout of 30000ms exceeded"). Lift
// ONLY /reporting requests — 100s since 2026-10-01 (was 60s): the Cloudflare
// edge in front of the app answers 524 at ~100s anyway (raisable only on an
// Enterprise plan), so waiting longer buys nothing. A call that sets its own
// timeout keeps it (books 300s, renders) — the check for the inherited
// default distinguishes "nobody chose" from an explicit choice.
const REPORTING_TIMEOUT_MS = 100_000;

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.url?.startsWith('/reporting') && config.timeout === 30000) {
      config.timeout = REPORTING_TIMEOUT_MS;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * Where an expired session lands. The landing page is the front door and
 * carries the sign-in form, so it is the same destination as Sign out
 * (`useAuthStore.logout`) — an expired session and a deliberate sign-out should
 * not leave the user on two different screens.
 */
const SIGNED_OUT_LANDING = '/welcome';
/**
 * Already signed-out pages — redirecting from one of these would loop.
 * `/login` is here because it still exists as a redirect to the landing page
 * for old bookmarks; bouncing a 401 off it would just add a hop.
 */
const SIGNED_OUT_PATHS = [SIGNED_OUT_LANDING, '/login'];

function showSessionExpiredToast() {
  const existing = document.getElementById('session-expired-toast');
  if (existing) return;
  const toast = document.createElement('div');
  toast.id = 'session-expired-toast';
  toast.setAttribute('role', 'alert');
  Object.assign(toast.style, {
    position: 'fixed', top: '24px', right: '24px', zIndex: '99999',
    padding: '16px 24px', borderRadius: '8px', minWidth: '320px',
    background: '#DC2626', color: '#fff', fontFamily: 'system-ui, sans-serif',
    fontSize: '14px', fontWeight: '500', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    display: 'flex', alignItems: 'center', gap: '12px',
  });
  toast.innerHTML = '<span style="font-size:18px">&#9888;</span><span>Session expired — please log in again</span>';
  document.body.appendChild(toast);
  setTimeout(() => toast.remove(), 5000);
}

// Response interceptor: handle errors with auto-refresh on 401
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: any) => void }> = [];

function processQueue(error: any, token: string | null) {
  failedQueue.forEach((p) => {
    if (token) p.resolve(token);
    else p.reject(error);
  });
  failedQueue = [];
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as any;

    // Try to refresh token on 401 (but not for login/refresh endpoints)
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/refresh')
    ) {
      const refreshToken = localStorage.getItem('refresh_token');

      if (refreshToken) {
        if (isRefreshing) {
          // Queue this request until refresh completes
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve: (token: string) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(apiClient(originalRequest));
              },
              reject,
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const response = await apiClient.post<AuthTokenResponse>('/auth/refresh', {
            refresh_token: refreshToken,
          });
          const newToken = response.data.access_token;
          localStorage.setItem('auth_token', newToken);
          if (response.data.refresh_token) {
            localStorage.setItem('refresh_token', response.data.refresh_token);
          }
          processQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          localStorage.removeItem('auth_token');
          localStorage.removeItem('refresh_token');
          showSessionExpiredToast();
          if (!SIGNED_OUT_PATHS.includes(window.location.pathname)) {
            window.location.href = SIGNED_OUT_LANDING;
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      showSessionExpiredToast();
      if (!SIGNED_OUT_PATHS.includes(window.location.pathname)) {
        window.location.href = SIGNED_OUT_LANDING;
      }
    }

    // A blob-typed request (PDF/Excel download, book preview) still gets a
    // JSON body on failure, but `data` arrives as a Blob so `data.detail` is
    // undefined and every such error surfaced as the bare axios string
    // ("Request failed with status code 413") — the server's explanation was
    // read by nobody. Read the blob before building the ApiError; this
    // interceptor is already async (functional team 2026-08-24).
    let payload = error.response?.data as ApiError | Blob | string | undefined;
    if (payload instanceof Blob) {
      let text = '';
      try {
        text = await payload.text();
        payload = JSON.parse(text) as ApiError;
      } catch {
        payload = text || undefined; // not JSON — keep the raw text for the gateway check
      }
    }
    // Proxy/gateway failures (Cloudflare 52x, load-balancer 502 pages) never
    // surface the infrastructure's own wording — see gatewayError.ts.
    const gatewayMessage = gatewayErrorMessage(error.response?.status, payload);
    const body = (typeof payload === 'object' ? payload : undefined) as ApiError | undefined;

    // A request that never got a response (axios timeout, network drop) is NOT a
    // server failure — the backend may still be running the work. Without a
    // discriminator it is indistinguishable from a genuine 500 here, because
    // `error.response` is undefined in both branches of `status_code` below.
    // Callers that kick off long operations (integration run, consolidation)
    // branch on `is_timeout` to say "still running" instead of "failed".
    // NOTE: deliberately NOT ERR_NETWORK — that is connection-refused / DNS
    // failure / CORS block, i.e. the request never reached the server. Treating
    // it as "still running" would tell the user to wait for work that never
    // started.
    const isTimeout = !error.response &&
      (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT');

    const apiError: ApiError = {
      // A browser-side timeout reads like the edge one, never axios's raw
      // "timeout of 100000ms exceeded".
      detail: gatewayMessage
        ?? (isTimeout ? GATEWAY_TIMEOUT_MESSAGE : undefined)
        ?? (body?.detail || error.message || 'An unexpected error occurred'),
      status_code: error.response?.status || 500,
      errors: gatewayMessage ? undefined : body?.errors,
      code: error.code,
      is_timeout: isTimeout,
    };

    return Promise.reject(apiError);
  },
);

// --- Auth API ----------------------------------------------------------------

export const authApi = {
  login: async (data: LoginRequest): Promise<AuthTokenResponse> => {
    const response = await apiClient.post<AuthTokenResponse>('/auth/login', data);
    return response.data;
  },

  register: async (data: RegisterRequest): Promise<AuthTokenResponse> => {
    const response = await apiClient.post<AuthTokenResponse>('/auth/register', data);
    return response.data;
  },

  getMe: async (): Promise<User> => {
    const response = await apiClient.get<User>('/auth/me');
    return response.data;
  },

  refreshToken: async (): Promise<AuthTokenResponse> => {
    const response = await apiClient.post<AuthTokenResponse>('/auth/refresh');
    return response.data;
  },
};

// --- Entities API ------------------------------------------------------------

// Backend uses `parent_entity_id` (int); frontend Entity uses `parent_id` (string).
// Translate at the API boundary so the rest of the UI stays clean.
function normalizeEntity(raw: Record<string, any>): Entity {
  return {
    ...raw,
    id: String(raw.id),
    parent_id: raw.parent_entity_id != null ? String(raw.parent_entity_id) : null,
    ownership_pct: Number(raw.ownership_pct),
  } as Entity;
}

function toEntityPayload(data: Partial<Entity>): Record<string, any> {
  const { parent_id, ...rest } = data as any;
  return {
    ...rest,
    parent_entity_id: parent_id ? Number(parent_id) : null,
  };
}

export const entitiesApi = {
  list: async (params?: { page?: number; page_size?: number; search?: string; exclude_icp?: boolean; only_icp?: boolean; is_active?: boolean | null; hierarchy_type?: string }): Promise<PaginatedResponse<Entity>> => {
    const finalParams: Record<string, unknown> = { is_active: true, exclude_icp: true, ...params };
    for (const k of Object.keys(finalParams)) {
      if (finalParams[k] === undefined || finalParams[k] === null) {
        delete finalParams[k];
      }
    }
    const response = await apiClient.get<PaginatedResponse<any>>('/entities', { params: finalParams });
    const raw: any[] = response.data.items;
    const idToName: Record<number, string> = {};
    raw.forEach((e) => { idToName[e.id] = e.name; });
    return {
      ...response.data,
      items: raw.map((e) => ({
        ...normalizeEntity(e),
        parent_name: e.parent_entity_id != null ? idToName[e.parent_entity_id] : undefined,
      })),
    };
  },

  /**
   * Lightweight member list for form-builder runtime pools — hits `/entities/form-members`
   * (~7 fields, ~10x smaller payload) instead of the full `/entities`. Same params/filters
   * and same `normalizeEntity` shape, so callers that only read id/parent_id/code/name/
   * is_active/allow_upper_level_entity_input are drop-in. Do NOT use where full FCCS
   * metadata is needed (Dimension Manager) — use `list` for that.
   */
  listFormMembers: async (params?: { page?: number; page_size?: number; exclude_icp?: boolean; only_icp?: boolean; is_active?: boolean | null; hierarchy_type?: string }): Promise<PaginatedResponse<Entity>> => {
    const finalParams: Record<string, unknown> = { is_active: true, exclude_icp: true, ...params };
    for (const k of Object.keys(finalParams)) {
      if (finalParams[k] === undefined || finalParams[k] === null) {
        delete finalParams[k];
      }
    }
    const response = await apiClient.get<PaginatedResponse<any>>('/entities/form-members', { params: finalParams });
    const raw: any[] = response.data.items;
    return { ...response.data, items: raw.map((e) => normalizeEntity(e)) };
  },

  create: async (data: Partial<Entity>): Promise<Entity> => {
    const response = await apiClient.post<any>('/entities', toEntityPayload(data));
    return normalizeEntity(response.data);
  },

  get: async (id: string): Promise<Entity> => {
    const response = await apiClient.get<any>(`/entities/${id}`);
    return normalizeEntity(response.data);
  },

  update: async (id: string, data: Partial<Entity>): Promise<Entity> => {
    const response = await apiClient.put<any>(`/entities/${id}`, toEntityPayload(data));
    return normalizeEntity(response.data);
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/entities/${id}`);
  },

  hierarchy: async (): Promise<Entity[]> => {
    const response = await apiClient.get<any[]>('/entities/hierarchy');
    return response.data.map(normalizeEntity);
  },

  bulkEnableIntercompany: async (): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post<{ success: boolean; message: string }>(
      '/entities/bulk-enable-intercompany'
    );
    return response.data;
  },

  seed: async (): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post<{ success: boolean; message: string }>('/entities/seed');
    return response.data;
  },

  share: async (entityId: string | number, targetParentId: number | null, targetHierarchyType: string): Promise<Entity> => {
    const response = await apiClient.post<any>(
      `/entities/${entityId}/share`,
      null,
      { params: { target_parent_id: targetParentId, target_hierarchy_type: targetHierarchyType } },
    );
    return normalizeEntity(response.data);
  },
};

// --- Ownership API (FCCS Manage Ownership) -----------------------------------

export const ownershipApi = {
  tree: async (params: { scenario: string; year: number; period: string; hierarchy_type?: string }): Promise<any[]> => {
    const response = await apiClient.get<any[]>('/ownership/tree', { params });
    return response.data;
  },

  list: async (params: Record<string, any>): Promise<any[]> => {
    const response = await apiClient.get<any[]>('/ownership', { params });
    return response.data;
  },

  bulkSave: async (data: { scenario: string; year: number; period: string; rows: any[] }): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.put<{ success: boolean; message: string }>('/ownership/bulk-save', data);
    return response.data;
  },

  lock: async (id: number, reason?: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post<{ success: boolean; message: string }>(`/ownership/${id}/lock`, reason ? { reason } : undefined);
    return response.data;
  },

  unlock: async (id: number, reason?: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post<{ success: boolean; message: string }>(`/ownership/${id}/unlock`, reason ? { reason } : undefined);
    return response.data;
  },

  clearOverride: async (id: number): Promise<any> => {
    const response = await apiClient.post<any>(`/ownership/clear-override/${id}`);
    return response.data;
  },

  recompute: async (data: { scenario: string; year: number; period: string; include_structure?: boolean; hierarchy_id?: number | null }): Promise<{ success: boolean; message: string; job_id?: number; already_running?: boolean }> => {
    const response = await apiClient.post<{ success: boolean; message: string; job_id?: number; already_running?: boolean }>('/ownership/recompute', data);
    return response.data;
  },

  copyToNextYear: async (data: { scenario: string; source_year: number; source_period: string; target_year: number; target_period: string }): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post<{ success: boolean; message: string }>('/ownership/copy-to-next-year', data);
    return response.data;
  },

  smartCopyToNextYear: async (params: { scenario: string; source_year: number; target_year?: number }): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post<{ success: boolean; message: string }>('/ownership/smart-copy-to-next-year', null, { params });
    return response.data;
  },

  priorYearAvailable: async (params: { scenario: string; year: number; period: string }): Promise<{ available: boolean; current_count: number; prior_count: number; source_year: number; source_period: string }> => {
    const response = await apiClient.get('/ownership/prior-year-available', { params });
    return response.data;
  },

  exportCsv: async (params: { scenario: string; year: number; period: string; hierarchy_type?: string }): Promise<Blob> => {
    const response = await apiClient.get('/ownership/export/sync', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  exportJob: async (params: { scenario: string; year: number; period: string; hierarchy_type?: string }): Promise<{ success: boolean; message: string; job_id: number; task_id: string }> => {
    const response = await apiClient.get<{ success: boolean; message: string; job_id: number; task_id: string }>('/ownership/export', { params });
    return response.data;
  },

  importData: async (data: { scenario: string; year: number; period: string; rows: any[]; hierarchy_type?: string }): Promise<OwnershipImportResponse> => {
    const { hierarchy_type, ...body } = data;
    const response = await apiClient.post<OwnershipImportResponse>('/ownership/import', body, {
      params: hierarchy_type ? { hierarchy_type } : undefined,
    });
    return response.data;
  },

  // Feature 1: Recompute status. Pass a POV (scenario/year/period) to also get
  // prior-period staleness (prior_period_changed) — true when the prior period
  // was modified after this POV was last recomputed, so the UI can prompt the
  // user to recompute ("changes in the previous period").
  recomputeStatus: async (params?: { scenario?: string; year?: number; period?: string }): Promise<{ recompute_needed: boolean; impacted_count: number; message: string; prior_period_changed?: boolean; prior_period_label?: string | null; changed_row_count?: number }> => {
    const response = await apiClient.get('/ownership/recompute-status', { params });
    return response.data;
  },

  // Feature 3: Lock All / Unlock All
  lockAll: async (data: { scenario: string; year: number; period: string; reason?: string }): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/ownership/lock-all', data);
    return response.data;
  },
  unlockAll: async (data: { scenario: string; year: number; period: string; reason?: string }): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/ownership/unlock-all', data);
    return response.data;
  },
  syncLockStatus: async (params: { scenario: string; year: number; period: string }): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/ownership/sync-lock-status', null, { params });
    return response.data;
  },

  // Feature 4: Scope summary & path
  scopeSummary: async (params: { scenario: string; year: number; period: string }): Promise<{ full: number; proportional: number; equity: number; not_consolidated: number; inactive: number; discontinued: number; total: number }> => {
    const response = await apiClient.get('/ownership/scope-summary', { params });
    return response.data;
  },
  ownershipPath: async (entityId: number, params: { scenario: string; year: number; period: string }): Promise<any> => {
    const response = await apiClient.get(`/ownership/path/${entityId}`, { params });
    return response.data;
  },

  // Feature 5: Observations
  observations: async (params: { scenario: string; year: number; period: string }): Promise<{ observations: any[]; count: number }> => {
    const response = await apiClient.get('/ownership/observations', { params });
    return response.data;
  },

  // Feature 7: Period lock cascade
  cascadePeriodLock: async (params: { scenario: string; year: number; period: string; lock: boolean }): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/ownership/cascade-period-lock', null, { params });
    return response.data;
  },

  // Feature 8: Scope lock
  scopeLockStatus: async (params: { scenario: string; year: number; period: string }): Promise<{ is_locked: boolean; locked_by?: number; locked_by_name?: string; locked_at?: string; reason?: string }> => {
    const response = await apiClient.get('/ownership/scope-lock-status', { params });
    return response.data;
  },
  scopeLock: async (data: { scenario: string; year: number; period: string; locked: boolean; reason?: string }): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/ownership/scope-lock', data);
    return response.data;
  },

  // Feature 10: Future year init status
  initStatus: async (params: { scenario: string; year: number; period: string }): Promise<{ initialized: boolean; ownership_not_initialized: boolean; message: string; source_year?: number; source_period?: string; can_copy: boolean }> => {
    const response = await apiClient.get('/ownership/init-status', { params });
    return response.data;
  },
  copyToNextYearV2: async (data: { scenario: string; source_year: number }): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/ownership/copy-to-next-year', data);
    return response.data;
  },
};

// --- Entity Ownership Structure API (BRD §5.4.3) ------------------------------

export interface OwnershipPathDetail { root: number; product: number; path: string }

/** A single per-line validation error from POST /ownership/import. */
export interface OwnershipImportError {
  line: number;
  code: string;
  message: string;
}

/** Response shape of POST /ownership/import (sync per-line validation).
 *  When `valid` is false nothing is imported; when true the async upsert job
 *  is dispatched. `job_id` is always present (a Job record is recorded either
 *  way). Legacy `success`/`task_id` fields remain optional for compatibility. */
export interface OwnershipImportResponse {
  valid: boolean;
  ok_count: number;
  error_count: number;
  skipped_count: number;
  errors: OwnershipImportError[];
  log_text: string;
  job_id: number;
  message: string;
  success?: boolean;
  task_id?: string;
}

export interface OwnershipStructureNode {
  entity_id: number;
  entity_code: string;
  entity_name: string;
  entity_type: string;
  ownership_id: number | null;
  direct_ownership_pct: number;
  effective_ownership_pct: number | null;
  control: boolean;
  consolidation_method: string;
  consolidation_pct: number;
  minority_interest_pct: number;
  scope_bucket: 'full' | 'proportional' | 'equity' | 'excluded';
  is_cross_held: boolean;
  is_locked: boolean;
  locked_by: number | null;
  locked_at: string | null;
  lock_reason?: string | null;
  control_override?: boolean;
  method_override?: boolean;
  /** Per-field previous-value snapshot for the inline-edit hover history.
   *  Present only for fields the user has actually changed. */
  change_meta?: Record<string, { prev: number | string | boolean; at: string; by_name: string }> | null;
  // --- US-OW-001/001b: system-calculated prior-period + change-tracking.
  //     Populated by the ownership recompute task; null until recomputed. ---
  ownership_pct_prior?: number | null;
  ownership_pct_change?: number | null;
  consolidation_pct_prior?: number | null;
  consolidation_pct_change?: number | null;
  minority_interest_pct_prior?: number | null;
  minority_interest_pct_change?: number | null;
  consolidation_method_prior?: string | null;
  method_change?: boolean | null;
  control_prior?: boolean | null;
  control_change?: boolean | null;
  elimination_pct?: number | null;
  children: OwnershipStructureNode[];
}

export interface SharedCapMember {
  entity_id: number;
  entity_code: string;
  is_shared_instance: boolean;
  path: number[];
  path_codes: string[];
  contribution_pct: number;
}

/** US-OWN-CAP: an entity + its shared instances consolidate >100% (or 100% twice)
 *  into one common ancestor. Non-blocking warning from GET /structure. */
export interface SharedConsolidationCapWarning {
  base_entity_id: number;
  base_entity_code: string;
  ancestor_entity_id: number;
  ancestor_entity_code: string;
  total_pct: number;
  kind: 'over_100' | 'double_100';
  message: string;
  members: SharedCapMember[];
}

export interface StructureTreeResponse {
  scenario: string;
  year: number;
  period: string;
  hierarchy_id: number | null;
  scope_locked: boolean;
  roots: OwnershipStructureNode[];
  auto_seeded: boolean;
  seed_source: string | null;
  shared_consolidation_warnings?: SharedConsolidationCapWarning[];
}

export interface ScopeEntity {
  ownership_id: number;
  entity_id: number;
  entity_code: string | null;
  entity_name: string | null;
  parent_entity_id: number;
  parent_entity_code: string | null;
  ownership_pct: number;
  effective_ownership_pct: number | null;
  control: boolean;
  consolidation_method: string;
  consolidation_pct: number;
  minority_interest_pct: number;
  is_locked: boolean;
}

export interface ScopeResponse {
  scenario: string;
  year: number;
  period: string;
  total_entities: number;
  counts: { full: number; proportional: number; equity: number; excluded: number };
  full: ScopeEntity[];
  proportional: ScopeEntity[];
  equity: ScopeEntity[];
  excluded: ScopeEntity[];
}

export interface ScopeLockResponse {
  scenario: string;
  year: number;
  period: string;
  hierarchy_id: number | null;
  is_locked: boolean;
  locked_by: number | null;
  locked_by_name: string | null;
  locked_at: string | null;
  reason: string | null;
}

/** Result of ensuring the CICTA option accounts (FCCS_CICTA, Total OCI, FCCS_OR OBFXCICTA, …). */
export interface CictaAccountsReportDTO {
  created: string[];
  existing: string[];
  warnings: string[];
}

export interface PathExplanationResponse {
  entity_id: number;
  entity_code: string | null;
  entity_name: string | null;
  scenario: string;
  year: number;
  period: string;
  effective_ownership_pct: number;
  paths: OwnershipPathDetail[];
  formula: string;
}

export interface OwnershipMeasure {
  key: string;
  label: string;
  description: string | null;
  /** False when the consolidation-rule interpreter cannot resolve this basis yet. */
  engine_supported: boolean;
}

export const ownershipStructureApi = {
  /** The ownership percentage columns, used to drive factor-basis dropdowns. */
  measures: async (): Promise<OwnershipMeasure[]> =>
    (await apiClient.get<OwnershipMeasure[]>('/entity-ownership-structure/measures')).data,

  structure: async (params: { scenario: string; year: number; period: string; hierarchy_id?: number | null; hierarchy_type?: string }): Promise<StructureTreeResponse> => {
    const response = await apiClient.get<StructureTreeResponse>('/entity-ownership-structure/structure', { params });
    return response.data;
  },

  scope: async (params: { scenario: string; year: number; period: string; hierarchy_type?: string }): Promise<ScopeResponse> => {
    const response = await apiClient.get<ScopeResponse>('/entity-ownership-structure/scope', { params });
    return response.data;
  },

  compute: async (data: { scenario: string; year: number; period: string; hierarchy_id?: number | null }): Promise<any> => {
    const response = await apiClient.post<any>('/entity-ownership-structure/compute', data);
    return response.data;
  },

  lockStatus: async (params: { scenario: string; year: number; period: string; hierarchy_id?: number | null }): Promise<ScopeLockResponse> => {
    const response = await apiClient.get<ScopeLockResponse>('/entity-ownership-structure/scope/lock', { params });
    return response.data;
  },

  lockScope: async (data: { scenario: string; year: number; period: string; hierarchy_id?: number | null; reason?: string }): Promise<ScopeLockResponse> => {
    const response = await apiClient.post<ScopeLockResponse>('/entity-ownership-structure/scope/lock', data);
    return response.data;
  },

  unlockScope: async (data: { scenario: string; year: number; period: string; hierarchy_id?: number | null; reason?: string }): Promise<ScopeLockResponse> => {
    const response = await apiClient.post<ScopeLockResponse>('/entity-ownership-structure/scope/unlock', data);
    return response.data;
  },

  paths: async (entity_id: number, params: { scenario: string; year: number; period: string; hierarchy_id?: number | null }): Promise<PathExplanationResponse> => {
    const response = await apiClient.get<PathExplanationResponse>(`/entity-ownership-structure/paths/${entity_id}`, { params });
    return response.data;
  },
};

// --- Accounts API ------------------------------------------------------------

export const accountsApi = {
  listGroup: async (params?: { account_type?: string; search?: string; is_active?: boolean }): Promise<GroupAccount[]> => {
    const response = await apiClient.get<any>('/accounts/group', { params: { page_size: 5000, ...params } });
    return response.data?.items ?? response.data;
  },

  /**
   * Lightweight group-account list for form-builder runtime pools — hits
   * `/accounts/group/form-members` (~21 fields incl. rollup/formula/operator props,
   * far smaller than the full ~73-field `/accounts/group`). Drop-in for callers that
   * map the pool fields in `fetchAccountRows`. Use `listGroup` where full metadata is
   * needed (Chart-of-Accounts / Dimension Manager).
   */
  listGroupFormMembers: async (params?: { account_type?: string; is_active?: boolean; codes?: string; fx_mode?: string; anchor?: string }): Promise<GroupAccount[]> => {
    const clean: Record<string, unknown> = { page_size: 5000, ...params };
    for (const k of Object.keys(clean)) {
      if (clean[k] === undefined || clean[k] === null || clean[k] === '') delete clean[k];
    }
    const response = await apiClient.get<any>('/accounts/group/form-members', { params: clean });
    return response.data?.items ?? response.data;
  },

  createGroup: async (data: Partial<GroupAccount>): Promise<GroupAccount> => {
    const response = await apiClient.post<GroupAccount>('/accounts/group', data);
    return response.data;
  },

  updateGroup: async (id: string, data: Partial<GroupAccount>): Promise<GroupAccount> => {
    const response = await apiClient.put<GroupAccount>(`/accounts/group/${id}`, data);
    return response.data;
  },

  deleteGroup: async (id: string): Promise<void> => {
    await apiClient.delete(`/accounts/group/${id}`);
  },

  syncIcpFlags: async (): Promise<{ updated: number; icp_count: number; source: string; message: string }> => {
    const response = await apiClient.post<{ updated: number; icp_count: number; source: string; message: string }>('/accounts/sync-icp-flags');
    return response.data;
  },

  listEntity: async (entityId: string, params?: { mapped?: boolean; search?: string }): Promise<EntityAccount[]> => {
    const response = await apiClient.get<any>('/accounts/entity', { params: { entity_id: entityId, page_size: 500, ...params } });
    return response.data?.items ?? response.data;
  },

  createEntity: async (entityId: string, data: Partial<EntityAccount>): Promise<EntityAccount> => {
    const response = await apiClient.post<EntityAccount>(`/accounts/entity/${entityId}`, data);
    return response.data;
  },

  mapAccount: async (entityAccountId: string, groupAccountId: string): Promise<EntityAccount> => {
    const response = await apiClient.post<EntityAccount>('/accounts/map', {
      entity_account_id: Number(entityAccountId),
      group_account_id: Number(groupAccountId),
    });
    return response.data;
  },

  unmapAccount: async (entityAccountId: string): Promise<EntityAccount> => {
    const response = await apiClient.put<EntityAccount>(`/accounts/entity/${entityAccountId}/unmap`);
    return response.data;
  },

  listUnmapped: async (entityId: string): Promise<EntityAccount[]> => {
    const response = await apiClient.get<any>('/accounts/entity', { params: { entity_id: entityId, mapping_status: 'unmapped', page_size: 500 } });
    return response.data?.items ?? response.data;
  },

  uploadMapping: async (entityId: string, file: File): Promise<{ rows_processed: number; mapped: number; skipped: number; warnings: string[]; errors: string[] }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/accounts/entity/upload-mapping?entity_id=${entityId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  uploadCOA: async (entityId: string, file: File): Promise<{
    rows_processed: number; created: number; updated: number; skipped: number;
    warnings: string[]; errors: string[];
    ai_column_detection?: boolean; detected_code_column?: string; detected_name_column?: string;
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/accounts/entity/upload-coa?entity_id=${entityId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  coaSummary: async (): Promise<{
    entity_id: number; entity_code: string; entity_name: string; functional_currency: string;
    total_accounts: number; mapped_accounts: number; unmapped_accounts: number; mapping_pct: number;
  }[]> => {
    const response = await apiClient.get('/accounts/entity/coa-summary');
    return response.data;
  },

  aiMap: async (entityId: string): Promise<{
    total_processed: number; auto_mapped: number; skipped: number; errors: string[];
  }> => {
    const response = await apiClient.post(`/accounts/entity/ai-map?entity_id=${entityId}`);
    return response.data;
  },

  downloadCoaTemplate: async (): Promise<void> => {
    const response = await apiClient.get('/accounts/entity/coa-template', { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'entity_coa_template.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  uploadBulkMapping: async (file: File): Promise<{
    total_mapped: number; total_skipped: number; entities_processed: number;
    entity_results: { entity_code: string; entity_name: string; sheet: string | null; explicit_mapped: number; wildcard_mapped: number; skipped: number; status: string }[];
    warnings: string[];
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/accounts/entity/upload-bulk-mapping', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  uploadGroupCoa: async (file: File): Promise<{
    rows_processed: number; created: number; updated: number; skipped: number;
    warnings: string[]; errors: string[];
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/accounts/group/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  uploadSiriusFccsCoa: async (file: File): Promise<{
    success: boolean; message: string; created: number; updated: number;
    skipped: number; parent_linked: number; total_rows: number;
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(
      '/accounts/group/upload-sirius',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 },
    );
    return response.data;
  },

  downloadGroupCoaTemplate: async (): Promise<void> => {
    const response = await apiClient.get('/accounts/group/coa-template', { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'group_coa_template.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  copyFromGroup: async (entityId: string): Promise<{
    created: number; skipped: number; total_group_accounts: number;
  }> => {
    const response = await apiClient.post(`/accounts/entity/${entityId}/copy-from-group`);
    return response.data;
  },
};

// --- Trial Balances API ------------------------------------------------------

export const trialBalancesApi = {
  upload: async (
    entityId: string,
    periodId: string,
    file: File,
    scenario: string = 'Actual',
    balanceType: string = 'ytd',
  ): Promise<{ rows_imported: number; errors: string[]; warnings: string[] }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<{ rows_imported: number; errors: string[]; warnings: string[] }>(
      `/trial-balances/upload?entity_id=${entityId}&period_id=${periodId}&scenario=${encodeURIComponent(scenario)}&balance_type=${encodeURIComponent(balanceType)}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  uploadBulk: async (
    entityId: string,
    year: number,
    files: File[],
    scenario: string = 'Actual',
    balanceType: string = 'monthly',
  ): Promise<{
    total_files: number;
    files_processed: number;
    files_skipped: number;
    files_errored: number;
    total_rows_imported: number;
    file_results: Array<{ filename: string; status: string; period?: string; rows_imported: number; reason?: string; warnings?: string[]; errors?: string[] }>;
    errors: string[];
    warnings: string[];
  }> => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    const response = await apiClient.post(
      `/trial-balances/upload-bulk?entity_id=${entityId}&year=${year}&scenario=${encodeURIComponent(scenario)}&balance_type=${encodeURIComponent(balanceType)}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  uploadOpeningBalances: async (
    entityId: string,
    periodId: string,
    file: File,
  ): Promise<{ rows_imported: number; errors: string[]; warnings: string[] }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<{ rows_imported: number; errors: string[]; warnings: string[] }>(
      `/trial-balances/upload-opening-balances?entity_id=${entityId}&period_id=${periodId}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  listUploads: async (params?: {
    entity_id?: string;
    period_id?: string;
    scenario?: string;
    balance_type?: string;
    status?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<TrialBalanceUpload>> => {
    const response = await apiClient.get<PaginatedResponse<TrialBalanceUpload>>('/trial-balances/uploads', { params });
    return response.data;
  },

  updateBalanceType: async (params: {
    entity_id: number;
    period_id: number;
    balance_type: 'ytd' | 'monthly';
    scenario?: string;
    filename?: string | null;
  }): Promise<{ status: string; message: string }> => {
    const response = await apiClient.patch('/trial-balances/uploads/balance-type', null, { params });
    return response.data;
  },

  list: async (params?: {
    entity_id?: string;
    period_id?: string;
    member?: string;
    dimensions?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<TrialBalance>> => {
    const response = await apiClient.get<PaginatedResponse<TrialBalance>>('/trial-balances', { params });
    return response.data;
  },

  adjusted: async (params: {
    entity_id: string;
    period_id: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<TrialBalance>> => {
    const response = await apiClient.get<PaginatedResponse<TrialBalance>>('/trial-balances/adjusted', { params });
    return response.data;
  },

  summary: async (entityId: string, periodId: string): Promise<TrialBalanceSummary> => {
    const response = await apiClient.get<TrialBalanceSummary>('/trial-balances/summary', {
      params: { entity_id: entityId, period_id: periodId },
    });
    return response.data;
  },

  validate: async (entityId: string, periodId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post<{ success: boolean; message: string }>(
      `/trial-balances/validate?entity_id=${entityId}&period_id=${periodId}`,
    );
    return response.data;
  },

  downloadTemplate: async (entityId: string): Promise<Blob> => {
    const response = await apiClient.get('/trial-balances/sample-template', {
      params: { entity_id: entityId },
      responseType: 'blob',
    });
    return response.data;
  },

  getStatement: async (
    entityId: string,
    periodId: string,
    statementType: 'balance_sheet' | 'income_statement',
    toCurrency?: string,
  ) => {
    const response = await apiClient.get('/trial-balances/entity-statement', {
      params: {
        entity_id: entityId,
        period_id: periodId,
        statement_type: statementType,
        ...(toCurrency ? { to_currency: toCurrency } : {}),
      },
    });
    return response.data;
  },

  exportStatement: async (
    entityId: string,
    periodId: string,
    statementType: 'balance_sheet' | 'income_statement',
    opts?: {
      level?: number;
      to_currency?: string;
      export_original?: boolean;
      export_translated?: boolean;
    },
  ): Promise<Blob> => {
    const params: Record<string, string | number | boolean> = {
      entity_id: entityId,
      period_id: periodId,
      statement_type: statementType,
    };
    if (opts?.level !== undefined) params.level = opts.level;
    if (opts?.to_currency) params.to_currency = opts.to_currency;
    if (opts?.export_original !== undefined) params.export_original = opts.export_original;
    if (opts?.export_translated !== undefined) params.export_translated = opts.export_translated;
    const response = await apiClient.get('/trial-balances/entity-statement/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  deleteUpload: async (entityId: number, periodId: number, scenario: string = 'Actual'): Promise<{ status: string; message: string }> => {
    const response = await apiClient.delete<{ status: string; message: string }>('/trial-balances/upload', {
      params: { entity_id: entityId, period_id: periodId, scenario },
    });
    return response.data;
  },
  translate: async (params: {
    entity_id: string;
    period_id: string;
    to_currency: string;
  }): Promise<TrialBalanceTranslationResponse> => {
    const response = await apiClient.post<TrialBalanceTranslationResponse>(
      `/trial-balances/translate`,
      null,
      { params },
    );
    return response.data;
  },

  calculateNci: async (entityId: string, periodId: string): Promise<any> => {
    const response = await apiClient.post<any>(
      `/trial-balances/calculate-nci`,
      null,
      { params: { entity_id: entityId, period_id: periodId } }
    );
    return response.data;
  },

  /**
   * Run the full automated NCI pipeline (translate if needed → CTA → NCI).
   * entity_id and period_id are required. reporting_currency defaults to AED.
   */
  nciPipeline: async (params: {
    entity_id: string;
    period_id: string;
    reporting_currency?: string;
  }): Promise<NciPipelineResult> => {
    const response = await apiClient.post<NciPipelineResult>(
      `/trial-balances/nci-pipeline`,
      null,
      { params: { ...params, reporting_currency: params.reporting_currency ?? 'AED' } },
    );
    return response.data;
  },
};


// --- Consolidation API -------------------------------------------------------

const CONSOLIDATION_PIPELINE: readonly string[] = [
  'SKILL-001',
  'SKILL-002',
  'SKILL-003',
  'SKILL-004',
  'SKILL-008',
];

const CONSOLIDATION_SKILL_MAP: Record<string, string> = {
  'SKILL-001': 'data_validation',
  'SKILL-002': 'ic_elimination',
  'SKILL-003': 'fx_translation',
  'SKILL-004': 'equity_consolidation',
  'SKILL-008': 'generate_statements',
};

const CONSOLIDATION_SKILL_LABELS: Record<string, string> = {
  'SKILL-001': 'Data Validation',
  'SKILL-002': 'IC Elimination',
  'SKILL-003': 'FX Translation',
  'SKILL-004': 'Equity Consolidation',
  'SKILL-008': 'Generate Statements',
};

const CONSOLIDATION_SKILL_DESC: Record<string, string> = {
  'SKILL-001': 'Validate trial balances, mappings, and FX rates',
  'SKILL-002': 'Eliminate intercompany transactions and balances',
  'SKILL-003': 'Translate foreign currency balances to reporting currency',
  'SKILL-004': 'Consolidate equity and calculate minority interest',
  'SKILL-008': 'Generate consolidated financial statements',
};

function buildConsolidationSkillsFromSummary(data: {
  status?: string;
  error_message?: string | null;
  results_summary?: Record<string, unknown> | null;
}, journalCountsBySkill: Record<string, number> = {}): SkillExecution[] {
  const rs = (data.results_summary || {}) as {
    skills_executed?: Array<{
      skill_id: string;
      skill_name?: string;
      status?: string;
      records_processed?: number;
      journal_entries_created?: number;
      summary?: { elimination_jes_created?: number };
    }>;
    skills_failed?: Array<{ skill_id: string; skill_name?: string; errors?: string[] }>;
  };
  const executed = rs.skills_executed || [];
  const failed = rs.skills_failed || [];
  const execById = Object.fromEntries(executed.map((s) => [s.skill_id, s]));
  const failById = Object.fromEntries(failed.map((s) => [s.skill_id, s]));
  const firstFailIdx = CONSOLIDATION_PIPELINE.findIndex((id) => failById[id]);

  return CONSOLIDATION_PIPELINE.map((skillId, i) => {
    const ex = execById[skillId];
    const fl = failById[skillId];
    const base = {
      skill_name: CONSOLIDATION_SKILL_MAP[skillId] || skillId,
      display_name: CONSOLIDATION_SKILL_LABELS[skillId] || ex?.skill_name || fl?.skill_name || skillId,
      description: CONSOLIDATION_SKILL_DESC[skillId] || '',
      step_order: i + 1,
      progress_pct: 0,
      message: 'Waiting to start',
      status: SkillStatus.PENDING,
    };

    if (ex) {
      const journalCount =
        (skillId === 'SKILL-002'
          ? (Number(ex.journal_entries_created) || Number(journalCountsBySkill[skillId]) || 0)
          : Number(ex.journal_entries_created) || 0);
      return {
        ...base,
        status: SkillStatus.COMPLETED,
        progress_pct: 100,
        message: `${ex.records_processed ?? 0} records, ${journalCount} JEs`,
      };
    }
    if (fl) {
      const err =
        Array.isArray(fl.errors) && fl.errors.length
          ? fl.errors.join('; ')
          : data.error_message || 'Failed';
      return {
        ...base,
        status: SkillStatus.FAILED,
        message: err.length > 240 ? `${err.slice(0, 240)}…` : err,
      };
    }
    if (firstFailIdx >= 0 && i > firstFailIdx) {
      return {
        ...base,
        status: SkillStatus.SKIPPED,
        message: 'Not run (pipeline stopped)',
      };
    }
    if (data.status === 'failed' && executed.length === 0 && failed.length === 0) {
      return {
        ...base,
        status: i === 0 ? SkillStatus.FAILED : SkillStatus.SKIPPED,
        message:
          i === 0
            ? data.error_message || 'Consolidation failed before skill results were recorded'
            : 'Not run',
      };
    }
    return base;
  });
}

export const consolidationApi = {
  /** Start a consolidation run. Pass ``parameters.entity_ids`` to scope to one or more entities (omit for full group). */
  run: async (
    periodId: string,
    options?: { parameters?: Record<string, unknown> }
  ): Promise<ConsolidationRun> => {
    const body: Record<string, unknown> = { period_id: Number(periodId) };
    if (options?.parameters && Object.keys(options.parameters).length > 0) {
      body.parameters = options.parameters;
    }
    // Queued (HTTP 202): the run row comes back as `running` immediately and the
    // pipeline executes on the Celery worker; the page already polls
    // `GET /consolidations/{id}` for status. The synchronous `/run` blocked for
    // the whole pipeline and timed out (30 s axios / ~100 s Cloudflare edge) on
    // any real group consolidation.
    const response = await apiClient.post<ConsolidationRun>('/consolidations/run-async', body);
    return response.data;
  },

  list: async (params?: { period_id?: string; status?: string }): Promise<ConsolidationRun[]> => {
    const response = await apiClient.get<any>('/consolidations', { params });
    return response.data?.items ?? response.data;
  },

  get: async (runId: string): Promise<ConsolidationResult> => {
    const [response, journalEntriesResponse] = await Promise.all([
      apiClient.get<any>(`/consolidations/${runId}`),
      apiClient.get<JournalEntry[]>(`/consolidations/${runId}/journal-entries`),
    ]);
    const data = response.data;
    const journalEntries = journalEntriesResponse.data || [];
    const runReference = `consolidation_run:${runId}`;
    const runScopedJournalEntries = journalEntries.filter((je) => je.reference === runReference);
    const rs = (data.results_summary || {}) as Record<string, unknown>;
    const skillsExecuted = (rs.skills_executed || []) as Array<{
      skill_id: string;
      journal_entries_created?: number;
      summary?: { elimination_jes_created?: number; total_eliminated_amount?: string | number };
    }>;
    const journalCountsBySkill = runScopedJournalEntries.reduce<Record<string, number>>((acc, je) => {
      const skillId = je.skill_name || '';
      if (!skillId) return acc;
      acc[skillId] = (acc[skillId] || 0) + 1;
      return acc;
    }, {});
    if (!journalCountsBySkill['SKILL-002']) {
      journalCountsBySkill['SKILL-002'] = journalEntries.filter((je) => je.skill_name === 'SKILL-002').length;
    }

    const skills = buildConsolidationSkillsFromSummary(data, journalCountsBySkill);

    const skill002 = skillsExecuted.find((s) => s.skill_id === 'SKILL-002');
    const skill003 = skillsExecuted.find((s) => s.skill_id === 'SKILL-003');
    const skill004 = skillsExecuted.find((s) => s.skill_id === 'SKILL-004');

    const actualElimJes = (
      runScopedJournalEntries.length > 0
        ? runScopedJournalEntries.filter((je) => je.skill_name === 'SKILL-002')
        : journalEntries.filter((je) => je.skill_name === 'SKILL-002')
    );

    const eliminationAmountFromJournals = actualElimJes.reduce((sum, je) => (
      sum + je.lines.reduce((lineSum, line) => lineSum + (Number(line.debit_amount) || 0), 0)
    ), 0);

    const elimJes =
      actualElimJes.length ||
      Number(skill002?.journal_entries_created) ||
      Number(skill002?.summary?.elimination_jes_created) ||
      0;
    const eliminationAmount =
      eliminationAmountFromJournals ||
      Number(skill002?.summary?.total_eliminated_amount) ||
      0;
    const fxJes = Number(skill003?.journal_entries_created) || 0;
    const equityJes = Number(skill004?.journal_entries_created) || 0;

    return {
      run_id: String(data.id),
      status: data.status,
      skills,
      journal_entries: journalEntries,
      summary: {
        total_eliminations: elimJes,
        elimination_amount: eliminationAmount,
        fx_adjustments: fxJes,
        equity_adjustments: equityJes,
        total_journal_entries: Number(rs.total_journal_entries_created) || 0,
      },
    };
  },

  results: async (runId: string): Promise<ConsolidationResult> => {
    const response = await apiClient.get<ConsolidationResult>(`/consolidations/${runId}/results`);
    return response.data;
  },

  journalEntries: async (runId: string): Promise<JournalEntry[]> => {
    const response = await apiClient.get<JournalEntry[]>(`/consolidations/${runId}/journal-entries`);
    return response.data;
  },

  approve: async (runId: string): Promise<ConsolidationRun> => {
    const response = await apiClient.post<ConsolidationRun>(`/consolidations/${runId}/approve`);
    return response.data;
  },

  rollback: async (runId: string): Promise<ConsolidationRun> => {
    const response = await apiClient.post<ConsolidationRun>(`/consolidations/${runId}/rollback`);
    return response.data;
  },

  executeSkill: async (runId: string, skillName: string, params?: Record<string, unknown>): Promise<SkillExecution> => {
    const response = await apiClient.post<SkillExecution>(`/consolidations/${runId}/skills/${skillName}`, params);
    return response.data;
  },

  // --- US-CM-8: Consolidation Waterfall (FCCS 10-member) -------------------

  /** Waterfall column totals (10 canonical members). */
  getWaterfall: async (params: {
    scenario_id: number;
    period_id: number;
    entity_ids: string;
    parent_entity_id?: number;
    /** PC = parent/reporting currency (default), EC = entity currency. */
    currency?: 'PC' | 'EC';
  }): Promise<WaterfallResponse> => {
    const response = await apiClient.get<WaterfallResponse>('/consolidation/waterfall', { params });
    return response.data;
  },

  /** Step-12 waterfall: accounts as rows, 10 members as columns. */
  getWaterfallByAccount: async (params: {
    scenario_id: number;
    period_id: number;
    entity_ids: string;
    parent_entity_id?: number;
    /** PC = parent/reporting currency (default), EC = entity currency. */
    currency?: 'PC' | 'EC';
  }): Promise<WaterfallByAccountResponse> => {
    const response = await apiClient.get<WaterfallByAccountResponse>('/consolidation/waterfall/by-account', { params });
    return response.data;
  },
};

// --- US-CM-8: Waterfall types ------------------------------------------------

/** One column in the 10-member waterfall. */
export interface WaterfallColumn {
  member: string;
  index: number;
  consolidation_codes: string[];
  total: string;
}

/** Response from GET /consolidation/waterfall. */
export interface WaterfallResponse {
  scenario_id: number;
  period_id: number;
  entity_ids: number[];
  parent_entity_id: number | null;
  columns: WaterfallColumn[];
  final_amount: string;
}

/** One account row in the per-account waterfall matrix. */
export interface WaterfallAccountRow {
  account_id: number;
  values: Record<string, string>;
}

/** Response from GET /consolidation/waterfall/by-account. */
export interface WaterfallByAccountResponse {
  scenario_id: number;
  period_id: number;
  entity_ids: number[];
  parent_entity_id: number | null;
  accounts: WaterfallAccountRow[];
  columns: string[];
  /** US-STAT-4: stale loaded ratios (loaded ≠ re-derived, workbook Diff semantics). */
  ratio_diffs?: {
    account_id: number;
    account_code: string;
    member: string;
    loaded: string;
    derived: string;
    diff: string;
  }[];
}

// --- Consolidation Engine API (configurable-rules preview) -------------------

/** One row of the configurable Consolidation Rules firing preview. */
export interface MCRPreviewRow {
  entity_id: number;
  ruleset: string;
  status: 'would_post' | 'skipped';
  reason?: string | null;
  line?: string | null;
  processing?: string | null;
  factor_expr?: string | null;
  factor_multiplier?: string | null;
  factor_note?: string | null;
  // Set when the rule-set's condition tests the Intercompany partner, which is
  // only knowable per data-point — so the preview lists the line as firing, and
  // which source rows it actually takes is decided at posting time.
  condition_note?: string | null;
  redirect_target?: string | null;
  redirect_resolved_account?: string | null;
  // Intercompany redirection token resolved at posting time (e.g. "#Source POV Entity#").
  intercompany_redirect?: string | null;
  results_unverified?: boolean;
}

export interface MCRPreviewResponse {
  run_id: number;
  generated_against: string;
  supported_rulesets: string[];
  preview: MCRPreviewRow[];
}

/** Result of a provisional configurable-rules posting (P8 Phase 3). */
export interface MCRPostResponse {
  run_id: number;
  dry_run: boolean;
  posted: number;
  posted_lines: Array<{
    entity_id: number;
    ruleset: string;
    line: string;
    account: string;
    processing: string;
    amount: string;
  }>;
  skipped: Array<Record<string, unknown>>;
  consolidation_code: string;
  results_unverified: boolean;
}

/**
 * Per-POV reporting-currency translation status (US-RC Phase D / plan D-2).
 * Written by the Translate rule (OK / NO_DATA) and flipped to
 * NEEDS_TRANSLATION by data, rate, override or default-method changes.
 * An absent intersection means "never translated".
 */
export type TranslationStatusValue = 'OK' | 'NEEDS_TRANSLATION' | 'NO_DATA';

export interface TranslationStatusRow {
  entity_id: number;
  entity_code: string;
  entity_name: string;
  scenario_id: number;
  fiscal_year_id: number;
  period_id: number;
  period_code: string | null;
  period_name: string | null;
  year: number | null;
  currency_member: string;
  status: TranslationStatusValue | string;
  translated_at: string | null;
  run_id: number | null;
  source: string;
  set_at: string | null;
}

export const consolidationEngineApi = {
  /**
   * Translation status rows for a POV — entity x reporting-currency member
   * (US-RC plan D-2). `scenarioId` is required; the rest narrow the slice.
   * Tenant-scoped; readable by any authenticated role.
   */
  translationStatus: async (params: {
    scenarioId: number;
    year?: number;
    periodId?: number;
    entityId?: number;
    currencyMember?: string;
  }): Promise<TranslationStatusRow[]> => {
    const response = await apiClient.get<TranslationStatusRow[]>('/consolidation-engine/translation-status', {
      params: {
        scenario_id: params.scenarioId,
        ...(params.year != null ? { year: params.year } : {}),
        ...(params.periodId != null ? { period_id: params.periodId } : {}),
        ...(params.entityId != null ? { entity_id: params.entityId } : {}),
        ...(params.currencyMember ? { currency_member: params.currencyMember } : {}),
      },
    });
    return Array.isArray(response.data) ? response.data : [];
  },

  /**
   * Read-only preview of which deployed configurable Consolidation rulesets
   * would fire for an engine run. Posts nothing — reconciliation only.
   * Omit runId to preview against the latest consolidation-engine run.
   */
  mcrPreview: async (runId?: number): Promise<MCRPreviewResponse> => {
    const response = await apiClient.get<MCRPreviewResponse>('/consolidation-engine/mcr-preview', {
      params: runId != null ? { run_id: runId } : undefined,
    });
    return response.data;
  },

  /**
   * Provisionally POST the configurable-rules entries into the segregated
   * CONFIG_CONSOL member. UNVERIFIED — pending finance reconciliation.
   * Requires confirm:true (or dry_run:true to compute only).
   */
  mcrPost: async (opts: { runId?: number; confirm?: boolean; dryRun?: boolean }): Promise<MCRPostResponse> => {
    const response = await apiClient.post<MCRPostResponse>('/consolidation-engine/mcr-post', {
      run_id: opts.runId,
      confirm: opts.confirm ?? false,
      dry_run: opts.dryRun ?? false,
    });
    return response.data;
  },

  /**
   * Trigger a full FCCS consolidation through the multi-step engine.
   * Synchronous: the response carries the completed run + its step results.
   * Set runConfigurableRules to also run the configurable Consolidation Rules
   * step (provisional CONFIG_CONSOL postings; reconciled once finance signs off).
   */
  run: async (payload: {
    scenarioId: number;
    periodId: number;
    entityIds?: number[];
    currencyCode: string;
    runConfigurableRules?: boolean;
  }): Promise<ConsolEngineRunResponse> => {
    const response = await apiClient.post<ConsolEngineRunResponse>('/consolidation-engine/run', {
      scenario_id: payload.scenarioId,
      period_id: payload.periodId,
      entity_ids: payload.entityIds ?? [],
      currency_code: payload.currencyCode,
      run_configurable_rules: payload.runConfigurableRules ?? false,
    });
    return response.data;
  },

  /**
   * Fetch a completed engine run with its step results.
   * Used to display pipeline progress after an SBR Launch.
   */
  getRun: async (runId: number): Promise<ConsolEngineRunResponse> => {
    const response = await apiClient.get<ConsolEngineRunResponse>(`/consolidation-engine/runs/${runId}`);
    return response.data;
  },

  /** Paged run list (newest first); `status` narrows to e.g. 'completed'. */
  listRuns: async (params: { periodId?: number; status?: string; page?: number; pageSize?: number } = {}) => {
    const response = await apiClient.get<{ items: ConsolEngineRunResponse[]; total: number; page: number; page_size: number }>(
      '/consolidation-engine/runs',
      { params: { period_id: params.periodId, status: params.status, page: params.page ?? 1, page_size: params.pageSize ?? 20 } },
    );
    return response.data;
  },

  /**
   * US-REORG-002 — the working model's validation rules (a)–(g) evaluated on
   * the facts a run stored: acquisition / disposal sign-off without a developer.
   */
  getReorgReview: async (runId: number): Promise<ReorgReviewResponse> => {
    const response = await apiClient.get<ReorgReviewResponse>(`/consolidation-engine/runs/${runId}/reorg-review`);
    return response.data;
  },
};

export interface ReorgReviewRow {
  check_id: string;
  description: string;
  entity: string | null;
  parent: string | null;
  account: string | null;
  icp: string | null;
  movement: string | null;
  data_source: string | null;
  expected: number;
  actual: number;
  delta: number;
  status: 'PASS' | 'FAIL' | 'INFO';
  note: string;
}

export interface ReorgReviewResponse {
  run_id: number;
  period: string | null;
  scenario: string | null;
  tops: string[];
  has_ownership_change: boolean;
  has_acq_disp_postings: boolean;
  acq_disp_rule_on: boolean;
  self_icp_rule_on: boolean;
  notes: string[];
  checks: Record<string, string>;
  summary: Record<string, { pass: number; fail: number; info: number }>;
  rows: ReorgReviewRow[];
}

export interface ConsolEngineStep {
  id: number;
  skill_id: string;
  step_order: number;
  status: string;
  records_in: number;
  records_out: number;
  error_message?: string | null;
  summary?: Record<string, unknown> | null;
}

export interface ConsolEngineRunResponse {
  id: number;
  status: string;
  parameters?: Record<string, unknown> | null;
  results_summary?: Record<string, unknown> | null;
  error_message?: string | null;
  steps: ConsolEngineStep[];
}

// --- Reports API -------------------------------------------------------------

// Transform backend report response (sections-based) to frontend FinancialStatement (line_items-based)
function transformReportResponse(data: any): FinancialStatement {
  // If already has line_items, return as-is
  if (data.line_items) return data as FinancialStatement;

  const sectionConfig: Record<string, { label: string; accountType: string }> = {
    assets: { label: 'Assets', accountType: 'asset' },
    liabilities: { label: 'Liabilities', accountType: 'liability' },
    equity: { label: 'Equity', accountType: 'equity' },
    revenue: { label: 'Revenue', accountType: 'revenue' },
    expenses: { label: 'Expenses', accountType: 'expense' },
    operating_activities: { label: 'Operating Activities', accountType: 'expense' },
    investing_activities: { label: 'Investing Activities', accountType: 'asset' },
    financing_activities: { label: 'Financing Activities', accountType: 'liability' },
    cash_and_equivalents: { label: 'Cash and Equivalents', accountType: 'asset' },
  };

  const lineItems: FinancialLineItem[] = [];
  const sections = data.sections || {};

  for (const [sectionKey, items] of Object.entries(sections)) {
    const config = sectionConfig[sectionKey] || { label: sectionKey, accountType: 'asset' };
    const sectionItems = items as any[];
    if (!sectionItems || sectionItems.length === 0) continue;

    // Section header
    lineItems.push({
      account_code: '',
      account_name: config.label,
      account_type: config.accountType as any,
      is_header: true,
      is_total: false,
      indent_level: 0,
      current_period: 0,
      prior_period: null,
      variance: null,
      variance_pct: null,
    });

    // Section items — group by parent_name for hierarchy
    let lastParent: string | null = null;
    for (const item of sectionItems) {
      const parentName = item.parent_name || null;
      if (parentName && parentName !== lastParent) {
        lineItems.push({
          account_code: item.parent_code || '',
          account_name: parentName,
          account_type: config.accountType as any,
          is_header: true,
          is_total: false,
          indent_level: 1,
          current_period: 0,
          prior_period: null,
          variance: null,
          variance_pct: null,
        });
        lastParent = parentName;
      }
      const balance = Number(item.consolidated_balance || item.balance || 0);
      lineItems.push({
        account_code: item.account_code || '',
        account_name: item.account_name || '',
        account_type: config.accountType as any,
        is_header: false,
        is_total: false,
        indent_level: parentName ? 2 : 1,
        current_period: balance,
        prior_period: null,
        variance: null,
        variance_pct: null,
      });
    }

    // Section total
    const totalKey = `total_${sectionKey}`;
    const totalValue = data.totals?.[totalKey];
    if (totalValue !== undefined) {
      lineItems.push({
        account_code: '',
        account_name: `Total ${config.label}`,
        account_type: config.accountType as any,
        is_header: false,
        is_total: true,
        indent_level: 0,
        current_period: Number(totalValue),
        prior_period: null,
        variance: null,
        variance_pct: null,
      });
    }
  }

  // Net income line for income statement
  if (data.net_income !== undefined) {
    lineItems.push({
      account_code: '',
      account_name: 'Net Income',
      account_type: 'revenue' as any,
      is_header: false,
      is_total: true,
      indent_level: 0,
      current_period: Number(data.net_income),
      prior_period: null,
      variance: null,
      variance_pct: null,
    });
  }

  // Unmapped accounts section — show entity accounts that have no group account mapping
  const unmappedAccounts = data.unmapped_accounts || [];
  if (unmappedAccounts.length > 0) {
    lineItems.push({
      account_code: '',
      account_name: `Unmapped Accounts (${unmappedAccounts.length})`,
      account_type: 'asset' as any,
      is_header: true,
      is_total: false,
      indent_level: 0,
      current_period: 0,
      prior_period: null,
      variance: null,
      variance_pct: null,
    });

    for (const ua of unmappedAccounts) {
      const balance = Number(ua.consolidated_balance || ua.balance || 0);
      lineItems.push({
        account_code: ua.account_code || '',
        account_name: ua.account_name || '',
        account_type: 'asset' as any,
        is_header: false,
        is_total: false,
        indent_level: 1,
        current_period: balance,
        prior_period: null,
        variance: null,
        variance_pct: null,
      });
    }

    lineItems.push({
      account_code: '',
      account_name: 'Total Unmapped',
      account_type: 'asset' as any,
      is_header: false,
      is_total: true,
      indent_level: 0,
      current_period: Number(data.unmapped_total || 0),
      prior_period: null,
      variance: null,
      variance_pct: null,
    });
  }

  return {
    statement_type: data.report_type === 'income_statement' ? 'income_statement' : data.report_type === 'cash_flow' ? 'cash_flow' : 'balance_sheet',
    period_name: data.period || 'Current',
    prior_period_name: null,
    entity_name: data.is_consolidated ? 'Consolidated' : '',
    currency: 'AED',
    generated_at: new Date().toISOString(),
    line_items: lineItems,
    totals: Object.fromEntries(
      Object.entries(data.totals || {}).map(([k, v]) => [k, Number(v)])
    ),
    warnings: data.warnings || [],
    unmapped_count: data.unmapped_count || 0,
    unmapped_total: Number(data.unmapped_total || 0),
    raw_unmapped_accounts: data.unmapped_accounts || [],
  };
}

export const reportsApi = {
  balanceSheet: async (params: {
    period_id: string;
    entity_id?: string;
    comparative_period_id?: string;
    comparative?: boolean;
  }): Promise<FinancialStatement> => {
    const response = await apiClient.get<any>('/reports/balance-sheet', { params });
    return transformReportResponse(response.data);
  },

  incomeStatement: async (params: {
    period_id: string;
    entity_id?: string;
    comparative_period_id?: string;
    comparative?: boolean;
  }): Promise<FinancialStatement> => {
    const response = await apiClient.get<any>('/reports/income-statement', { params });
    return transformReportResponse(response.data);
  },

  cashFlow: async (params: {
    period_id: string;
    entity_id?: string;
    comparative_period_id?: string;
    comparative?: boolean;
  }): Promise<FinancialStatement> => {
    const response = await apiClient.get<any>('/reports/cash-flow', { params });
    return transformReportResponse(response.data);
  },

  nciReport: async (params: {
    period_id: string;
    entity_id: string;
  }): Promise<NciReport> => {
    const response = await apiClient.get<NciReport>('/trial-balances/nci-report', { params });
    return response.data;
  },

  intercompanySummary: async (params: { period_id: string }): Promise<IntercompanySummary> => {
    const response = await apiClient.get<IntercompanySummary>('/reports/intercompany-summary', { params });
    return response.data;
  },

  export: async (params: {
    statement_type: string;
    period_id: string;
    format: 'excel' | 'pdf';
    entity_id?: string;
  }): Promise<Blob> => {
    const response = await apiClient.get('/reports/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  drillDown: async (params: {
    period_id: string;
    account_code: string;
    run_id?: string;
  }): Promise<{
    line_item_name: string;
    line_item_code: string;
    consolidated_amount: string | number;
    entity_breakdown: Array<{
      entity_id: number;
      entity_name: string;
      reported_balance: string | number;
      fx_adjustment: string | number;
      elimination_adjustment: string | number;
      equity_adjustment: string | number;
      tax_adjustment: string | number;
      consolidated_balance: string | number;
      currency: string;
      pct_of_total: string | number;
    }>;
  }> => {
    const response = await apiClient.get('/reports/drill-down', { params });
    return response.data;
  },

  monthlyDrilldown: async (params: {
    entity_id: string;
    account_code: string;
    period_id: string;
  }): Promise<{
    account_code: string;
    account_name: string;
    account_type: string;
    entity_name: string;
    target_period: string;
    currency: string;
    months: Array<{
      month: number;
      month_name: string;
      period_id: number;
      cumulative_ytd: number;
      monthly_amount: number;
    }>;
    ytd_total: number;
  }> => {
    const response = await apiClient.get('/reports/monthly-drilldown', { params });
    return response.data;
  },

  financialReports: async (params: {
    entity_id: number;
    year: number;
    scenario?: string;
    view?: 'monthly' | 'quarterly' | 'annual';
  }): Promise<any> => {
    const response = await apiClient.get('/reports/financial-reports', { params });
    return response.data;
  },

  financialReportsExport: async (params: {
    entity_id: number;
    year: number;
    scenario?: string;
    view?: 'monthly' | 'quarterly' | 'annual';
    report_type?: 'pnl' | 'balance_sheet' | 'both';
  }): Promise<Blob> => {
    const response = await apiClient.get('/reports/financial-reports/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  translate: async (data: TranslationRequest): Promise<any> => {
    const response = await apiClient.post('/reports/translate', data);
    return response.data;
  },

  financialReportsExportAll: async (params: {
    year: number;
    scenario?: string;
    view?: 'monthly' | 'quarterly' | 'annual';
    report_type?: 'pnl' | 'balance_sheet' | 'both';
  }): Promise<Blob> => {
    const response = await apiClient.get('/reports/financial-reports/export-all', {
      params,
      responseType: 'blob',
      timeout: 300000, // 5 min timeout for bulk export
    });
    return response.data;
  },

  fccsValidation: async (params: {
    entity_code: string;
    statement_type: string;
    year: number;
  }): Promise<any> => {
    const response = await apiClient.get('/reports/fccs-validation', { params });
    return response.data;
  },

  fccsValidationExport: async (params: {
    entity_id: number;
    entity_code: string;
    year: number;
  }): Promise<Blob> => {
    const response = await apiClient.get('/reports/fccs-validation/export', {
      params,
      responseType: 'blob',
      timeout: 300000, // 5 min timeout for validation export
    });
    return response.data;
  },

  validationFiles: async (): Promise<any[]> => {
    const response = await apiClient.get('/reports/validation-files');
    return response.data;
  },

  uploadValidationFile: async (fileType: string, file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/reports/validation-files/upload?file_type=${fileType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteValidationFile: async (fileId: number): Promise<any> => {
    const response = await apiClient.delete(`/reports/validation-files/${fileId}`);
    return response.data;
  },

  consolidationReviewSummary: async (params: {
    period_id: string;
  }): Promise<ConsolidationReviewSummary> => {
    const response = await apiClient.get<ConsolidationReviewSummary>('/reports/consolidation-review-summary', {
      params,
    });
    return response.data;
  },

  /** FCCS-style multi-column consolidation review (local → translated → proportion → IC → NCI → contribution). */
  consolidationReviewMatrix: async (params: {
    period_id: string;
    /** Limit columns to this entity's subtree (matches Consolidation company selector). */
    scope_entity_id?: string;
  }): Promise<ConsolidationReviewMatrix> => {
    const response = await apiClient.get<ConsolidationReviewMatrix>('/reports/consolidation-review-matrix', {
      params,
    });
    return response.data;
  },

  consolidationReviewMatrixExport: async (params: { period_id: string; scope_entity_id?: string; detailed?: boolean }): Promise<Blob> => {
    const response = await apiClient.get('/reports/consolidation-review-matrix/export', { params, responseType: 'blob' });
    return response.data;
  },

  /** OCI statement (BRD 6.1) */
  ociStatement: async (params: { period_id: string; entity_id?: string; run_id?: string }): Promise<any> => {
    const response = await apiClient.get('/reports/oci', { params });
    return response.data;
  },

  /** Consolidation layer breakdown (BRD 6.1) */
  consolidationLayers: async (params: { period_id: string; run_id?: string }): Promise<any> => {
    const response = await apiClient.get('/reports/consolidation-layers', { params });
    return response.data;
  },

  /** Segment report per IFRS 8 (BRD 6.2) */
  segmentReport: async (params: { period_id: string; dimension?: string; run_id?: string }): Promise<any> => {
    const response = await apiClient.get('/reports/segment-report', { params });
    return response.data;
  },

  /** Disclosure package (BRD 6.3) */
  disclosurePackage: async (params: { period_id: string; entity_id?: string; run_id?: string }): Promise<any> => {
    const response = await apiClient.get('/reports/disclosures', { params });
    return response.data;
  },
};

// --- Periods API -------------------------------------------------------------

export const periodsApi = {
  list: async (params?: {
    status?: string;
    year?: number;
    fiscal_year_id?: number;
    period_role?: string;
    period_member_type?: string;
    page?: number;
    page_size?: number;
  }): Promise<Period[]> => {
    const response = await apiClient.get<any>('/periods', { params });
    return response.data?.items ?? response.data;
  },

  /** All pages (max 500/page on server) — use for pickers that need every matching period. */
  listAll: async (params?: {
    status?: string;
    year?: number;
    fiscal_year_id?: number;
    period_role?: string;
    period_member_type?: string;
  }): Promise<Period[]> => {
    const pageSize = 500;
    const all: Period[] = [];
    for (let page = 1; page < 200; page += 1) {
      const response = await apiClient.get<any>('/periods', {
        params: { ...params, page, page_size: pageSize },
      });
      const batch: Period[] = response.data?.items ?? response.data ?? [];
      if (!Array.isArray(batch) || batch.length === 0) break;
      all.push(...batch);
      if (batch.length < pageSize) break;
    }
    return all;
  },

  create: async (data: Partial<Period>): Promise<Period> => {
    const response = await apiClient.post<Period>('/periods', data);
    return response.data;
  },

  get: async (id: string | number): Promise<Period> => {
    const response = await apiClient.get<Period>(`/periods/${id}`);
    return response.data;
  },

  update: async (id: string | number, data: Partial<Period>): Promise<Period> => {
    const response = await apiClient.put<Period>(`/periods/${id}`, data);
    return response.data;
  },

  open: async (id: string | number, reason?: string): Promise<Period> => {
    const response = await apiClient.post<Period>(`/periods/${id}/open`, { reason });
    return response.data;
  },

  close: async (id: string | number, reason?: string): Promise<Period> => {
    const response = await apiClient.post<Period>(`/periods/${id}/close`, { reason });
    return response.data;
  },

  lock: async (id: string | number, reason: string): Promise<Period> => {
    const response = await apiClient.post<Period>(`/periods/${id}/lock`, { reason });
    return response.data;
  },

  lockThrough: async (id: string | number, reason: string): Promise<Period[]> => {
    const response = await apiClient.post<Period[]>(`/periods/${id}/lock-through`, { reason });
    return response.data;
  },
  reopen: async (id: string | number, reason: string): Promise<Period> => {
    const response = await apiClient.post<Period>(`/periods/${id}/reopen`, { reason });
    return response.data;
  },

  transition: async (
    id: string | number,
    action: 'open' | 'close' | 'lock' | 'reopen',
    reason?: string,
  ): Promise<Period> => {
    const body = reason ? { reason } : undefined;
    const response = await apiClient.post<Period>(`/periods/${id}/${action}`, body);
    return response.data;
  },

  closeChildren: async (id: string | number): Promise<any> => {
    const response = await apiClient.post(`/periods/${id}/close-children`);
    return response.data;
  },

  openChildren: async (id: string | number): Promise<any> => {
    const response = await apiClient.post(`/periods/${id}/open-children`);
    return response.data;
  },

  delete: async (id: string | number): Promise<void> => {
    await apiClient.delete(`/periods/${id}`);
  },

  checkLock: async (params: { year: number; period: string }): Promise<{
    is_locked: boolean;
    period_status: string;
    locked_by: number | null;
    locked_at: string | null;
    locked_reason: string | null;
  }> => {
    const response = await apiClient.get('/periods/check-lock', { params });
    return response.data;
  },
};

// --- Fiscal Years API --------------------------------------------------------

export const fiscalYearsApi = {
  list: async (params?: { status?: string; is_current?: boolean }): Promise<any[]> => {
    const response = await apiClient.get<any>('/fiscal-years', { params });
    return response.data?.items ?? response.data;
  },

  get: async (id: number | string): Promise<any> => {
    const response = await apiClient.get<any>(`/fiscal-years/${id}`);
    return response.data;
  },

  calendarPattern: async (): Promise<{ pattern: { start_month: number; start_day: number } | null }> => {
    const response = await apiClient.get<any>('/fiscal-years/calendar-pattern');
    return response.data;
  },

  create: async (data: {
    year: number;
    label: string;
    start_date: string;
    end_date: string;
    num_periods?: number;
    calendar_type?: string;
    quarter_pattern?: string;
    auto_generate_members?: boolean;
    is_current?: boolean;
    enable_approvals?: boolean;
    comparative_year_id?: number | null;
  }): Promise<any> => {
    const response = await apiClient.post('/fiscal-years', {
      auto_generate_members: true,
      ...data,
    });
    return response.data;
  },

  update: async (id: number | string, data: any): Promise<any> => {
    const response = await apiClient.put(`/fiscal-years/${id}`, data);
    return response.data;
  },

  generateMembers: async (id: number | string): Promise<any[]> => {
    const response = await apiClient.post(`/fiscal-years/${id}/generate-members`);
    return response.data;
  },

  getTree: async (id: number | string): Promise<any> => {
    const response = await apiClient.get(`/fiscal-years/${id}/tree`);
    return response.data;
  },

  close: async (id: number | string, reason?: string): Promise<any> => {
    const response = await apiClient.post(`/fiscal-years/${id}/close`, { reason });
    return response.data;
  },

  lock: async (id: number | string, reason: string): Promise<any> => {
    const response = await apiClient.post(`/fiscal-years/${id}/lock`, { reason });
    return response.data;
  },

  reopen: async (id: number | string, reason: string): Promise<any> => {
    const response = await apiClient.post(`/fiscal-years/${id}/reopen`, { reason });
    return response.data;
  },

  listPeriods: async (
    id: number | string,
    role?: 'base' | 'rollup' | 'adjustment',
  ): Promise<Period[]> => {
    const response = await apiClient.get<any>(`/fiscal-years/${id}/periods`, {
      params: role ? { role } : undefined,
    });
    return response.data?.items ?? response.data;
  },
};

// --- Currencies API ----------------------------------------------------------

export const currenciesApi = {
  list: async (params?: { is_active?: boolean }): Promise<Currency[]> => {
    const response = await apiClient.get<Currency[]>('/currencies', { params });
    return response.data;
  },

  create: async (data: { code: string; name: string; symbol?: string }): Promise<Currency> => {
    const response = await apiClient.post<Currency>('/currencies', data);
    return response.data;
  },

  update: async (id: string, data: { name?: string; symbol?: string; is_active?: boolean }): Promise<Currency> => {
    const response = await apiClient.put<Currency>(`/currencies/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/currencies/${id}`);
  },
};

// --- FX Rates API ------------------------------------------------------------

export const fxRatesApi = {
  list: async (params?: { period_id?: string }): Promise<FxRate[]> => {
    const response = await apiClient.get<any>('/fx-rates', { params });
    return response.data?.items ?? response.data;
  },

  create: async (data: Partial<FxRate>): Promise<FxRate> => {
    const response = await apiClient.post<FxRate>('/fx-rates', data);
    return response.data;
  },

  bulkCreate: async (data: Partial<FxRate>[]): Promise<FxRate[]> => {
    const response = await apiClient.post<FxRate[]>('/fx-rates/bulk', data);
    return response.data;
  },

  update: async (id: string, data: Partial<FxRate>): Promise<FxRate> => {
    const response = await apiClient.put<FxRate>(`/fx-rates/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/fx-rates/${id}`);
  },
};

// --- Currency Triangulation API ----------------------------------------------

export interface TriangulationPreviewRow {
  period: string | null;
  kind: string;
  from_currency: string;
  to_currency: string;
  rate: number | null;
}

export interface TriangulationResult {
  cells_written: number;
  cells_deleted: number;
  pivot_currency: string | null;
  from_currencies: string[];
  to_currencies: string[];
  preview: TriangulationPreviewRow[];
  message: string;
}

export const triangulationApi = {
  // Compute the cross-currency rate matrix for a scenario + year and persist it
  // to the "Exchange Rates" accounts (visible in the triangulation data form).
  calculate: async (data: {
    scenario: string;
    year: number;
    accounts?: 'both' | 'average' | 'ending';
    /** The one month to compute, e.g. 'Jan'. Triangulation runs month at a time. */
    period: string;
  }): Promise<TriangulationResult> => {
    const response = await apiClient.post<TriangulationResult>(
      '/currency-triangulation/calculate',
      data,
    );
    return response.data;
  },
};

// --- Entity Code Mapping API -------------------------------------------------

export const entityCodeMappingApi = {
  list: async (params?: {
    source_system?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<EntityCodeMapping>> => {
    const response = await apiClient.get<PaginatedResponse<EntityCodeMapping>>(
      '/entity-code-mappings',
      { params }
    );
    return response.data;
  },

  create: async (data: {
    source_system: string;
    external_code: string;
    entity_id: number;
  }): Promise<EntityCodeMapping> => {
    const response = await apiClient.post<EntityCodeMapping>(
      '/entity-code-mappings',
      data
    );
    return response.data;
  },

  update: async (
    id: number,
    data: Partial<{ source_system: string; external_code: string; entity_id: number }>
  ): Promise<EntityCodeMapping> => {
    const response = await apiClient.put<EntityCodeMapping>(
      `/entity-code-mappings/${id}`,
      data
    );
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/entity-code-mappings/${id}`);
  },

  validateCodes: async (codes: string[], sourceSystem = 'Excel'): Promise<ValidateCodesResponse> => {
    const response = await apiClient.post<ValidateCodesResponse>(
      '/entity-code-mappings/validate-codes',
      { codes, source_system: sourceSystem }
    );
    return response.data;
  },

  unmappedEntityIds: async (sourceSystem = 'Excel'): Promise<{ unmapped_entity_ids: number[] }> => {
    const response = await apiClient.get<{ unmapped_entity_ids: number[] }>(
      '/entity-code-mappings/unmapped-entity-ids',
      { params: { source_system: sourceSystem } }
    );
    return response.data;
  },

  unmappedIcpCodes: async (sourceSystem = 'Excel'): Promise<{ unmapped_icp_codes: string[] }> => {
    const response = await apiClient.get<{ unmapped_icp_codes: string[] }>(
      '/entity-code-mappings/unmapped-icp-codes',
      { params: { source_system: sourceSystem } }
    );
    return response.data;
  },

  bulkCreate: async (data: {
    source_system?: string;
    mappings: Array<{ external_code: string; entity_id: number }>;
  }): Promise<{ created: EntityCodeMapping[]; errors: string[]; skipped: number }> => {
    const response = await apiClient.post<{
      created: EntityCodeMapping[];
      errors: string[];
      skipped: number;
    }>('/entity-code-mappings/bulk', {
      source_system: data.source_system ?? 'Excel',
      mappings: data.mappings,
    });
    return response.data;
  },

  /** One-time CSV import: FCCS_Intercompany Entities + Intercompany Entities columns. */
  uploadCsv: async (
    file: File,
    sourceSystem = 'FCCS'
  ): Promise<{
    created: EntityCodeMapping[];
    updated: EntityCodeMapping[];
    skipped: number;
    errors: string[];
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(
      '/entity-code-mappings/upload-csv',
      formData,
      {
        params: { source_system: sourceSystem },
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  },
};

// --- Intercompany API --------------------------------------------------------

export const intercompanyApi = {
  list: async (params?: {
    period_id?: string;
    from_date?: string;
    to_date?: string;
    entity_id?: string;
    match_status?: string;
    page?: number;
    page_size?: number;
    /** When true (default on backend), hide lines already paired / cleared. */
    work_queue_only?: boolean;
  }): Promise<PaginatedResponse<IntercompanyTransaction>> => {
    const response = await apiClient.get<PaginatedResponse<IntercompanyTransaction>>('/intercompany', { params });
    return response.data;
  },

  create: async (data: Partial<IntercompanyTransaction>): Promise<IntercompanyTransaction> => {
    const response = await apiClient.post<IntercompanyTransaction>('/intercompany', data);
    return response.data;
  },

  bulkCreate: async (data: Partial<IntercompanyTransaction>[]): Promise<IntercompanyTransaction[]> => {
    const response = await apiClient.post<IntercompanyTransaction[]>('/intercompany/bulk', data);
    return response.data;
  },

  update: async (id: string, data: Partial<IntercompanyTransaction>): Promise<IntercompanyTransaction> => {
    const response = await apiClient.put<IntercompanyTransaction>(`/intercompany/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/intercompany/${id}`);
  },

  summary: async (params: {
    period_id?: string;
    from_date?: string;
    to_date?: string;
  }): Promise<IntercompanySummary> => {
    const response = await apiClient.get<IntercompanySummary>(`/intercompany/summary`, {
      params,
    });
    return response.data;
  },

  reconciliationReport: async (periodId: string): Promise<IntercompanyReconciliationReport> => {
    const response = await apiClient.get<IntercompanyReconciliationReport>(
      '/intercompany/reconciliation-report',
      { params: { period_id: periodId } }
    );
    return response.data;
  },

  eliminationCompleteness: async (periodId: string): Promise<{
    period_id: number;
    is_complete: boolean;
    message: string;
    matched_count: number;
    unmatched_count: number;
    mirror_gap_count?: number;
    total_ic_exposure: string;
  }> => {
    const response = await apiClient.get('/intercompany/elimination-completeness', {
      params: { period_id: periodId },
    });
    return response.data;
  },

  createDisputesFromUnmatched: async (periodId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post<{ success: boolean; message: string }>(
      '/intercompany/create-disputes-from-unmatched',
      null,
      { params: { period_id: periodId } }
    );
    return response.data;
  },

  workflowReadiness: async (
    periodId: string,
    sourceSystem = 'Excel'
  ): Promise<{
    period_id: number;
    source_system: string;
    metrics: Record<string, unknown>;
    steps: Array<{ id: string; title: string; ok: boolean; detail: string }>;
  }> => {
    const response = await apiClient.get('/intercompany/workflow-readiness', {
      params: { period_id: periodId, source_system: sourceSystem },
    });
    return response.data;
  },

  syncFromTrialBalance: async (
    periodId: string,
    sourceSystem = 'Excel'
  ): Promise<{
    removed: number;
    created: number;
    skipped_no_icp_mapping?: number;
    skipped_partner_config?: number;
    skipped_zero_net?: number;
    message: string;
  }> => {
    const response = await apiClient.post(
      '/intercompany/sync-from-trial-balance',
      {},
      { params: { period_id: periodId, source_system: sourceSystem } }
    );
    return response.data;
  },

  importFromTrialBalance: async (
    periodId: string,
    sourceSystem = 'Excel'
  ): Promise<{
    removed: number;
    created: number;
    message: string;
    skipped_no_icp_mapping?: number;
    skipped_partner_config?: number;
  }> => {
    const response = await apiClient.post(
      '/intercompany/import-from-trial-balance',
      {},
      { params: { period_id: periodId, source_system: sourceSystem } }
    );
    return response.data;
  },

  testElimination: async (
    periodId: string,
    sourceSystem = 'Excel',
    transactionTypeDefault = 'trade'
  ): Promise<IntercompanyEliminationTestResult> => {
    const response = await apiClient.post<IntercompanyEliminationTestResult>(
      '/intercompany/test-elimination',
      {},
      {
        params: {
          period_id: periodId,
          source_system: sourceSystem,
          transaction_type_default: transactionTypeDefault,
        },
      }
    );
    return response.data;
  },

  downloadEliminationWorksheet: async (periodId: string): Promise<void> => {
    // POST avoids 405 from some proxies that only allow POST under /api.
    const response = await apiClient.post('/intercompany/elimination-worksheet', {}, {
      params: { period_id: periodId },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `ic_elimination_worksheet_period_${periodId}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};

// --- IC Disputes API ---------------------------------------------------------

export const icDisputesApi = {
  list: async (params?: {
    period_id?: number;
    status?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<IcDispute>> => {
    const response = await apiClient.get<PaginatedResponse<IcDispute>>('/ic-disputes', { params });
    return response.data;
  },

  create: async (data: {
    period_id: number;
    entity_from_id: number;
    entity_to_id: number;
    amount_from: number;
    amount_to: number;
    currency: string;
    transaction_type: string;
    description?: string;
    transaction_from_id?: number;
    transaction_to_id?: number;
    resolution_deadline?: string;
  }): Promise<IcDispute> => {
    const response = await apiClient.post<IcDispute>('/ic-disputes', data);
    return response.data;
  },

  update: async (
    id: number,
    data: Partial<{ status: string; proposed_adjustment: string; resolution_notes: string; escalated_to_user_id: number }>
  ): Promise<IcDispute> => {
    const response = await apiClient.put<IcDispute>(`/ic-disputes/${id}`, data);
    return response.data;
  },
};

// --- Users API ---------------------------------------------------------------

export const usersApi = {
  list: async (): Promise<User[]> => {
    const response = await apiClient.get<any>('/users');
    return response.data?.items ?? response.data;
  },

  create: async (data: UserCreate): Promise<User> => {
    const response = await apiClient.post<User>('/users', data);
    return response.data;
  },

  get: async (id: string): Promise<User> => {
    const response = await apiClient.get<User>(`/users/${id}`);
    return response.data;
  },

  update: async (id: string, data: UserUpdate): Promise<User> => {
    const response = await apiClient.put<User>(`/users/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/users/${id}`);
  },

  changePassword: async (id: string, data: ChangePasswordRequest): Promise<void> => {
    await apiClient.put(`/users/${id}/password`, data);
  },
};

// --- Skills API --------------------------------------------------------------

export const skillsApi = {
  list: async (): Promise<SkillExecution[]> => {
    const response = await apiClient.get<SkillExecution[]>('/skills');
    return response.data;
  },

  execute: async (skillName: string, params?: Record<string, unknown>): Promise<SkillExecution> => {
    const response = await apiClient.post<SkillExecution>(`/skills/${skillName}/execute`, params);
    return response.data;
  },
};

// --- Audit API ---------------------------------------------------------------

export const auditApi = {
  list: async (params: {
    page?: number;
    action?: string;
    resource_type?: string;
    user_id?: number;
    from_date?: string;
    to_date?: string;
  }): Promise<PaginatedResponse<AuditLog>> => {
    const response = await apiClient.get<PaginatedResponse<AuditLog>>('/audit', { params });
    return response.data;
  },

  get: async (id: number): Promise<AuditLog> => {
    const response = await apiClient.get<AuditLog>(`/audit/${id}`);
    return response.data;
  },

  summary: async (): Promise<AuditSummary> => {
    const response = await apiClient.get<AuditSummary>('/audit/summary');
    return response.data;
  },

  /** Pre-built journal testing report (BRD 7.2) */
  journalTestingReport: async (periodId: number): Promise<any> => {
    const response = await apiClient.get('/audit/reports/journal-testing', { params: { period_id: periodId } });
    return response.data;
  },

  /** Pre-built reconciliation status report (BRD 7.2) */
  reconciliationStatusReport: async (periodId: number): Promise<any> => {
    const response = await apiClient.get('/audit/reports/reconciliation-status', { params: { period_id: periodId } });
    return response.data;
  },

  /** Pre-built IC elimination completeness report (BRD 7.2) */
  icEliminationReport: async (periodId: number): Promise<any> => {
    const response = await apiClient.get('/audit/reports/ic-elimination-completeness', { params: { period_id: periodId } });
    return response.data;
  },

  /** SOX certifications (BRD 7.2) */
  getCertifications: async (periodId: number): Promise<any> => {
    const response = await apiClient.get(`/audit/certifications/${periodId}`);
    return response.data;
  },

  createCertification: async (data: { period_id: number; certification_type: string; statement: string; exceptions?: string[] }): Promise<any> => {
    const response = await apiClient.post('/audit/certifications', data);
    return response.data;
  },

  /** Continuous audit monitoring (BRD Module 16) */
  continuousMonitoring: async (hours?: number): Promise<any> => {
    const response = await apiClient.get('/audit/continuous-monitoring', { params: { hours: hours || 24 } });
    return response.data;
  },
};

// --- Tax API -----------------------------------------------------------------

export const taxApi = {
  compute: async (data: {
    period_id: number;
    entity_ids?: number[];
    parameters?: Record<string, any>;
  }): Promise<TaxComputeResponse> => {
    const response = await apiClient.post<TaxComputeResponse>('/tax/compute', data);
    return response.data;
  },

  results: async (periodId: number): Promise<TaxComputeResponse> => {
    const response = await apiClient.get<TaxComputeResponse>('/tax/results', {
      params: { period_id: periodId },
    });
    return response.data;
  },

  entityDetail: async (entityId: number, periodId: number): Promise<EntityTaxDetail> => {
    const response = await apiClient.get<EntityTaxDetail>(`/tax/entities/${entityId}/detail`, {
      params: { period_id: periodId },
    });
    return response.data;
  },

  adjustments: async (entityId: number, data: any): Promise<EntityTaxDetail> => {
    const response = await apiClient.put<EntityTaxDetail>(`/tax/entities/${entityId}/adjustments`, data);
    return response.data;
  },

  qfzpStatus: async (): Promise<QfzpEntityStatus[]> => {
    const response = await apiClient.get<QfzpEntityStatus[]>('/tax/qfzp-status');
    return response.data;
  },

  sbrEligible: async (): Promise<EntityTaxDetail[]> => {
    const response = await apiClient.get<EntityTaxDetail[]>('/tax/sbr-eligible');
    return response.data;
  },

  /** DMTT (Pillar Two) computation (BRD Module 19) */
  dmtt: async (periodId: number): Promise<any> => {
    const response = await apiClient.post('/tax/dmtt', null, { params: { period_id: periodId } });
    return response.data;
  },

  /** Tax effect of consolidation adjustments (BRD Module 19) */
  consolidationTaxEffect: async (periodId: number, runId?: number): Promise<any> => {
    const params: any = { period_id: periodId };
    if (runId) params.run_id = runId;
    const response = await apiClient.get('/tax/consolidation-tax-effect', { params });
    return response.data;
  },
};

// --- Transfer Pricing API ----------------------------------------------------

export const transferPricingApi = {
  analyze: async (data: { period_id: number }): Promise<TPTransaction[]> => {
    const response = await apiClient.post<TPTransaction[]>('/transfer-pricing/analyze', data);
    return response.data;
  },

  results: async (periodId: number): Promise<TPTransaction[]> => {
    const response = await apiClient.get<TPTransaction[]>('/transfer-pricing/results', {
      params: { period_id: periodId },
    });
    return response.data;
  },

  transaction: async (id: number): Promise<TPTransaction> => {
    const response = await apiClient.get<TPTransaction>(`/transfer-pricing/transactions/${id}`);
    return response.data;
  },

  documentation: async (data: { period_id: number }): Promise<Blob> => {
    const response = await apiClient.post('/transfer-pricing/documentation', data, {
      responseType: 'blob',
    });
    return response.data;
  },

  summary: async (periodId: number): Promise<TPSummary> => {
    const response = await apiClient.get<TPSummary>('/transfer-pricing/summary', {
      params: { period_id: periodId },
    });
    return response.data;
  },
};

// --- Anomaly Detection API ---------------------------------------------------

export const anomalyApi = {
  detect: async (data: {
    period_id: number;
    parameters?: Record<string, any>;
  }): Promise<Anomaly[]> => {
    const response = await apiClient.post<Anomaly[]>('/anomalies/detect', data);
    return response.data;
  },

  results: async (
    periodId: number,
    params?: { severity?: string; category?: string },
  ): Promise<Anomaly[]> => {
    const response = await apiClient.get<Anomaly[]>('/anomalies/results', {
      params: { period_id: periodId, ...params },
    });
    return response.data;
  },

  summary: async (periodId: number): Promise<AnomalySummary> => {
    const response = await apiClient.get<AnomalySummary>('/anomalies/summary', {
      params: { period_id: periodId },
    });
    return response.data;
  },

  get: async (id: number): Promise<Anomaly> => {
    const response = await apiClient.get<Anomaly>(`/anomalies/${id}`);
    return response.data;
  },

  resolve: async (
    id: number,
    data: { resolution_status: string; resolution_notes: string },
  ): Promise<Anomaly> => {
    const response = await apiClient.put<Anomaly>(`/anomalies/${id}/resolve`, data);
    return response.data;
  },

  benford: async (periodId: number): Promise<BenfordAnalysis> => {
    const response = await apiClient.get<BenfordAnalysis>('/anomalies/benford', {
      params: { period_id: periodId },
    });
    return response.data;
  },
};

// --- Exports API -------------------------------------------------------------

export const exportsApi = {
  balanceSheet: async (data: ExportRequest): Promise<Blob> => {
    const response = await apiClient.post('/exports/balance-sheet', data, { responseType: 'blob' });
    return response.data;
  },

  incomeStatement: async (data: ExportRequest): Promise<Blob> => {
    const response = await apiClient.post('/exports/income-statement', data, { responseType: 'blob' });
    return response.data;
  },

  cashFlow: async (data: ExportRequest): Promise<Blob> => {
    const response = await apiClient.post('/exports/cash-flow', data, { responseType: 'blob' });
    return response.data;
  },

  trialBalance: async (data: {
    report_type: string;
    period_id: number;
    entity_id: number;
    format?: string;
    company_name?: string;
    export_original?: boolean;
    export_translated?: boolean;
    include_cta?: boolean;
    to_currency?: string;
  }): Promise<Blob> => {
    const response = await apiClient.post('/exports/trial-balance', data, { responseType: 'blob' });
    return response.data;
  },

  consolidationPackage: async (data: any): Promise<Blob> => {
    const response = await apiClient.post('/exports/consolidation-package', data, { responseType: 'blob' });
    return response.data;
  },
};

// --- ERP API -----------------------------------------------------------------

export const erpApi = {
  connectors: async (): Promise<ERPConnector[]> => {
    const response = await apiClient.get<ERPConnector[]>('/erp/connectors');
    return response.data;
  },

  testConnection: async (data: any): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post<{ success: boolean; message: string }>('/erp/test-connection', data);
    return response.data;
  },

  importTrialBalance: async (data: any): Promise<{ count: number; message: string }> => {
    const response = await apiClient.post<{ count: number; message: string }>('/erp/import/trial-balance', data);
    return response.data;
  },

  importChartOfAccounts: async (data: any): Promise<{ count: number; message: string }> => {
    const response = await apiClient.post<{ count: number; message: string }>('/erp/import/chart-of-accounts', data);
    return response.data;
  },
};

// --- Notifications API -------------------------------------------------------

export const notificationsApi = {
  list: async (params?: { page?: number; page_size?: number }): Promise<PaginatedResponse<Notification>> => {
    const response = await apiClient.get<PaginatedResponse<Notification>>('/notifications', { params });
    return response.data;
  },

  unreadCount: async (): Promise<{ count: number }> => {
    const response = await apiClient.get<{ count: number }>('/notifications/unread-count');
    return response.data;
  },

  markRead: async (id: string): Promise<void> => {
    await apiClient.put(`/notifications/${id}/read`);
  },

  markAllRead: async (): Promise<void> => {
    await apiClient.put('/notifications/read-all');
  },
};

// --- Approvals API -----------------------------------------------------------

export const approvalsApi = {
  list: async (params?: { status?: string; workflow_type?: string }): Promise<ApprovalWorkflow[]> => {
    const response = await apiClient.get<ApprovalWorkflow[]>('/approvals', { params });
    return response.data;
  },

  pending: async (): Promise<ApprovalWorkflow[]> => {
    const response = await apiClient.get<ApprovalWorkflow[]>('/approvals/pending');
    return response.data;
  },

  myRequests: async (): Promise<ApprovalWorkflow[]> => {
    const response = await apiClient.get<ApprovalWorkflow[]>('/approvals/my-requests');
    return response.data;
  },

  approve: async (id: string, comments?: string): Promise<ApprovalWorkflow> => {
    const response = await apiClient.post<ApprovalWorkflow>(`/approvals/${id}/approve`, { comments });
    return response.data;
  },

  reject: async (id: string, comments?: string): Promise<ApprovalWorkflow> => {
    const response = await apiClient.post<ApprovalWorkflow>(`/approvals/${id}/reject`, { comments });
    return response.data;
  },

  create: async (data: Partial<ApprovalWorkflow>): Promise<ApprovalWorkflow> => {
    const response = await apiClient.post<ApprovalWorkflow>('/approvals', data);
    return response.data;
  },
};

// --- Natural Language Query API -----------------------------------------------

export const nlQueryApi = {
  submit: async (query: string): Promise<NLQueryResponse> => {
    const response = await apiClient.post<NLQueryResponse>('/nl-query', { query });
    return response.data;
  },

  suggestions: async (): Promise<string[]> => {
    const response = await apiClient.get<string[]>('/nl-query/suggestions');
    return response.data;
  },
};

// --- XBRL API ----------------------------------------------------------------

export const xbrlApi = {
  generate: async (data: { consolidation_run_id: string; taxonomy?: string }): Promise<Blob> => {
    const response = await apiClient.post('/xbrl/generate', data, { responseType: 'blob' });
    return response.data;
  },

  taxonomy: async (): Promise<{ id: string; name: string; version: string }[]> => {
    const response = await apiClient.get<{ id: string; name: string; version: string }[]>('/xbrl/taxonomies');
    return response.data;
  },

  mappings: async (): Promise<XBRLMapping[]> => {
    const response = await apiClient.get<XBRLMapping[]>('/xbrl/mappings');
    return response.data;
  },

  updateMapping: async (accountCode: string, data: Partial<XBRLMapping>): Promise<XBRLMapping> => {
    const response = await apiClient.put<XBRLMapping>(`/xbrl/mappings/${accountCode}`, data);
    return response.data;
  },

  validate: async (data: { consolidation_run_id: string }): Promise<XBRLValidationResult> => {
    const response = await apiClient.post<XBRLValidationResult>('/xbrl/validate', data);
    return response.data;
  },

  preview: async (data: { consolidation_run_id: string }): Promise<{ content: string; format: string }> => {
    const response = await apiClient.post<{ content: string; format: string }>('/xbrl/preview', data);
    return response.data;
  },
};

// --- Background Tasks API ----------------------------------------------------

export const tasksApi = {
  list: async (): Promise<BackgroundTask[]> => {
    const response = await apiClient.get<BackgroundTask[]>('/tasks');
    return response.data;
  },

  get: async (id: string): Promise<BackgroundTask> => {
    const response = await apiClient.get<BackgroundTask>(`/tasks/${id}`);
    return response.data;
  },

  cancel: async (id: string): Promise<void> => {
    await apiClient.post(`/tasks/${id}/cancel`);
  },
};

// --- Jobs API ----------------------------------------------------------------

export const jobsApi = {
  list: async (params?: { status?: string; task_type?: string; page?: number; page_size?: number }) => {
    const { task_type, ...rest } = params || {};
    const res = await apiClient.get('/jobs', { params: { ...rest, ...(task_type ? { type: task_type } : {}) } });
    return res.data;
  },
  get: async (jobId: number) => {
    const res = await apiClient.get(`/jobs/${jobId}`);
    return res.data;
  },
  run: async (data: { job_type: string; job_name?: string; parameters?: Record<string, unknown>; job_category?: string }) => {
    const res = await apiClient.post('/jobs/run', data);
    return res.data;
  },
  cancelAllPending: async () => {
    const res = await apiClient.post('/jobs/cancel-pending');
    return res.data as { cancelled: number; message: string };
  },
  cancel: async (jobId: number) => {
    const res = await apiClient.post(`/jobs/${jobId}/cancel`);
    return res.data;
  },
  retry: async (jobId: number) => {
    const res = await apiClient.post(`/jobs/${jobId}/retry`);
    return res.data;
  },
  delete: async (jobId: number) => {
    const res = await apiClient.delete(`/jobs/${jobId}`);
    return res.data;
  },
  // Download a completed job's output file (e.g. journal export). Streams the
  // file as a blob and triggers a browser download using the server filename.
  downloadResult: async (jobId: number): Promise<void> => {
    const response = await apiClient.get(`/jobs/${jobId}/download`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const disposition = response.headers['content-disposition'] as string | undefined;
    const filename = disposition?.match(/filename="?([^"]+)"?/)?.[1] || `job_${jobId}_result`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  stats: async () => {
    const res = await apiClient.get('/jobs/stats/summary');
    return res.data;
  },

  // Schedules
  listSchedules: async (params?: { page?: number; page_size?: number }) => {
    const res = await apiClient.get('/jobs/schedules', { params });
    return res.data;
  },
  createSchedule: async (data: {
    job_name: string;
    job_type: string;
    parameters?: Record<string, unknown>;
    recurrence_type: string;
    recurrence_interval?: number;
    scheduled_start?: string;
    scheduled_end?: string;
    timezone?: string;
    is_active?: boolean;
  }) => {
    const res = await apiClient.post('/jobs/schedules', data);
    return res.data;
  },
  updateSchedule: async (id: number, data: Record<string, unknown>) => {
    const res = await apiClient.put(`/jobs/schedules/${id}`, data);
    return res.data;
  },
  deleteSchedule: async (id: number) => {
    const res = await apiClient.delete(`/jobs/schedules/${id}`);
    return res.data;
  },
  pauseSchedule: async (id: number) => {
    const res = await apiClient.post(`/jobs/schedules/${id}/pause`);
    return res.data;
  },
  resumeSchedule: async (id: number) => {
    const res = await apiClient.post(`/jobs/schedules/${id}/resume`);
    return res.data;
  },
  runScheduleNow: async (id: number) => {
    const res = await apiClient.post(`/jobs/schedules/${id}/run-now`);
    return res.data;
  },
};

// --- SSO API -----------------------------------------------------------------

export const ssoApi = {
  providers: async (): Promise<SSOProvider[]> => {
    const response = await apiClient.get<SSOProvider[]>('/auth/sso/providers');
    return response.data;
  },

  configure: async (providerId: string, data: Record<string, any>): Promise<SSOProvider> => {
    const response = await apiClient.put<SSOProvider>(`/auth/sso/providers/${providerId}`, data);
    return response.data;
  },
};

// --- MFA API -----------------------------------------------------------------

export const mfaApi = {
  setup: async (): Promise<MFASetup> => {
    const response = await apiClient.post<MFASetup>('/auth/mfa/setup');
    return response.data;
  },

  verify: async (code: string): Promise<{ verified: boolean }> => {
    const response = await apiClient.post<{ verified: boolean }>('/auth/mfa/verify', { code });
    return response.data;
  },

  disable: async (code: string): Promise<void> => {
    await apiClient.post('/auth/mfa/disable', { code });
  },

  status: async (): Promise<{ enabled: boolean }> => {
    const response = await apiClient.get<{ enabled: boolean }>('/auth/mfa/status');
    return response.data;
  },
};

// --- EmaraTax API ------------------------------------------------------------

export const emaraTaxApi = {
  prepare: async (data: { period_id: string; return_type?: string }): Promise<TaxReturn> => {
    const response = await apiClient.post<TaxReturn>('/emara-tax/prepare', data);
    return response.data;
  },

  validate: async (id: string): Promise<{ is_valid: boolean; errors: string[] }> => {
    const response = await apiClient.post<{ is_valid: boolean; errors: string[] }>(`/emara-tax/${id}/validate`);
    return response.data;
  },

  submit: async (id: string): Promise<TaxReturn> => {
    const response = await apiClient.post<TaxReturn>(`/emara-tax/${id}/submit`);
    return response.data;
  },

  list: async (params?: { period_id?: string }): Promise<TaxReturn[]> => {
    const response = await apiClient.get<TaxReturn[]>('/emara-tax', { params });
    return response.data;
  },
};

// --- ESG API -----------------------------------------------------------------

export const esgApi = {
  metrics: async (params?: { entity_id?: string; category?: string; period_id?: string }): Promise<ESGMetric[]> => {
    const response = await apiClient.get<ESGMetric[]>('/esg/metrics', { params });
    return response.data;
  },

  createMetric: async (data: Partial<ESGMetric>): Promise<ESGMetric> => {
    const response = await apiClient.post<ESGMetric>('/esg/metrics', data);
    return response.data;
  },

  report: async (params?: { period_id?: string; framework?: string }): Promise<Blob> => {
    const response = await apiClient.get('/esg/report', { params, responseType: 'blob' });
    return response.data;
  },

  dashboard: async (params?: { period_id?: string }): Promise<ESGDashboardData> => {
    const response = await apiClient.get<ESGDashboardData>('/esg/dashboard', { params });
    return response.data;
  },

  targets: async (): Promise<ESGTarget[]> => {
    const response = await apiClient.get<ESGTarget[]>('/esg/targets');
    return response.data;
  },

  createTarget: async (data: Partial<ESGTarget>): Promise<ESGTarget> => {
    const response = await apiClient.post<ESGTarget>('/esg/targets', data);
    return response.data;
  },
};

// --- Forecasting API ---------------------------------------------------------

export const forecastApi = {
  generate: async (data: {
    entity_ids?: string[];
    account_codes?: string[];
    periods_ahead?: number;
    method?: string;
  }): Promise<ForecastResult[]> => {
    const response = await apiClient.post<ForecastResult[]>('/forecasts/generate', data);
    return response.data;
  },

  list: async (params?: { entity_id?: string }): Promise<ForecastResult[]> => {
    const response = await apiClient.get<ForecastResult[]>('/forecasts', { params });
    return response.data;
  },

  get: async (id: string): Promise<ForecastResult> => {
    const response = await apiClient.get<ForecastResult>(`/forecasts/${id}`);
    return response.data;
  },

  compareActuals: async (forecastId: string): Promise<{
    forecast: ForecastResult;
    actuals: { period: string; value: number }[];
    accuracy_pct: number;
  }> => {
    const response = await apiClient.get(`/forecasts/${forecastId}/compare-actuals`);
    return response.data;
  },

  export: async (params: { entity_id?: string; format?: string }): Promise<Blob> => {
    const response = await apiClient.get('/forecasts/export', { params, responseType: 'blob' });
    return response.data;
  },
};

// --- Multi-GAAP API ----------------------------------------------------------

export const multiGaapApi = {
  convert: async (data: {
    period_id: string;
    source_gaap: string;
    target_gaap: string;
  }): Promise<GaapConversion> => {
    const response = await apiClient.post<GaapConversion>('/multi-gaap/convert', data);
    return response.data;
  },

  standards: async (): Promise<{ code: string; name: string; description: string }[]> => {
    const response = await apiClient.get<{ code: string; name: string; description: string }[]>('/multi-gaap/standards');
    return response.data;
  },

  reconciliation: async (params: {
    period_id: string;
    source_gaap: string;
    target_gaap: string;
  }): Promise<GaapConversion> => {
    const response = await apiClient.get<GaapConversion>('/multi-gaap/reconciliation', { params });
    return response.data;
  },
};

// --- Marketplace API ---------------------------------------------------------

export const marketplaceApi = {
  list: async (params?: { category?: string; search?: string }): Promise<MarketplaceSkill[]> => {
    const response = await apiClient.get<MarketplaceSkill[]>('/marketplace/skills', { params });
    return response.data;
  },

  get: async (skillId: string): Promise<MarketplaceSkill> => {
    const response = await apiClient.get<MarketplaceSkill>(`/marketplace/skills/${skillId}`);
    return response.data;
  },

  install: async (skillId: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(`/marketplace/skills/${skillId}/install`);
    return response.data;
  },

  uninstall: async (skillId: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(`/marketplace/skills/${skillId}/uninstall`);
    return response.data;
  },

  installed: async (): Promise<MarketplaceSkill[]> => {
    const response = await apiClient.get<MarketplaceSkill[]>('/marketplace/installed');
    return response.data;
  },
};

// --- Blockchain Audit API ----------------------------------------------------

export const blockchainAuditApi = {
  chain: async (params?: {
    page?: number;
    page_size?: number;
    event_type?: string;
    from_date?: string;
    to_date?: string;
  }): Promise<PaginatedResponse<AuditBlock>> => {
    const response = await apiClient.get<PaginatedResponse<AuditBlock>>('/blockchain-audit/chain', { params });
    return response.data;
  },

  verify: async (): Promise<{ is_valid: boolean; blocks_verified: number; errors: string[] }> => {
    const response = await apiClient.post<{ is_valid: boolean; blocks_verified: number; errors: string[] }>('/blockchain-audit/verify');
    return response.data;
  },

  stats: async (): Promise<AuditChainStats> => {
    const response = await apiClient.get<AuditChainStats>('/blockchain-audit/stats');
    return response.data;
  },

  block: async (id: string): Promise<AuditBlock> => {
    const response = await apiClient.get<AuditBlock>(`/blockchain-audit/blocks/${id}`);
    return response.data;
  },

  export: async (params: { from_date?: string; to_date?: string }): Promise<Blob> => {
    const response = await apiClient.get('/blockchain-audit/export', { params, responseType: 'blob' });
    return response.data;
  },
};

// --- Scenario Modeling API ---------------------------------------------------

export const scenarioApi = {
  run: async (data: {
    scenario_type: string;
    name?: string;
    parameters: Record<string, any>;
  }): Promise<ScenarioResult> => {
    const response = await apiClient.post<ScenarioResult>('/scenarios/run', data);
    return response.data;
  },

  list: async (): Promise<ScenarioResult[]> => {
    const response = await apiClient.get<ScenarioResult[]>('/scenarios');
    return response.data;
  },

  get: async (id: string): Promise<ScenarioResult> => {
    const response = await apiClient.get<ScenarioResult>(`/scenarios/${id}`);
    return response.data;
  },

  compare: async (ids: string[]): Promise<ScenarioResult[]> => {
    const response = await apiClient.post<ScenarioResult[]>('/scenarios/compare', { scenario_ids: ids });
    return response.data;
  },
};

// --- Phase 9: Data Quality API -----------------------------------------------

export const dataQualityApi = {
  get: async (entityId: number, periodId: number): Promise<import('@/types').DataQualityReport> => {
    const response = await apiClient.get(`/data-quality/entity/${entityId}/period/${periodId}`);
    return response.data;
  },

  run: async (periodId: number): Promise<import('@/types').DataQualityReport[]> => {
    const response = await apiClient.post(`/data-quality/period/${periodId}`);
    return response.data;
  },

  summary: async (periodId: number): Promise<import('@/types').DataQualityReport[]> => {
    const response = await apiClient.get(`/data-quality/period/${periodId}/summary`);
    return response.data;
  },

  /** Certification gate check for a single entity (BRD 2.4) */
  certificationGate: async (entityId: number, periodId: number): Promise<any> => {
    const response = await apiClient.get(`/data-quality/certification-gate/entity/${entityId}/period/${periodId}`);
    return response.data;
  },

  /** Certification gate check for all entities in a period (BRD 2.4) */
  periodCertificationGate: async (periodId: number): Promise<any> => {
    const response = await apiClient.get(`/data-quality/certification-gate/period/${periodId}`);
    return response.data;
  },
};

// --- Phase 9: Predictive Close API -------------------------------------------

export const predictiveCloseApi = {
  get: async (periodId: number): Promise<import('@/types').CloseTimeline> => {
    const response = await apiClient.get(`/predictive-close/${periodId}`);
    return response.data;
  },

  readiness: async (periodId: number): Promise<import('@/types').EntityReadiness[]> => {
    const response = await apiClient.get(`/predictive-close/${periodId}/readiness`);
    return response.data;
  },

  bottlenecks: async (periodId: number): Promise<import('@/types').Bottleneck[]> => {
    const response = await apiClient.get(`/predictive-close/${periodId}/bottlenecks`);
    return response.data;
  },
};

// --- Phase 10: KPI API -------------------------------------------------------

export const kpiApi = {
  dashboard: async (periodId: number): Promise<import('@/types').KPIDashboard> => {
    const response = await apiClient.get(`/kpi/dashboard/${periodId}`);
    return response.data;
  },

  trend: async (kpiId: string): Promise<{ periods: string[]; values: number[] }> => {
    const response = await apiClient.get(`/kpi/${kpiId}/trend`);
    return response.data;
  },
};

// --- Phase 10: Close Status API ----------------------------------------------

export const closeStatusApi = {
  get: async (periodId: number): Promise<import('@/types').PeriodCloseStatus> => {
    const response = await apiClient.get(`/close-status/period/${periodId}`);
    return response.data;
  },

  updateStage: async (periodId: number, entityId: number, stage: string, data: any): Promise<void> => {
    await apiClient.put(`/close-status/period/${periodId}/entity/${entityId}/stage/${stage}`, data);
  },

  /** Close cycle analytics with historical trend (BRD 7.3-7.4) */
  analytics: async (numPeriods?: number): Promise<any> => {
    const response = await apiClient.get('/close-status/analytics', { params: { num_periods: numPeriods || 6 } });
    return response.data;
  },

  /** Close performance metrics for a period */
  metrics: async (periodId: number): Promise<any> => {
    const response = await apiClient.get(`/close-status/period/${periodId}/metrics`);
    return response.data;
  },
};

// --- Phase 10: Rules API -----------------------------------------------------

export const rulesApi = {
  list: async (): Promise<import('@/types').ConsolidationRule[]> => {
    const response = await apiClient.get('/rules');
    return response.data;
  },

  create: async (data: Partial<import('@/types').ConsolidationRule>): Promise<import('@/types').ConsolidationRule> => {
    const response = await apiClient.post('/rules', data);
    return response.data;
  },

  update: async (id: number, data: Partial<import('@/types').ConsolidationRule>): Promise<import('@/types').ConsolidationRule> => {
    const response = await apiClient.put(`/rules/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/rules/${id}`);
  },

  evaluate: async (periodId: number): Promise<{ matched: number; actions_taken: number; journal_entries: number }> => {
    const response = await apiClient.post(`/rules/evaluate/${periodId}`);
    return response.data;
  },
};

// --- Phase 10: Auto Mapper API -----------------------------------------------

export const autoMapperApi = {
  suggest: async (entityId: number): Promise<import('@/types').MappingSuggestion[]> => {
    const response = await apiClient.post(`/auto-mapper/suggest/${entityId}`);
    return response.data;
  },

  confirm: async (data: { entity_account_id: number; group_account_id: number }): Promise<void> => {
    await apiClient.post('/auto-mapper/confirm', data);
  },
};

// --- Phase 10: Budget API ----------------------------------------------------

export const budgetApi = {
  list: async (periodId: number): Promise<import('@/types').BudgetEntry[]> => {
    const response = await apiClient.get(`/budget/period/${periodId}`);
    return response.data;
  },

  create: async (data: Partial<import('@/types').BudgetEntry>): Promise<import('@/types').BudgetEntry> => {
    const response = await apiClient.post('/budget', data);
    return response.data;
  },

  update: async (id: number, data: Partial<import('@/types').BudgetEntry>): Promise<import('@/types').BudgetEntry> => {
    const response = await apiClient.put(`/budget/${id}`, data);
    return response.data;
  },

  variance: async (periodId: number): Promise<import('@/types').BudgetVariance[]> => {
    const response = await apiClient.get(`/budget/variance/${periodId}`);
    return response.data;
  },

  approve: async (id: number): Promise<void> => {
    await apiClient.post(`/budget/${id}/approve`);
  },
};

// --- Phase 10: Dimensions API ------------------------------------------------

export const dimensionsApi = {
  types: async (): Promise<{ type: string; label: string; description: string; value_count: number }[]> => {
    const response = await apiClient.get('/dimensions/types');
    return response.data;
  },

  values: async (type: string): Promise<{ id: number; code: string; name: string }[]> => {
    const response = await apiClient.get(`/dimensions/${type}/values`);
    return response.data;
  },

  createValue: async (type: string, data: any): Promise<{ id: number; code: string; name: string }> => {
    const response = await apiClient.post(`/dimensions/${type}/values`, data);
    return response.data;
  },

  getConfig: async (): Promise<Record<string, { label: string; fccs_column: string; description?: string }>> => {
    const response = await apiClient.get('/dimensions/config');
    return response.data;
  },

  updateConfig: async (config: Record<string, { label: string; fccs_column: string }>): Promise<Record<string, { label: string; fccs_column: string }>> => {
    const response = await apiClient.put('/dimensions/config', config);
    return response.data;
  },

  validateMetadata: async (dimension?: string): Promise<any> => {
    // Sync endpoint — returns report immediately for Dimension Manager UI
    const response = await apiClient.post('/dimensions/validate-metadata/sync', null, { params: { dimension: dimension || 'all' } });
    return response.data;
  },
  validateMetadataAsync: async (dimension?: string): Promise<any> => {
    // Async endpoint — dispatches Job, returns job_id (used from Jobs page)
    const response = await apiClient.post('/dimensions/validate-metadata', null, { params: { dimension: dimension || 'all' } });
    return response.data;
  },

  getValidationHistory: async (limit?: number): Promise<any[]> => {
    const response = await apiClient.get('/dimensions/validate-metadata/history', { params: { limit: limit || 10 } });
    return response.data;
  },
  getValidationRules: async (): Promise<any[]> => {
    const response = await apiClient.get('/dimensions/validate-metadata/rules');
    return response.data;
  },
  updateValidationRules: async (disabledRules: string[]): Promise<any> => {
    const response = await apiClient.put('/dimensions/validate-metadata/rules', { disabled_rules: disabledRules });
    return response.data;
  },
};

// --- Data forms (EPM / FCCS form designer persistence) ------------------------

export interface DataFormListItem {
  id: number;
  code: string;
  name: string;
  description: string | null;
  form_type: string;
  folder: string | null;
  /** FK to a persistent form_folders row (null = virtual "Library" root). */
  folder_id: number | null;
  cube_name: string | null;
  /** When true, this is a predefined/system form: only admins can change its definition or delete it. */
  predefined?: boolean;
  /** Display property "Hide Form": suppressed from the data-entry list, still shown in the form list. */
  hidden?: boolean;
  created_at: string;
  updated_at: string;
}

export interface DataFormDetail extends DataFormListItem {
  tenant_id: number;
  is_active: boolean;
  is_seeded: boolean;
  predefined?: boolean;
  options: Record<string, unknown>;
}

export interface DataFormSaveMeta {
  validation_report?: Record<string, unknown> | null;
  post_save_rule_previews?: Record<string, unknown>[];
  post_save_rule_results?: Record<string, unknown>[];
  entry_status?: string | null;
  tb_lock_status?: string | null;
  /** Number of consol_fact rows inserted or updated on save. */
  cube_cells_written?: number;
  /** consol_fact rows removed (client sent null for the cell). */
  cube_cells_deleted?: number;
  /** Cells that could not be mapped to a valid cube intersection. */
  cube_cells_skipped_failed?: number;
  /**
   * Cells the client asked to clear where the cube held nothing to delete — the value
   * shown was derived (engine-run opening balance / carry-forward), not stored. Not a
   * failure: the cube already matches the request.
   */
  cube_cells_noop_clears?: number;
  /** Storage keys behind `cube_cells_noop_clears` — the client reverts these cells. */
  noop_clear_keys?: string[];
  /**
   * Option B targeted post-save refresh: server-recomputed values for the derived
   * cells (parent/rollup totals, backend-calculated cells) whose value CHANGED as a
   * result of this save. Keyed by storage cell key; `null` = clear the cell. When
   * present, the client applies these and skips the full-grid reload. Absent/undefined
   * → the client falls back to a full reload. Never includes the edited leaves.
   */
  updated_cells?: Record<string, number | null> | null;
  /**
   * Large-grid Save: server skipped the O(grid) derived re-read. Keep local parent
   * rollup and do not start a full-grid cube hydrate after Save.
   */
  prefer_client_rollup?: boolean;
  /** Optimistic lock version after this save — use as expected_version on next save. */
  version?: number | null;
}

export interface DataFormCellPatchResult {
  form: DataFormDetail;
  meta?: DataFormSaveMeta | null;
}

export interface FormCellCommentAttachment {
  id: string;
  filename: string;
  size: number;
  content_type?: string | null;
}

export interface FormCellCommentRecord {
  text: string;
  author_id?: number | null;
  author_name: string;
  posted_at: string;
  attachments: FormCellCommentAttachment[];
}

export interface FormCellCommentResponse {
  cell_key: string;
  comment: FormCellCommentRecord | null;
}

export interface FormLineItemDetailsResponse {
  cell_key: string;
  tree: Record<string, unknown> | null;
}

export interface FormDataEntryState {
  cell_data: Record<string, unknown>;
  cell_comments?: Record<string, FormCellCommentRecord>;
  line_item_details?: Record<string, Record<string, unknown>>;
  entry_status: string;
  submitted_at: string | null;
  submitted_by_user_id: number | null;
  tb_lock_status: string | null;
  tb_lock_blocks_edit: boolean;
  form_submission_blocks_edit: boolean;
  period_certification_blocks_edit: boolean;
  lock_messages: string[];
  last_validation: Record<string, unknown> | null;
  version: number;
}

export interface ExcelRowError {
  row_number: number;
  message: string;
  cell_key: string | null;
}

export interface FormDataExcelImportResult {
  row_errors: ExcelRowError[];
  validation_report: Record<string, unknown> | null;
  applied: boolean;
  cells_merged_count: number;
}

export interface FormDataPublishTrialBalanceResponse {
  status: string;
  message: string;
  trial_balances_path: string;
  erp_import_hint?: string | null;
}

export interface CubeCellDrillContribution {
  row_id: number;
  entity_id: number | null;
  icp_id: number | null;
  account_id: number | null;
  movement_code: string | null;
  consolidation_code: string | null;
  currency_code: string | null;
  custom1_id: number | null;
  custom2_id: number | null;
  custom3_id: number | null;
  custom4_id: number | null;
  amount: number;
  pct: number;
  updated_at: string | null;
}

export interface CubeCellDrillResponse {
  storage_key: string;
  row_code: string;
  col_code: string;
  context_codes: Record<string, string>;
  resolved: Record<string, number | string | null> | null;
  total: number | null;
  rollup_dims: string[];
  contributions: CubeCellDrillContribution[];
  resolve_error: string | null;
  value_history?: CubeCellValueHistoryEntry[];
}

export interface CubeCellValueHistoryEntry {
  changed_at: string;
  old: number | string | null;
  new: number | string | null;
  kind: string;
  user: string | null;
}

export interface FormCellLineageResponse {
  cell_key: string;
  entity_id: number;
  period_id: number;
  scenario: string;
  hints: Record<string, unknown>[];
  note: string | null;
}

// --- Form folders (persistent, nested folder tree for the Forms library) ------

export interface FormFolder {
  id: number;
  name: string;
  parent_id: number | null;
  depth: number;
  path: string;
  sort_order: number;
  form_count: number;
  /** Shown in the Data page's folder rows. `updated_at` also moves when a form is filed in/out. */
  created_at?: string | null;
  updated_at?: string | null;
}

export type FolderPrincipalType = 'role' | 'user';
export type FolderAccessLevel = 'view' | 'edit' | 'none';

export interface FormFolderPermission {
  id?: number;
  principal_type: FolderPrincipalType;
  principal: string;
  access_level: FolderAccessLevel;
}

export const formFoldersApi = {
  list: async (): Promise<FormFolder[]> => {
    const response = await apiClient.get<FormFolder[]>('/form-folders');
    return response.data;
  },
  create: async (payload: { name: string; parent_id?: number | null }): Promise<FormFolder> => {
    const response = await apiClient.post<FormFolder>('/form-folders', payload);
    return response.data;
  },
  rename: async (id: number, name: string): Promise<FormFolder> => {
    const response = await apiClient.patch<FormFolder>(`/form-folders/${id}`, { name });
    return response.data;
  },
  move: async (id: number, parent_id: number | null): Promise<FormFolder> => {
    const response = await apiClient.post<FormFolder>(`/form-folders/${id}/move`, { parent_id });
    return response.data;
  },
  remove: async (id: number): Promise<void> => {
    await apiClient.delete(`/form-folders/${id}`);
  },
  getPermissions: async (id: number): Promise<FormFolderPermission[]> => {
    const response = await apiClient.get<FormFolderPermission[]>(`/form-folders/${id}/permissions`);
    return response.data;
  },
  setPermissions: async (id: number, permissions: FormFolderPermission[]): Promise<FormFolderPermission[]> => {
    const response = await apiClient.put<FormFolderPermission[]>(`/form-folders/${id}/permissions`, { permissions });
    return response.data;
  },
};

/**
 * Windowed cube-cell-snapshot (P3 / T3.1). Optional: omit it and the server answers
 * over the whole axis exactly as before. Send the FULL logical axis order plus the
 * slice to paint — the server suppresses across the whole axis first, then cuts the
 * window out of the survivors, so `row_start`/`row_count` index the POST-suppression
 * axis, which is the one the grid actually renders.
 */
export interface CubeCellWindowRequest {
  row_codes: string[];
  col_codes: string[];
  row_start?: number;
  col_start?: number;
  /** Omit for "to the end of the axis". */
  row_count?: number;
  col_count?: number;
}

export const dataFormsApi = {
  list: async (params?: { q?: string; form_type?: string; include_inactive?: boolean; include_hidden?: boolean; folder_id?: number }): Promise<DataFormListItem[]> => {
    const response = await apiClient.get<DataFormListItem[]>('/data-forms', { params });
    return response.data;
  },
  /** Backend evaluation of metadata-based conditional-format rules (UDA / Attribute / Account Type
   * / Generation / Has Children / …). Returns, per rule id, the cell keys it paints. */
  formatRuleMatches: async (
    id: number,
    payload: {
      rows: Array<{ code: string; dims: Record<string, string> }>;
      cols: Array<{ code: string; dims: Record<string, string> }>;
      pov: Record<string, string>;
      rules: unknown[];
    },
  ): Promise<{ matches: Array<{ ruleId: string; cellKeys: string[] }> }> => {
    const response = await apiClient.post<{ matches: Array<{ ruleId: string; cellKeys: string[] }> }>(
      `/data-forms/${id}/format-rule-matches`,
      payload,
    );
    return response.data;
  },
  get: async (id: number): Promise<DataFormDetail> => {
    const response = await apiClient.get<DataFormDetail>(`/data-forms/${id}`);
    return response.data;
  },
  /** Lightweight pre-check: does this POV (form × entity × period × scenario)
   * have ANY data in consol_fact? When false, the frontend skips the entire
   * member-pool load + cube hydrate + grid render pipeline. */
  getDataProbe: async (
    id: number,
    params: { entity_id: number; period_id: number; scenario?: string },
  ): Promise<{ form_name: string; form_code: string; has_data: boolean }> => {
    const response = await apiClient.get<{ form_name: string; form_code: string; has_data: boolean }>(
      `/data-forms/${id}/data-probe`,
      { params: { entity_id: params.entity_id, period_id: params.period_id, scenario: params.scenario ?? 'Actual' } },
    );
    return response.data;
  },
  create: async (payload: {
    code: string;
    name: string;
    description?: string | null;
    form_type?: string;
    folder?: string | null;
    folder_id?: number | null;
    options?: Record<string, unknown>;
    predefined?: boolean;
  }): Promise<DataFormDetail> => {
    const response = await apiClient.post<DataFormDetail>('/data-forms', payload);
    return response.data;
  },
  update: async (
    id: number,
    payload: {
      name?: string;
      description?: string | null;
      form_type?: string;
      folder?: string | null;
      folder_id?: number | null;
      is_active?: boolean;
      options?: Record<string, unknown>;
      predefined?: boolean;
    },
  ): Promise<DataFormDetail> => {
    const response = await apiClient.put<DataFormDetail>(`/data-forms/${id}`, payload);
    return response.data;
  },
  postCubeCellSnapshot: async (
    id: number,
    body:
      | {
          storage_keys: string[];
          prefer_source?: boolean;
          bypass_replica?: boolean;
          hydrate_mode?: 'full' | 'leaves_only';
          suppress_missing_rows?: boolean;
          suppress_missing_cols?: boolean;
          suppress_missing_blocks?: boolean;
          suppress_zero?: boolean;
          window?: CubeCellWindowRequest;
        }
      | {
          groups: { prefix: string; bases: string[] }[];
          storage_keys?: string[];
          prefer_source?: boolean;
          bypass_replica?: boolean;
          hydrate_mode?: 'full' | 'leaves_only';
          suppress_missing_rows?: boolean;
          suppress_missing_cols?: boolean;
          suppress_missing_blocks?: boolean;
          suppress_zero?: boolean;
          window?: CubeCellWindowRequest;
        },
    signal?: AbortSignal,
  ): Promise<{
    cell_data: Record<string, number>;
    text_cell_data?: Record<string, string>;
    client_summable_keys?: string[];
    suppressed_row_codes?: string[];
    suppressed_col_codes?: string[];
    // Opening-balance cells with no manual fact behind them (engine-run / carry-forward).
    // The grid locks these: clearing one can only ever be a cube no-op.
    derived_opening_keys?: string[];
    // Present only when the request carried a `window` (P3 / T3.1). `visible_*` are the
    // post-suppression codes for the requested slice, in axis order; `total_*` are the
    // PRE-suppression logical counts, for sizing the virtual scroller's track.
    visible_rows?: string[];
    visible_cols?: string[];
    total_rows?: number;
    total_cols?: number;
    // Distinguishes "the window is genuinely empty" from "this server ignored my window".
    windowed?: boolean;
    // POV-cache epoch read BEFORE these values. A snapshot built from them must quote the
    // OLDEST epoch it contains when it is PUT; the server refuses it once the epoch moved.
    cache_epoch?: number | null;
    perf?: {
      cells: number;
      rollups: number;
      axis_only: number;
      off_axis: number;
      cumulative: number;
      ch_used: boolean;
      resolve_s: number;
      rollups_s: number;
      total_s: number;
      output_cells?: number;
      client_summable?: number;
      resolved_cells?: number;
    };
  }> => {
    const response = await apiClient.post<{
      cell_data: Record<string, number>;
      text_cell_data?: Record<string, string>;
      client_summable_keys?: string[];
      suppressed_row_codes?: string[];
      suppressed_col_codes?: string[];
      // Opening-balance cells with no manual fact behind them (engine-run / carry-forward).
      // The grid locks these: clearing one can only ever be a cube no-op.
      derived_opening_keys?: string[];
      visible_rows?: string[];
      visible_cols?: string[];
      total_rows?: number;
      total_cols?: number;
      windowed?: boolean;
      cache_epoch?: number | null;
      perf?: {
        cells: number;
        rollups: number;
        axis_only: number;
        off_axis: number;
        cumulative: number;
        ch_used: boolean;
        resolve_s: number;
        rollups_s: number;
        total_s: number;
        output_cells?: number;
        client_summable?: number;
        resolved_cells?: number;
      };
    }>(
      `/data-forms/${id}/cube-cell-snapshot`,
      body,
      { signal, timeout: 60000 },
    );
    return response.data;
  },
  getCubePovCache: async (
    id: number,
    povDigest: string,
  ): Promise<{
    hit: boolean;
    cell_data: Record<string, number>;
    text_cell_data: Record<string, string>;
    derived_opening_keys?: string[];
    /** POV-cache epoch read before the lookup (see postCubeCellSnapshot.cache_epoch). */
    epoch?: number | null;
  }> => {
    const response = await apiClient.post(`/data-forms/${id}/cube-pov-cache/lookup`, {
      pov_digest: povDigest,
    });
    return response.data;
  },
  putCubePovCache: async (
    id: number,
    body: {
      pov_digest: string;
      cell_data: Record<string, number>;
      text_cell_data?: Record<string, string>;
      derived_opening_keys?: string[];
      /** Oldest epoch any value in this snapshot was read under; null = unknown (refused). */
      epoch: number | null;
    },
  ): Promise<void> => {
    await apiClient.put(`/data-forms/${id}/cube-pov-cache`, body);
  },
  warmCubePovCache: async (
    id: number,
    body: { pov_digest: string; storage_keys: string[] },
  ): Promise<{ queued: boolean; reason?: string | null }> => {
    const response = await apiClient.post<{ queued: boolean; reason?: string | null }>(
      `/data-forms/${id}/cube-pov-cache/warm`,
      body,
      { timeout: 15000 },
    );
    return response.data;
  },
  patchCells: async (
    id: number,
    payload: {
      cell_data: Record<string, number | null>;
      // Free-text edits for Text-data-type account cells (rowCode|colCode → string).
      // null/empty clears the cell. Separate from numeric cell_data.
      text_cell_data?: Record<string, string | null>;
      intersection?: {
        base_member_ids: Record<string, number>;
        cell_member_ids: Record<string, Record<string, number>>;
        member_codes?: Record<string, string>;
      };
      context?: { entity_id: number; period_id: number; scenario?: string; expected_version?: number };
      grid_cell_keys?: string[];
      // Displayed grid values the client already holds; lets the server skip an
      // O(grid) cube re-read for P5 validation on save. Edits in cell_data override.
      grid_cell_values?: Record<string, number | null> | null;
      calculated_cell_keys?: string[];
      run_post_save_rule_previews?: boolean;
      run_post_save_rules_execute?: boolean;
    },
    signal?: AbortSignal,
  ): Promise<DataFormCellPatchResult> => {
    // Raise timeout to 120s — large grids with many changed cells and VI checks
    // can exceed the 30s global default on staging/prod under load.
    const response = await apiClient.patch<DataFormCellPatchResult | DataFormDetail>(
      `/data-forms/${id}/cells`,
      payload,
      { timeout: 120000, signal },
    );
    const d = response.data as DataFormCellPatchResult | DataFormDetail;
    if (d && typeof d === 'object' && 'form' in d) return d as DataFormCellPatchResult;
    return { form: d as DataFormDetail, meta: null };
  },
  getDataEntryState: async (
    id: number,
    params: { entity_id: number; period_id: number; scenario?: string },
  ): Promise<FormDataEntryState> => {
    const response = await apiClient.get<FormDataEntryState>(`/data-forms/${id}/data-entry-state`, {
      params: {
        entity_id: params.entity_id,
        period_id: params.period_id,
        scenario: params.scenario ?? 'Actual',
      },
    });
    return response.data;
  },
  getCellComment: async (
    id: number,
    cellKey: string,
    params: { entity_id: number; period_id: number; scenario?: string },
  ): Promise<FormCellCommentResponse> => {
    const response = await apiClient.get<FormCellCommentResponse>(
      `/data-forms/${id}/cell-comments/${encodeURIComponent(cellKey)}`,
      {
        params: {
          entity_id: params.entity_id,
          period_id: params.period_id,
          scenario: params.scenario ?? 'Actual',
        },
      },
    );
    return response.data;
  },
  getLineItemDetails: async (
    id: number,
    cellKey: string,
    params: { entity_id: number; period_id: number; scenario?: string },
  ): Promise<FormLineItemDetailsResponse> => {
    const response = await apiClient.get<FormLineItemDetailsResponse>(
      `/data-forms/${id}/line-item-details/${encodeURIComponent(cellKey)}`,
      {
        params: {
          entity_id: params.entity_id,
          period_id: params.period_id,
          scenario: params.scenario ?? 'Actual',
        },
      },
    );
    return response.data;
  },
  postLineItemDetails: async (
    id: number,
    payload: {
      cell_key: string;
      entity_id: number;
      period_id: number;
      scenario?: string;
      tree: Record<string, unknown> | null;
    },
  ): Promise<FormLineItemDetailsResponse> => {
    const response = await apiClient.post<FormLineItemDetailsResponse>(
      `/data-forms/${id}/line-item-details`,
      {
        cell_key: payload.cell_key,
        entity_id: payload.entity_id,
        period_id: payload.period_id,
        scenario: payload.scenario ?? 'Actual',
        tree: payload.tree,
      },
    );
    return response.data;
  },
  postCubeCellDrill: async (
    id: number,
    body: { storage_key: string },
  ): Promise<CubeCellDrillResponse> => {
    const res = await apiClient.post<CubeCellDrillResponse>(
      `/data-forms/${id}/cube-cell-drill`,
      body,
    );
    return res.data;
  },
  postCellComment: async (
    id: number,
    payload: {
      cell_key: string;
      entity_id: number;
      period_id: number;
      scenario?: string;
      text: string;
      files?: File[];
    },
    onProgress?: (loaded: number, total: number) => void,
  ): Promise<FormCellCommentResponse> => {
    const fd = new FormData();
    fd.append('cell_key', payload.cell_key);
    fd.append('entity_id', String(payload.entity_id));
    fd.append('period_id', String(payload.period_id));
    fd.append('scenario', payload.scenario ?? 'Actual');
    fd.append('text', payload.text);
    for (const f of payload.files ?? []) {
      fd.append('files', f);
    }
    const response = await apiClient.post<FormCellCommentResponse>(`/data-forms/${id}/cell-comments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(e.loaded, e.total);
      },
    });
    return response.data;
  },
  downloadCellCommentFile: async (
    id: number,
    fileId: string,
    params: { entity_id: number; period_id: number; scenario?: string },
  ): Promise<Blob> => {
    const response = await apiClient.get(`/data-forms/${id}/cell-comment-files/${encodeURIComponent(fileId)}`, {
      params: {
        entity_id: params.entity_id,
        period_id: params.period_id,
        scenario: params.scenario ?? 'Actual',
      },
      responseType: 'blob',
    });
    return response.data as Blob;
  },
  downloadExcelTemplate: async (
    id: number,
    body: { grid_keys: string[]; entity_id?: number; period_id?: number; scenario?: string },
  ): Promise<Blob> => {
    const response = await apiClient.post(`/data-forms/${id}/excel-template`, body, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },
  importExcelCells: async (id: number, data: FormData): Promise<FormDataExcelImportResult> => {
    const response = await apiClient.post<FormDataExcelImportResult>(`/data-forms/${id}/excel-import`, data);
    return response.data;
  },
  publishToTrialBalanceStub: async (id: number): Promise<FormDataPublishTrialBalanceResponse> => {
    const response = await apiClient.post<FormDataPublishTrialBalanceResponse>(
      `/data-forms/${id}/publish-to-trial-balance`,
    );
    return response.data;
  },
  submitEntry: async (
    id: number,
    payload: {
      context: { entity_id: number; period_id: number; scenario?: string };
      intersection?: {
        base_member_ids: Record<string, number>;
        cell_member_ids: Record<string, Record<string, number>>;
        member_codes?: Record<string, string>;
      };
      grid_cell_keys?: string[];
      run_post_save_rule_previews?: boolean;
      run_post_save_rules_execute?: boolean;
    },
  ): Promise<DataFormCellPatchResult> => {
    const response = await apiClient.post<DataFormCellPatchResult>(`/data-forms/${id}/submit-entry`, payload);
    return response.data;
  },
  reopenEntry: async (
    id: number,
    payload: { context: { entity_id: number; period_id: number; scenario?: string } },
  ): Promise<DataFormCellPatchResult> => {
    const response = await apiClient.post<DataFormCellPatchResult>(`/data-forms/${id}/reopen-entry`, payload);
    return response.data;
  },
  cellLineage: async (
    id: number,
    params: { cell_key: string; entity_id: number; period_id: number; scenario?: string },
  ): Promise<FormCellLineageResponse> => {
    const response = await apiClient.get<FormCellLineageResponse>(`/data-forms/${id}/cell-lineage`, {
      params: {
        cell_key: params.cell_key,
        entity_id: params.entity_id,
        period_id: params.period_id,
        scenario: params.scenario ?? 'Actual',
      },
    });
    return response.data;
  },
  remove: async (id: number): Promise<void> => {
    await apiClient.delete(`/data-forms/${id}`);
  },
};

// --- Phase 10: Permissions API -----------------------------------------------

export const permissionsApi = {
  getUserPermissions: async (userId: number): Promise<any> => {
    const response = await apiClient.get(`/permissions/user/${userId}`);
    return response.data;
  },

  grant: async (data: any): Promise<void> => {
    await apiClient.post('/permissions', data);
  },

  /** SoD conflict report (BRD NFRs) */
  sodReport: async (): Promise<any> => {
    const response = await apiClient.get('/permissions/sod-report');
    return response.data;
  },
};

// --- Phase 11: i18n API ------------------------------------------------------

export const i18nApi = {
  translations: async (locale: string): Promise<Record<string, string>> => {
    const response = await apiClient.get(`/i18n/translations/${locale}`);
    return response.data;
  },

  locales: async (): Promise<{ code: string; name: string; direction: string }[]> => {
    const response = await apiClient.get('/i18n/locales');
    return response.data;
  },

  setPreference: async (locale: string): Promise<void> => {
    await apiClient.put('/i18n/preference', { locale });
  },
};

// --- Phase 11: Search API ----------------------------------------------------

export const searchApi = {
  search: async (query: string, type?: string): Promise<import('@/types').SearchResult[]> => {
    const response = await apiClient.get('/search', { params: { q: query, type } });
    return response.data;
  },
};

// --- Phase 11: CDC API -------------------------------------------------------

export const cdcApi = {
  changes: async (params?: any): Promise<import('@/types').PaginatedResponse<import('@/types').CDCRecord>> => {
    const response = await apiClient.get('/cdc/changes', { params });
    return response.data;
  },

  stats: async (): Promise<{ total_changes: number; pending_sync: number; last_synced_at: string | null }> => {
    const response = await apiClient.get('/cdc/stats');
    return response.data;
  },
};

// --- Phase 11: Recurring Adjustments API -------------------------------------

export const recurringAdjustmentsApi = {
  templates: async (): Promise<import('@/types').RecurringTemplate[]> => {
    const response = await apiClient.get('/recurring-adjustments/templates');
    return response.data;
  },

  createTemplate: async (data: Partial<import('@/types').RecurringTemplate>): Promise<import('@/types').RecurringTemplate> => {
    const response = await apiClient.post('/recurring-adjustments/templates', data);
    return response.data;
  },

  suggestions: async (periodId: number): Promise<import('@/types').RecurringTemplate[]> => {
    const response = await apiClient.get(`/recurring-adjustments/suggestions/${periodId}`);
    return response.data;
  },
};

// --- Phase 12: Webhooks API --------------------------------------------------

export const webhookApi = {
  list: async (): Promise<import('@/types').WebhookSubscription[]> => {
    const response = await apiClient.get('/webhooks');
    return response.data;
  },

  create: async (data: any): Promise<import('@/types').WebhookSubscription> => {
    const response = await apiClient.post('/webhooks', data);
    return response.data;
  },

  update: async (id: number, data: any): Promise<import('@/types').WebhookSubscription> => {
    const response = await apiClient.put(`/webhooks/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/webhooks/${id}`);
  },

  test: async (id: number): Promise<{ success: boolean; status_code: number; duration_ms: number }> => {
    const response = await apiClient.post(`/webhooks/${id}/test`);
    return response.data;
  },

  deliveries: async (id: number): Promise<import('@/types').WebhookDelivery[]> => {
    const response = await apiClient.get(`/webhooks/${id}/deliveries`);
    return response.data;
  },
};

// --- Phase 12: API Keys API --------------------------------------------------

export const apiKeyApi = {
  list: async (): Promise<import('@/types').APIKeyInfo[]> => {
    const response = await apiClient.get('/api-keys');
    return response.data;
  },

  create: async (data: any): Promise<{ key: string; info: import('@/types').APIKeyInfo }> => {
    const response = await apiClient.post('/api-keys', data);
    return response.data;
  },

  revoke: async (id: number): Promise<void> => {
    await apiClient.delete(`/api-keys/${id}`);
  },

  rotate: async (id: number): Promise<{ key: string; info: import('@/types').APIKeyInfo }> => {
    const response = await apiClient.post(`/api-keys/${id}/rotate`);
    return response.data;
  },
};

// --- Phase 12: Security API --------------------------------------------------

export const securityApi = {
  audit: async (): Promise<import('@/types').SecurityAuditReport> => {
    const response = await apiClient.get('/security/audit');
    return response.data;
  },

  compliance: async (): Promise<any> => {
    const response = await apiClient.get('/security/compliance');
    return response.data;
  },

  recommendations: async (): Promise<string[]> => {
    const response = await apiClient.get('/security/recommendations');
    return response.data;
  },
};

// --- Phase 12: Archives API --------------------------------------------------

export const archiveApi = {
  list: async (): Promise<import('@/types').PeriodArchive[]> => {
    const response = await apiClient.get('/archives');
    return response.data;
  },

  archive: async (periodId: number): Promise<import('@/types').PeriodArchive> => {
    const response = await apiClient.post(`/archives/period/${periodId}`);
    return response.data;
  },

  restore: async (periodId: number): Promise<void> => {
    await apiClient.post(`/archives/${periodId}/restore`);
  },

  storage: async (): Promise<import('@/types').StorageUsage> => {
    const response = await apiClient.get('/archives/storage');
    return response.data;
  },

  retentionPolicy: async (): Promise<{ min_retention_years: number; auto_archive_months: number }> => {
    const response = await apiClient.get('/archives/retention-policy');
    return response.data;
  },
};

// --- Phase 13: Policy Conflicts API ------------------------------------------

export const policyApi = {
  conflicts: async (): Promise<import('@/types').PolicyConflict[]> => {
    const response = await apiClient.get('/policies/conflicts');
    return response.data;
  },

  conflictSummary: async (): Promise<any> => {
    const response = await apiClient.get('/policies/conflicts/summary');
    return response.data;
  },

  groupPolicies: async (): Promise<any> => {
    const response = await apiClient.get('/policies/group');
    return response.data;
  },

  updatePolicy: async (key: string, data: any): Promise<void> => {
    await apiClient.put(`/policies/group/${key}`, data);
  },
};

// --- Phase 13: MPM API -------------------------------------------------------

export const mpmApi = {
  dashboard: async (periodId: number): Promise<import('@/types').MPMResult[]> => {
    const response = await apiClient.get(`/mpm/dashboard/${periodId}`);
    return response.data;
  },

  reconciliation: async (mpmId: string, periodId: number): Promise<any> => {
    const response = await apiClient.get(`/mpm/${mpmId}/reconciliation/${periodId}`);
    return response.data;
  },

  definitions: async (): Promise<any> => {
    const response = await apiClient.get('/mpm/definitions');
    return response.data;
  },
};

// --- Phase 13: Audit Package API ---------------------------------------------

export const auditPackageApi = {
  download: async (runId: number): Promise<Blob> => {
    const response = await apiClient.get(`/exports/audit-package/${runId}`, { responseType: 'blob' });
    return response.data;
  },

  contents: async (runId: number): Promise<any> => {
    const response = await apiClient.get(`/exports/audit-package/${runId}/contents`);
    return response.data;
  },
};

// --- Phase 13: IFRS Rule Library API -----------------------------------------

export const ruleLibraryApi = {
  getAll: async (): Promise<any[]> => {
    const response = await apiClient.get('/rules/library');
    return response.data;
  },

  seed: async (): Promise<void> => {
    await apiClient.post('/rules/library/seed');
  },

  categories: async (): Promise<string[]> => {
    const response = await apiClient.get('/rules/library/categories');
    return response.data;
  },
};

// --- Phase 14: Onboarding API ------------------------------------------------

export const onboardingApi = {
  progress: async (): Promise<import('@/types').OnboardingProgress> => {
    const response = await apiClient.get('/onboarding/progress');
    return response.data;
  },

  completeStep: async (step: string): Promise<void> => {
    await apiClient.post(`/onboarding/steps/${step}/complete`);
  },

  skipStep: async (step: string): Promise<void> => {
    await apiClient.post(`/onboarding/steps/${step}/skip`);
  },

  guidance: async (step: string): Promise<{ title: string; content: string; tips: string[] }> => {
    const response = await apiClient.get(`/onboarding/steps/${step}/guidance`);
    return response.data;
  },

  seedSampleData: async (): Promise<{ message: string }> => {
    const response = await apiClient.post('/onboarding/seed-sample-data');
    return response.data;
  },
};

// --- Phase 14: Import Wizard API ---------------------------------------------

export const importApi = {
  start: async (file: File, importType: string, data?: Record<string, string>): Promise<import('@/types').ImportSession> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('import_type', importType);
    if (data) Object.entries(data).forEach(([k, v]) => formData.append(k, v));
    const response = await apiClient.post('/import/start', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getSession: async (id: number): Promise<import('@/types').ImportSession> => {
    const response = await apiClient.get(`/import/${id}`);
    return response.data;
  },

  updateMapping: async (id: number, mappings: Record<string, string>): Promise<void> => {
    await apiClient.put(`/import/${id}/mapping`, { mappings });
  },

  validate: async (id: number): Promise<import('@/types').ImportSession> => {
    const response = await apiClient.post(`/import/${id}/validate`);
    return response.data;
  },

  preview: async (id: number): Promise<{ rows: any[]; summary: { create: number; update: number; skip: number } }> => {
    const response = await apiClient.get(`/import/${id}/preview`);
    return response.data;
  },

  execute: async (id: number): Promise<import('@/types').ImportSession> => {
    const response = await apiClient.post(`/import/${id}/execute`);
    return response.data;
  },

  sessions: async (): Promise<import('@/types').ImportSession[]> => {
    const response = await apiClient.get('/import/sessions');
    return response.data;
  },
};

// --- Phase 14: Dashboard Preferences API -------------------------------------

export const dashboardPrefApi = {
  getLayout: async (id: string): Promise<any> => {
    const response = await apiClient.get(`/dashboard/layout/${id}`);
    return response.data;
  },

  saveLayout: async (id: string, layout: any): Promise<void> => {
    await apiClient.put(`/dashboard/layout/${id}`, { layout });
  },

  getTheme: async (): Promise<{ theme: string }> => {
    const response = await apiClient.get('/dashboard/theme');
    return response.data;
  },

  setTheme: async (theme: string): Promise<void> => {
    await apiClient.put('/dashboard/theme', { theme });
  },
};

// --- Phase 14: Performance API -----------------------------------------------

export const performanceApi = {
  apiMetrics: async (): Promise<any> => {
    const response = await apiClient.get('/performance/api-metrics');
    return response.data;
  },

  consolidationMetrics: async (): Promise<any> => {
    const response = await apiClient.get('/performance/consolidation-metrics');
    return response.data;
  },

  systemHealth: async (): Promise<import('@/types').SystemHealth> => {
    const response = await apiClient.get('/performance/system-health');
    return response.data;
  },
};

// --- AI Config API -----------------------------------------------------------

export const aiConfigApi = {
  get: async (): Promise<{
    provider?: string;
    base_url?: string;
    api_key_masked?: string;
    model?: string;
    temperature?: number;
    max_tokens?: number;
    is_configured?: boolean;
  }> => {
    const response = await apiClient.get('/ai-config');
    return response.data;
  },

  save: async (config: {
    provider: string;
    base_url: string;
    api_key: string;
    model: string;
    temperature?: number;
    max_tokens?: number;
  }): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.put('/ai-config', config);
    return response.data;
  },

  test: async (config: {
    provider: string;
    base_url: string;
    api_key: string;
    model: string;
  }): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/ai-config/test', config);
    return response.data;
  },
};

// --- AI Suggestions API ------------------------------------------------------

export const aiSuggestionsApi = {
  getUploadSuggestion: async (data: {
    upload_type: string;
    error_message: string;
    warnings?: string[];
    file_name?: string;
  }): Promise<{ suggestion: string; model_used: string }> => {
    const response = await apiClient.post('/ai-suggestions/upload-error', data);
    return response.data;
  },
};

// --- Journals API -----------------------------------------------------------

export const journalsApi = {
  list: async (params?: {
    period_id?: string;
    entity_id?: string;
    entry_type?: string;
    status?: string;
    source_skill?: string;
    is_manual?: boolean;
    search?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<any>> => {
    const response = await apiClient.get<PaginatedResponse<any>>('/journals', { params });
    return response.data;
  },

  get: async (id: string): Promise<any> => {
    const response = await apiClient.get(`/journals/${id}`);
    return response.data;
  },

  create: async (data: {
    period_id: number;
    entry_type?: string;
    description: string;
    reference?: string;
    lines: Array<{
      entity_id: number;
      account_id: number;
      debit: number;
      credit: number;
      currency?: string;
      description?: string;
    }>;
  }): Promise<any> => {
    const response = await apiClient.post('/journals', data);
    return response.data;
  },

  update: async (id: string, data: {
    description?: string;
    reference?: string;
    entry_type?: string;
    lines?: Array<{
      entity_id: number;
      account_id: number;
      debit: number;
      credit: number;
      currency?: string;
      description?: string;
    }>;
  }): Promise<any> => {
    const response = await apiClient.put(`/journals/${id}`, data);
    return response.data;
  },

  post: async (id: string): Promise<any> => {
    const response = await apiClient.post(`/journals/${id}/post`);
    return response.data;
  },

  reverse: async (id: string): Promise<any> => {
    const response = await apiClient.post(`/journals/${id}/reverse`);
    return response.data;
  },

  delete: async (id: string): Promise<any> => {
    const response = await apiClient.delete(`/journals/${id}`);
    return response.data;
  },

  bulkDelete: async (params: {
    source_skill?: string;
    period_id?: string;
    entry_type?: string;
    status?: string;
  }): Promise<any> => {
    const response = await apiClient.delete('/journals', { params });
    return response.data;
  },

  bulkUpload: async (file: File, periodId?: string): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    const params: Record<string, string> = {};
    if (periodId) params.period_id = periodId;
    const response = await apiClient.post('/journals/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params,
    });
    return response.data;
  },

  exportExcel: async (params?: {
    period_id?: string;
    entry_type?: string;
    status?: string;
  }): Promise<void> => {
    const response = await apiClient.get('/journals/export/excel', {
      params,
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `journal_entries_${new Date().toISOString().slice(0, 10)}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

// --- Export Jobs API ---------------------------------------------------------

/** Where a dimension member is used (US-DIM-REF-001) — the delete blocker trace. */
export interface MemberReference {
  source: string;
  /** Business title, e.g. "Financial data" (never a table name). */
  title: string;
  count: number;
  /** "114 amounts" — pre-pluralised for display. */
  count_label: string;
  /** Screen the reference lives on, e.g. "Ownership Structure". */
  where: string;
  /** Route to that screen, for a direct link. */
  route: string | null;
  /** What to remove, in the imperative. */
  action: string;
  /** The specifics: which scenario/period/form actually holds the member. */
  items: string[];
  items_truncated: boolean;
  /**
   * True = must be cleared before the member can be deleted (posted data,
   * data entry forms, valid/invalid intersection rules). False = advisory:
   * shown so the user knows, but the delete goes through.
   */
  blocking: boolean;
  /** Schema-level wording, kept for support ("114 row(s) in consol_fact"). */
  technical: string;
}

export interface MemberReferencesResponse {
  dimension: string;
  member_id: number | null;
  code: string | null;
  /** Nothing references this member anywhere. */
  deletable: boolean;
  /** The delete is permitted — no BLOCKING reference. Advisory ones may exist. */
  can_delete: boolean;
  total_references: number;
  blocking_count: number;
  advisory_count: number;
  references: MemberReference[];
}

export const memberReferencesApi = {
  get: async (params: {
    dimension: string;
    member_id?: number | string;
    code?: string;
    name?: string;
    shared?: boolean;
  }): Promise<MemberReferencesResponse> =>
    (await apiClient.get<MemberReferencesResponse>('/member-references', { params })).data,
};

export const exportJobsApi = {
  trigger: async (jobType: string, year: number): Promise<any> => {
    const response = await apiClient.post('/export-jobs/trigger', null, {
      params: { job_type: jobType, year },
    });
    return response.data;
  },

  status: async (jobType: string, year: number): Promise<any> => {
    const response = await apiClient.get('/export-jobs/status', {
      params: { job_type: jobType, year },
    });
    return response.data;
  },

  download: async (jobType: string, year: number): Promise<void> => {
    const response = await apiClient.get('/export-jobs/download', {
      params: { job_type: jobType, year },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const disposition = response.headers['content-disposition'];
    const filename = disposition?.match(/filename="(.+?)"/)?.[1] || `export_${jobType}_${year}.xlsx`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  list: async (): Promise<any> => {
    const response = await apiClient.get('/export-jobs');
    return response.data;
  },
};

// --- Entity Types API --------------------------------------------------------

export const entityTypesApi = {
  listTypes: async (params?: { is_active?: boolean }): Promise<{ items: EntityTypeLookup[]; total: number }> => {
    const response = await apiClient.get('/entity-types', { params });
    return response.data;
  },
  createType: async (data: EntityTypeLookupCreate): Promise<EntityTypeLookup> => {
    const response = await apiClient.post('/entity-types', data);
    return response.data;
  },
  updateType: async (id: number, data: Partial<EntityTypeLookupCreate>): Promise<EntityTypeLookup> => {
    const response = await apiClient.put(`/entity-types/${id}`, data);
    return response.data;
  },
  listStatuses: async (params?: { is_active?: boolean }): Promise<{ items: EntityStatusLookup[]; total: number }> => {
    const response = await apiClient.get('/entity-statuses', { params });
    return response.data;
  },
  createStatus: async (data: EntityStatusLookupCreate): Promise<EntityStatusLookup> => {
    const response = await apiClient.post('/entity-statuses', data);
    return response.data;
  },
};

// --- Consolidation Methods API -----------------------------------------------

export const consolidationMethodsApi = {
  list: async (params?: { is_active?: boolean }): Promise<ConsolidationMethodLookup[]> => {
    const response = await apiClient.get('/consolidation-methods', { params });
    return response.data;
  },
  create: async (data: ConsolidationMethodLookupCreate): Promise<ConsolidationMethodLookup> => {
    const response = await apiClient.post('/consolidation-methods', data);
    return response.data;
  },
  update: async (id: number, data: Partial<ConsolidationMethodLookupCreate>): Promise<ConsolidationMethodLookup> => {
    const response = await apiClient.put(`/consolidation-methods/${id}`, data);
    return response.data;
  },
  bulkSave: async (
    // id=0 creates a new custom method; delete=true removes a custom method
    methods: Array<Partial<ConsolidationMethodLookupCreate> & { id: number; delete?: boolean }>,
  ): Promise<ConsolidationMethodLookup[]> => {
    const response = await apiClient.put('/consolidation-methods/bulk-save', { methods });
    return response.data;
  },
  delete: async (id: number): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete(`/consolidation-methods/${id}`);
    return response.data;
  },
  exportCsv: async (): Promise<Blob> => {
    const response = await apiClient.get('/consolidation-methods/export', { responseType: 'blob' });
    return response.data;
  },
  importCsv: async (file: File): Promise<{ created: number; updated: number; errors?: string[]; message?: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/consolidation-methods/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

// --- Hierarchies API ---------------------------------------------------------

export const hierarchiesApi = {
  list: async (params?: { is_active?: boolean }): Promise<EntityHierarchy[]> => {
    const response = await apiClient.get('/hierarchies', { params });
    return response.data;
  },
  create: async (data: EntityHierarchyCreate): Promise<EntityHierarchy> => {
    const response = await apiClient.post('/hierarchies', data);
    return response.data;
  },
  update: async (id: number, data: Partial<EntityHierarchyCreate> & { is_active?: boolean }): Promise<EntityHierarchy> => {
    const response = await apiClient.put(`/hierarchies/${id}`, data);
    return response.data;
  },
  getTree: async (id: number): Promise<HierarchyTreeNode[]> => {
    const response = await apiClient.get(`/hierarchies/${id}/nodes`);
    return response.data;
  },
  addNode: async (id: number, data: HierarchyNodeCreate): Promise<HierarchyTreeNode> => {
    const response = await apiClient.post(`/hierarchies/${id}/nodes`, data);
    return response.data;
  },
};

// --- Consolidation Status API ------------------------------------------------

export const consolidationStatusApi = {
  get: async (periodId: number, scenario = 'Actual'): Promise<ConsolidationStatusSummary> => {
    const response = await apiClient.get('/consolidation-status', { params: { period_id: periodId, scenario } });
    return response.data;
  },
  refresh: async (data: { period_id: number; scenario: string; entity_ids?: number[] }): Promise<ConsolidationStatusSummary> => {
    const response = await apiClient.post('/consolidation-status/refresh', data);
    return response.data;
  },
};

// --- TB Lock API -------------------------------------------------------------

export const tbLockApi = {
  lock: async (data: { entity_id: number; period_id: number; scenario: string; comment?: string }): Promise<TBLockResponse> => {
    const response = await apiClient.post('/tb-locks/lock', data);
    return response.data;
  },
  unlock: async (data: { entity_id: number; period_id: number; scenario: string; comment?: string }): Promise<TBLockResponse> => {
    const response = await apiClient.post('/tb-locks/unlock', data);
    return response.data;
  },
  submit: async (data: { entity_id: number; period_id: number; scenario: string; comment?: string }): Promise<TBLockResponse> => {
    const response = await apiClient.post('/tb-locks/submit', data);
    return response.data;
  },
  approve: async (data: { entity_id: number; period_id: number; scenario: string; comment?: string }): Promise<TBLockResponse> => {
    const response = await apiClient.post('/tb-locks/approve', data);
    return response.data;
  },
  reject: async (data: { entity_id: number; period_id: number; scenario: string; comment?: string }): Promise<TBLockResponse> => {
    const response = await apiClient.post('/tb-locks/reject', data);
    return response.data;
  },
};

// --- Menu Configuration ------------------------------------------------------

export const menuConfigApi = {
  getConfig: async (): Promise<import('@/types').MenuConfigListResponse> => {
    const response = await apiClient.get('/menu-config');
    return response.data;
  },
  updateConfig: async (items: import('@/types').MenuConfigUpdate[]): Promise<import('@/types').MenuConfigListResponse> => {
    const response = await apiClient.put('/menu-config', { items });
    return response.data;
  },
  resetConfig: async (): Promise<import('@/types').MenuConfigListResponse> => {
    const response = await apiClient.post('/menu-config/reset', {});
    return response.data;
  },
};

// --- Scenarios API (v2) ------------------------------------------------------

export const scenariosApi = {
  list: async (params?: {
    scenario_type?: string;
    data_storage?: string;
    parent_scenario_id?: number;
    requires_approval?: boolean;
    is_active?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<any[]> => {
    const response = await apiClient.get<any>('/scenarios', { params });
    return response.data?.items ?? response.data;
  },

  get: async (id: number | string): Promise<any> => {
    const response = await apiClient.get(`/scenarios/${id}`);
    return response.data;
  },

  create: async (data: any): Promise<any> => {
    const response = await apiClient.post('/scenarios', data);
    return response.data;
  },

  update: async (id: number | string, data: any): Promise<any> => {
    const response = await apiClient.put(`/scenarios/${id}`, data);
    return response.data;
  },

  delete: async (id: number | string): Promise<void> => {
    await apiClient.delete(`/scenarios/${id}`);
  },

  tree: async (): Promise<any[]> => {
    const response = await apiClient.get('/scenarios/tree');
    return response.data;
  },

  seed: async (): Promise<any> => {
    const response = await apiClient.post('/scenarios/seed');
    return response.data;
  },
};

// --- FCCS Dimension APIs -------------------------------------------------------

export const fccsApi = {
  // Scenarios — delegate to dedicated scenariosApi; kept here for back-compat
  listScenarios: async () => (await apiClient.get('/scenarios')).data,
  createScenario: async (data: any) => (await apiClient.post('/scenarios', data)).data,
  updateScenario: async (id: number, data: any) => (await apiClient.put(`/scenarios/${id}`, data)).data,
  deleteScenario: async (id: number) => (await apiClient.delete(`/scenarios/${id}`)).data,
  seedScenarios: async () => (await apiClient.post('/scenarios/seed')).data,

  // Fiscal Years
  listFiscalYears: async () => (await apiClient.get('/fiscal-years')).data,
  createFiscalYear: async (data: any) => (await apiClient.post('/fiscal-years', data)).data,
  updateFiscalYear: async (id: number, data: any) => (await apiClient.put(`/fiscal-years/${id}`, data)).data,

  /**
   * Uniform slim member list for a structural/catalog dimension — hits
   * `/form-members/{dimensionId}` (~4 fields) instead of the dim's full endpoint.
   * Returns the same members mapped by `rowsFromParentCodeHierarchy` in the form
   * runtime. Supported dims: Movement, Consolidation, Currency, View, DataSource,
   * MultiGAAP, Status, FromCurrency, ICP. Full dim endpoints stay for Dimension Manager.
   */
  listDimFormMembers: async (
    dimensionId: string,
    opts: { includeInactive?: boolean } = {},
  ): Promise<Array<{ id: number | null; code: string; name: string; parent_code: string | null; is_active: boolean }>> => {
    const res = await apiClient.get<any>(`/form-members/${dimensionId}`, {
      params: { include_inactive: opts.includeInactive ?? true, page_size: 5000 },
    });
    return res.data?.items ?? res.data ?? [];
  },

  /**
   * Slim member pools for MANY dimensions in ONE request.
   *
   * The data-entry grid needs a pool per dimension before it opens; one-request-per-dimension
   * meant ~9 round trips that the browser's ~6-connection cap turned into serialised waves.
   * Returns `{ dimensionId: rows }`; ids this endpoint does not serve are simply absent, so the
   * caller falls back to `listDimFormMembers` per dimension.
   */
  listDimFormMembersBatch: async (
    dimensionIds: string[],
    opts: { includeInactive?: boolean } = {},
  ): Promise<
    Record<string, Array<{ id: number | null; code: string; name: string; parent_code: string | null; is_active: boolean }>>
  > => {
    if (dimensionIds.length === 0) return {};
    const res = await apiClient.get<any>('/form-members/batch', {
      params: {
        dimension_ids: dimensionIds.join(','),
        include_inactive: opts.includeInactive ?? true,
      },
    });
    return res.data ?? {};
  },

  // Consolidation Dimension
  listConsolDim: async () => (await apiClient.get('/consolidation-dim/members')).data,
  seedConsolDim: async () => (await apiClient.post('/consolidation-dim/members/seed')).data,
  createConsolDim: async (data: any) => (await apiClient.post('/consolidation-dim/members', data)).data,
  updateConsolDim: async (code: string, data: any) => (await apiClient.put(`/consolidation-dim/members/${code}`, data)).data,
  deleteConsolDim: async (code: string) => (await apiClient.delete(`/consolidation-dim/members/${code}`)).data,
  moveConsolDim: async (code: string, newParentCode: string | null) => (await apiClient.patch(`/consolidation-dim/members/${code}/move`, null, { params: { new_parent_code: newParentCode } })).data,
  validateConsolHierarchy: async () => (await apiClient.post('/consolidation-dim/members/validate')).data,

  // Movement Dimension
  listMovementDim: async () => (await apiClient.get('/movement-dim/members')).data,
  seedMovementDim: async () => (await apiClient.post('/movement-dim/members/seed')).data,
  reseedMovementDim: async () => (await apiClient.post('/movement-dim/members/reseed')).data,
  createMovementDim: async (data: any) => (await apiClient.post('/movement-dim/members', data)).data,
  updateMovementDim: async (code: string, data: any) => (await apiClient.put(`/movement-dim/members/${code}`, data)).data,
  deleteMovementDim: async (code: string) => (await apiClient.delete(`/movement-dim/members/${code}`)).data,
  /**
   * Mark (or clear) the Default Movement flag on many movement members at once.
   * Send no `codes` to mark every eligible member under the movement-subtotal
   * branch; send a list to act on exactly those. `skipped` explains, per code,
   * anything the server refused — a bulk action must not drop work silently.
   */
  bulkSetDefaultMovement: async (body: {
    codes?: string[];
    is_default_movement: boolean;
    root?: string;
  }): Promise<{
    updated: number;
    already_set: number;
    eligible_total: number;
    skipped: Record<string, string>;
  }> => (await apiClient.post('/movement-dim/members/bulk-default-movement', body)).data,
  moveMovementDim: async (code: string, newParentCode: string | null) => (await apiClient.patch(`/movement-dim/members/${code}/move`, null, { params: { new_parent_code: newParentCode } })).data,
  validateMovementHierarchy: async () => (await apiClient.post('/movement-dim/members/validate')).data,

  // Data Sources
  listDataSources: async () => (await apiClient.get('/data-sources')).data,
  seedDataSources: async () => (await apiClient.post('/data-sources/seed')).data,
  createDataSource: async (data: any) => (await apiClient.post('/data-sources', data)).data,
  updateDataSource: async (code: string, data: any) => (await apiClient.put(`/data-sources/${code}`, data)).data,
  deleteDataSource: async (code: string) => (await apiClient.delete(`/data-sources/${code}`)).data,

  // Data Types
  listDataTypes: async () => (await apiClient.get('/data-type-dim')).data,
  seedDataTypes: async () => (await apiClient.post('/data-type-dim/seed')).data,
  createDataType: async (data: any) => (await apiClient.post('/data-type-dim', data)).data,
  updateDataType: async (code: string, data: any) => (await apiClient.put(`/data-type-dim/${code}`, data)).data,
  deleteDataType: async (code: string) => (await apiClient.delete(`/data-type-dim/${code}`)).data,

  // Values
  listValues: async () => (await apiClient.get('/value-dim')).data,
  seedValues: async () => (await apiClient.post('/value-dim/seed')).data,
  createValue: async (data: any) => (await apiClient.post('/value-dim', data)).data,
  updateValue: async (code: string, data: any) => (await apiClient.put(`/value-dim/${code}`, data)).data,
  deleteValue: async (code: string) => (await apiClient.delete(`/value-dim/${code}`)).data,

  // IC Partners
  listIcPartners: async (opts: { includeInactive?: boolean } = {}) =>
    (
      await apiClient.get('/intercompany-partners', {
        params: opts.includeInactive ? { include_inactive: true } : undefined,
      })
    ).data,
  syncIcPartners: async () => (await apiClient.post('/intercompany-partners/sync-from-entities')).data,
  createIcPartner: async (data: any) => (await apiClient.post('/intercompany-partners', data)).data,
  updateIcPartner: async (code: string, data: any) => (await apiClient.put(`/intercompany-partners/${code}`, data)).data,
  deleteIcPartner: async (code: string) => (await apiClient.delete(`/intercompany-partners/${code}`)).data,

  // Currency Translation Rules
  listTranslationRules: async () => (await apiClient.get('/currency-translation-rules')).data,
  seedTranslationRules: async () => (await apiClient.post('/currency-translation-rules/seed')).data,

  // Custom Dimensions — Config CRUD
  listCustomConfigs: async () => (await apiClient.get('/custom-dimensions')).data,
  createCustomConfig: async (data: any) => (await apiClient.post('/custom-dimensions', data)).data,
  updateCustomConfig: async (slot: string, data: any) => (await apiClient.put(`/custom-dimensions/${slot}`, data)).data,
  deleteCustomConfig: async (slot: string) => (await apiClient.delete(`/custom-dimensions/${slot}`)).data,
  seedCustomDimensions: async () => (await apiClient.post('/custom-dimensions/seed')).data,
  // Custom Dimensions — Member CRUD
  listCustomMembers: async (slot: string) => (await apiClient.get(`/custom-dimensions/${slot}/members`)).data,
  createCustomMember: async (slot: string, data: any) => (await apiClient.post(`/custom-dimensions/${slot}/members`, data)).data,
  updateCustomMember: async (slot: string, code: string, data: any) => (await apiClient.put(`/custom-dimensions/${slot}/members/${code}`, data)).data,
  deleteCustomMember: async (slot: string, code: string) => (await apiClient.delete(`/custom-dimensions/${slot}/members/${code}`)).data,

  // Intersection Rules
  listIntersectionRules: async () => (await apiClient.get('/intersection-rules')).data,
  /** Cheap tenant-wide check: are there ANY enabled VI groups / active legacy deny rules?
   *  When false, the form runtime skips all validate / validate-batch calls (every cell is valid). */
  intersectionRulesSummary: async (): Promise<{ has_rules: boolean; groups: number; legacy: number }> =>
    (await apiClient.get('/intersection-rules/summary')).data,
  createIntersectionRule: async (data: any) => (await apiClient.post('/intersection-rules', data)).data,
  validatePOV: async (data: any) => (await apiClient.post('/intersection-rules/validate', data)).data,
  // The backend caps each validate-batch request at 500 items (guard against unbounded
  // payloads). Large grids (e.g. an ICP axis with 1000+ members) exceed that in one shot
  // and get a 422, so chunk into <=500 and merge the per-item results back in order.
  validatePOVBatch: async (items: any[]) => {
    const MAX_BATCH = 500;
    if (items.length <= MAX_BATCH) {
      return (await apiClient.post('/intersection-rules/validate-batch', { items })).data;
    }
    const merged: any[] = [];
    for (let i = 0; i < items.length; i += MAX_BATCH) {
      const chunk = items.slice(i, i + MAX_BATCH);
      const data = (await apiClient.post('/intersection-rules/validate-batch', { items: chunk })).data;
      merged.push(...(data?.items ?? []));
    }
    return { items: merged };
  },
  /** Validate a WHOLE form grid in one request. The client sends axis vectors (per-row / per-col
   *  member_ids deltas), the server builds the row×col product and returns which rows/cols/cells
   *  are invalid. Replaces the per-cell validate-batch loop (no chunking, no 422). Falls back to
   *  validatePOV/validatePOVBatch client-side when the endpoint is disabled (404) or errors. */
  validateIntersectionGrid: async (payload: {
    context: Record<string, unknown>;
    rows: Array<{ code: string; member_ids: Record<string, number> }>;
    cols: Array<{ code: string; member_ids: Record<string, number> }>;
    cell_cap?: number | null;
    mark_cells?: boolean;
  }): Promise<{
    context_valid: boolean;
    context_violations: string[];
    mode: 'cells' | 'probe';
    invalid_cells: Array<{ row: string; col: string }>;
    invalid_rows: string[];
    invalid_cols: string[];
  }> => (await apiClient.post('/intersection-rules/validate-grid', payload)).data,

  // Cube Query
  queryCube: async (data: any) => (await apiClient.post('/cube/query', data)).data,
  drillThrough: async (params: any) => (await apiClient.get('/cube/drill-through', { params })).data,

  // Currency Dimension (new)
  listCurrencyDim: async () => (await apiClient.get('/currency-dim')).data,
  seedCurrencyDim: async (force?: boolean) => (await apiClient.post('/currency-dim/seed', null, { params: force ? { force: true } : undefined })).data,
  createCurrencyDim: async (data: any) => (await apiClient.post('/currency-dim', data)).data,
  updateCurrencyDim: async (code: string, data: any) => (await apiClient.put(`/currency-dim/${code}`, data)).data,
  deleteCurrencyDim: async (code: string) => (await apiClient.delete(`/currency-dim/${code}`)).data,
  listWorldCurrencies: async () => (await apiClient.get('/currency-dim/world-currencies')).data,
  addInputCurrency: async (data: { code: string; name: string; symbol?: string; precision?: number | null; create_reporting?: boolean }) =>
    (await apiClient.post('/currency-dim/add-input', data)).data,

  // View Dimension (new)
  listViewDim: async () => (await apiClient.get('/view-dim')).data,
  seedViewDim: async () => (await apiClient.post('/view-dim/seed')).data,

  // Cubes (new)
  listCubes: async (params?: { include_inactive?: boolean }) =>
    (await apiClient.get('/cube-config', { params: params?.include_inactive ? { include_inactive: true } : undefined }))
      .data,
  seedCubes: async () => (await apiClient.post('/cube-config/seed')).data,
  patchCube: async (cubeId: number, data: { is_active: boolean }) =>
    (await apiClient.patch(`/cube-config/${cubeId}`, data)).data,
  // Rename / partial-update a cube (any subset of fields).
  updateCube: async (
    cubeId: number,
    data: { name?: string; code?: string; description?: string | null; is_active?: boolean },
  ) => (await apiClient.patch(`/cube-config/${cubeId}`, data)).data,
  renameCube: async (cubeId: number, name: string) =>
    (await apiClient.patch(`/cube-config/${cubeId}`, { name })).data,
  deleteCube: async (cubeId: number) =>
    (await apiClient.delete(`/cube-config/${cubeId}`)).data,
  createCube: async (data: {
    code: string;
    name: string;
    description?: string | null;
    template_code?: 'consol' | 'rates' | 'dataCube' | 'predefinedDataCube';
    cube_type?: 'BSO' | 'ASO';
    usage?: 'consolidation' | 'rates' | 'data_entry' | 'reporting' | 'mixed' | 'custom';
    enable_data_entry?: boolean;
    enable_consolidation?: boolean;
    enable_rates?: boolean;
    enable_reporting?: boolean;
    is_active?: boolean;
  }) => (await apiClient.post('/cube-config', data)).data,
  getCubeDimensions: async (cubeId: number) => (await apiClient.get(`/cube-config/${cubeId}/dimensions`)).data,

  // Alias Tables (new)
  listAliasTables: async () => (await apiClient.get('/alias-tables')).data,
  seedAliasTables: async () => (await apiClient.post('/alias-tables/seed')).data,
  getMemberAliases: async (dimensionType: string, memberCode: string) =>
    (await apiClient.get(`/alias-tables/member/${dimensionType}/${memberCode}`)).data,
  updateMemberAlias: async (aliasTableId: number, dimensionType: string, memberCode: string, aliasValue: string) =>
    (await apiClient.put(`/alias-tables/${aliasTableId}/member/${dimensionType}/${memberCode}`, { alias_value: aliasValue })).data,

  // Smart Lists — delegates to standalone smartListsApi below
  listSmartLists: async (): Promise<SmartList[]> => (await apiClient.get('/smart-lists')).data,
  getSmartList: async (id: number): Promise<SmartListWithEntries> => (await apiClient.get(`/smart-lists/${id}`)).data,
  createSmartList: async (data: SmartListCreate): Promise<SmartList> => (await apiClient.post('/smart-lists', data)).data,
  updateSmartList: async (id: number, data: Partial<SmartListCreate> & { is_active?: boolean }): Promise<SmartList> =>
    (await apiClient.put(`/smart-lists/${id}`, data)).data,
  deleteSmartList: async (id: number): Promise<void> => { await apiClient.delete(`/smart-lists/${id}`); },
  getSmartListEntries: async (listId: number): Promise<SmartListEntry[]> => (await apiClient.get(`/smart-lists/${listId}/entries`)).data,
  addSmartListEntry: async (listId: number, data: { code: string; label: string; numeric_value?: number; sort_order?: number }): Promise<SmartListEntry> =>
    (await apiClient.post(`/smart-lists/${listId}/entries`, data)).data,
  updateSmartListEntry: async (listId: number, entryId: number, data: Partial<SmartListEntry>): Promise<SmartListEntry> =>
    (await apiClient.put(`/smart-lists/${listId}/entries/${entryId}`, data)).data,
  deleteSmartListEntry: async (listId: number, entryId: number): Promise<void> => {
    await apiClient.delete(`/smart-lists/${listId}/entries/${entryId}`);
  },
  reorderSmartListEntries: async (listId: number, items: { id: number; sort_order: number }[]): Promise<void> => {
    await apiClient.post(`/smart-lists/${listId}/entries/reorder`, items);
  },
  getSmartListUsage: async (id: number): Promise<SmartListUsage[]> => (await apiClient.get(`/smart-lists/${id}/usage`)).data,

  // Accounts seed
  seedAccounts: async () => (await apiClient.post('/accounts/group/seed')).data,

  // Review Usage (G8) — where a member is referenced
  getMemberUsage: async (dimensionType: string, memberCode: string) =>
    (await apiClient.get(`/dimensions/usage/${dimensionType}/${memberCode}`)).data,

  // UDAs — User-Defined Attributes (G10)
  getMemberUdas: async (dimensionType: string, memberCode: string) =>
    (await apiClient.get(`/dimensions/udas/${dimensionType}/${memberCode}`)).data,
  addMemberUda: async (dimensionType: string, memberCode: string, uda: string) =>
    (await apiClient.post(`/dimensions/udas/${dimensionType}/${memberCode}`, { uda })).data,
  removeMemberUda: async (dimensionType: string, memberCode: string, uda: string) =>
    (await apiClient.delete(`/dimensions/udas/${dimensionType}/${memberCode}/${uda}`)).data,

  // Multi-GAAP Dimension
  listMultiGaapDim: async () => (await apiClient.get('/multi-gaap-dim/members')).data,
  seedMultiGaapDim: async () => (await apiClient.post('/multi-gaap-dim/members/seed')).data,
  createMultiGaapDim: async (data: any) => (await apiClient.post('/multi-gaap-dim/members', data)).data,
  updateMultiGaapDim: async (code: string, data: any) => (await apiClient.put(`/multi-gaap-dim/members/${code}`, data)).data,
  deleteMultiGaapDim: async (code: string) => (await apiClient.delete(`/multi-gaap-dim/members/${code}`)).data,
  moveMultiGaapDim: async (code: string, newParentCode: string | null) => (await apiClient.patch(`/multi-gaap-dim/members/${code}/move`, null, { params: { new_parent_code: newParentCode } })).data,
  validateMultiGaapHierarchy: async () => (await apiClient.post('/multi-gaap-dim/members/validate')).data,

  // Status Dimension
  listStatusDim: async () => (await apiClient.get('/status-dim/members')).data,
  seedStatusDim: async () => (await apiClient.post('/status-dim/members/seed')).data,
  createStatusDim: async (data: any) => (await apiClient.post('/status-dim/members', data)).data,
  updateStatusDim: async (code: string, data: any) => (await apiClient.put(`/status-dim/members/${code}`, data)).data,
  deleteStatusDim: async (code: string) => (await apiClient.delete(`/status-dim/members/${code}`)).data,
  moveStatusDim: async (code: string, newParentCode: string | null) => (await apiClient.patch(`/status-dim/members/${code}/move`, null, { params: { new_parent_code: newParentCode } })).data,
  validateStatusHierarchy: async () => (await apiClient.post('/status-dim/members/validate')).data,

  // HSP_Metric Dimension
  listHspMetricDim: async () => (await apiClient.get('/hsp-metric-dim/members')).data,
  seedHspMetricDim: async () => (await apiClient.post('/hsp-metric-dim/members/seed')).data,
  createHspMetricDim: async (data: any) => (await apiClient.post('/hsp-metric-dim/members', data)).data,
  updateHspMetricDim: async (code: string, data: any) => (await apiClient.put(`/hsp-metric-dim/members/${code}`, data)).data,
  deleteHspMetricDim: async (code: string) => (await apiClient.delete(`/hsp-metric-dim/members/${code}`)).data,
  moveHspMetricDim: async (code: string, newParentCode: string | null) => (await apiClient.patch(`/hsp-metric-dim/members/${code}/move`, null, { params: { new_parent_code: newParentCode } })).data,
  validateHspMetricHierarchy: async () => (await apiClient.post('/hsp-metric-dim/members/validate')).data,

  // From Currency Dimension — auto-mirrors Input Currencies as From_<code>
  listFromCurrencyDim: async () => (await apiClient.get('/from-currency-dim/members')).data,
  createFromCurrencyDim: async (data: any) => (await apiClient.post('/from-currency-dim/members', data)).data,
  updateFromCurrencyDim: async (code: string, data: any) => (await apiClient.put(`/from-currency-dim/members/${code}`, data)).data,
  deleteFromCurrencyDim: async (code: string) => (await apiClient.delete(`/from-currency-dim/members/${code}`)).data,
  syncFromCurrencyDim: async () => (await apiClient.post('/from-currency-dim/members/sync')).data,

  // Seed All Dimensions at once
  seedAll: async () => (await apiClient.post('/dimensions/seed-all')).data,

  // Dimension Security (BRD 14.03)
  listSecurityRules: async (params?: { dimension?: string; user_id?: number; role_name?: string; active_only?: boolean }) =>
    (await apiClient.get('/dimension-security', { params })).data,
  createSecurityRule: async (data: any) => (await apiClient.post('/dimension-security', data)).data,
  updateSecurityRule: async (id: number, data: any) => (await apiClient.put(`/dimension-security/${id}`, data)).data,
  deleteSecurityRule: async (id: number) => (await apiClient.delete(`/dimension-security/${id}`)).data,
  checkAccess: async (params: { user_id: number; dimension: string; member_code: string }) =>
    (await apiClient.get('/dimension-security/check', { params })).data,
};

// --- Report Builder ----------------------------------------------------------

export const reportBuilderApi = {
  listSavedReports: async () => (await apiClient.get('/report-schedules')).data,
  createReport: async (data: any) => (await apiClient.post('/report-schedules', data)).data,
  runReport: async (id: number) => (await apiClient.post(`/report-schedules/${id}/run`)).data,
};

// --- Footnotes ---------------------------------------------------------------

export const footnotesApi = {
  listFootnotes: async (params?: any) => (await apiClient.get('/footnotes', { params })).data,
  createFootnote: async (data: any) => (await apiClient.post('/footnotes', data)).data,
  updateFootnote: async (id: number, data: any) => (await apiClient.put(`/footnotes/${id}`, data)).data,
  deleteFootnote: async (id: number) => (await apiClient.delete(`/footnotes/${id}`)).data,
};

// --- Validation Exceptions ---------------------------------------------------

export const validationExceptionsApi = {
  listExceptions: async (params?: any) => (await apiClient.get('/validation-exceptions', { params })).data,
  investigateException: async (id: number, userId: number) =>
    (await apiClient.put(`/validation-exceptions/${id}/investigate`, { assigned_to: userId })).data,
  waiveException: async (id: number, data: any) =>
    (await apiClient.put(`/validation-exceptions/${id}/waive`, data)).data,
  resolveException: async (id: number, data: any) =>
    (await apiClient.put(`/validation-exceptions/${id}/resolve`, data)).data,
  escalateException: async (id: number, data: any) =>
    (await apiClient.put(`/validation-exceptions/${id}/escalate`, data)).data,
  exceptionSummary: async (params?: any) =>
    (await apiClient.get('/validation-exceptions/summary', { params })).data,
};

// --- SoD (Segregation of Duties) ---------------------------------------------

export const sodApi = {
  listSodRules: async () => (await apiClient.get('/sod/rules')).data,
  createSodRule: async (data: any) => (await apiClient.post('/sod/rules', data)).data,
  checkSod: async (params: any) => (await apiClient.get('/sod/check', { params })).data,
  listSodViolations: async () => (await apiClient.get('/sod/violations')).data,
};

// --- Data Load ---------------------------------------------------------------

export const dataLoadApi = {
  // Config management
  listConfigs: async () => (await apiClient.get('/data-load/configs')).data,
  getDefaultConfig: async () => (await apiClient.get('/data-load/configs/default')).data,
  createConfig: async (data: any) => (await apiClient.post('/data-load/configs', data)).data,
  updateConfig: async (id: number, data: any) => (await apiClient.put(`/data-load/configs/${id}`, data)).data,
  deleteConfig: async (id: number) => (await apiClient.delete(`/data-load/configs/${id}`)).data,

  // Status & monitoring
  getStatusMatrix: async (periodId: number, scenario?: string) =>
    (await apiClient.get('/data-load/status-matrix', { params: { period_id: periodId, scenario: scenario || 'Actual' } })).data,
  getEntityDataStatus: async (periodId: number, scenario?: string) =>
    (await apiClient.get('/data-load/entity-data-status', { params: { period_id: periodId, scenario: scenario || 'Actual' } })).data,
  getProcessLog: async (params?: { period_id?: number; entity_id?: number; limit?: number; offset?: number }) =>
    (await apiClient.get('/data-load/process-log', { params })).data,

  // YTD/Periodic conversion
  convertYtdToPeriodic: async (data: { entity_id: number; period_id: number; scenario?: string }) =>
    (await apiClient.post('/data-load/convert-ytd-to-periodic', data)).data,

  // Opening balance
  carryForwardOpening: async (data: { period_id: number; entity_ids?: number[] }) =>
    (await apiClient.post('/data-load/carry-forward-opening', data)).data,

  // Movement & View dimensions
  listMovements: async () => (await apiClient.get('/data-load/movements')).data,
  seedMovements: async () => (await apiClient.post('/data-load/movements/seed')).data,
  listViews: async () => (await apiClient.get('/data-load/views')).data,
  seedViews: async () => (await apiClient.post('/data-load/views/seed')).data,

  // Validation
  validateSession: async (sessionId: string): Promise<any> => {
    const response = await apiClient.post(`/data-load/validate/${sessionId}`);
    return response.data;
  },
  getValidationReport: async (sessionId: string): Promise<any> => {
    const response = await apiClient.get(`/data-load/validation-report/${sessionId}`);
    return response.data;
  },

  // Copy & Clear
  copyData: async (data: { source_scenario: string; source_year: number; source_period: string; target_scenario: string; target_year: number; target_period: string; entity_ids?: number[]; clear_target_first?: boolean }): Promise<any> => {
    const response = await apiClient.post('/data-load/copy-data', data);
    return response.data;
  },
  clearData: async (data: { scenario: string; year: number; period: string; entity_ids?: number[]; preview_only?: boolean }): Promise<any> => {
    const response = await apiClient.post('/data-load/clear-data', data);
    return response.data;
  },

  // Jobs
  listJobs: async (): Promise<any[]> => {
    const response = await apiClient.get('/data-load/jobs');
    return response.data;
  },
  getJob: async (jobId: string): Promise<any> => {
    const response = await apiClient.get(`/data-load/jobs/${jobId}`);
    return response.data;
  },
};

// --- TB Reports --------------------------------------------------------------

export const tbReportsApi = {
  getConsolidationReviewMatrix: async (params: { period_id: number; entity_id?: number; scenario?: string; account_type?: string }) =>
    (await apiClient.get('/tb-reports/consolidation-review-matrix', { params })).data,
  getTbComparison: async (params: { period_id: number; scenario?: string; materiality_threshold?: number }) =>
    (await apiClient.get('/tb-reports/tb-comparison', { params })).data,
  getTranslationReview: async (params: { entity_id: number; period_id: number; scenario?: string }) =>
    (await apiClient.get('/tb-reports/translation-review', { params })).data,
  getMovementSchedule: async (params: { entity_id: number; period_id: number; account_id?: number; scenario?: string }) =>
    (await apiClient.get('/tb-reports/movement-schedule', { params })).data,
  getEntityReadiness: async (params: { period_id: number; scenario?: string }) =>
    (await apiClient.get('/tb-reports/entity-readiness', { params })).data,
  getDataQualitySummary: async (params: { period_id: number; entity_id?: number; scenario?: string }) =>
    (await apiClient.get('/tb-reports/data-quality-summary', { params })).data,

  // Excel exports
  exportConsolidationReview: async (params: { period_id: number; entity_id?: number; scenario?: string }) =>
    (await apiClient.get('/tb-reports/export/consolidation-review', { params, responseType: 'blob' })).data,
  exportMovementSchedule: async (params: { entity_id: number; period_id: number; scenario?: string }) =>
    (await apiClient.get('/tb-reports/export/movement-schedule', { params, responseType: 'blob' })).data,
  exportTranslationReview: async (params: { entity_id: number; period_id: number; scenario?: string }) =>
    (await apiClient.get('/tb-reports/export/translation-review', { params, responseType: 'blob' })).data,
};

// --- Valid intersections (Oracle-style VI admin API, Phase 5) ---------------

export const validIntersectionsApi = {
  listGroups: async (params?: {
    enabled?: boolean;
    definition_type?: string;
    search?: string;
    page?: number;
    page_size?: number;
    modified_after?: string;
    modified_before?: string;
  }) => (await apiClient.get('/valid-intersections/groups', { params })).data,

  getGroup: async (groupId: number) => (await apiClient.get(`/valid-intersections/groups/${groupId}`)).data,

  createGroup: async (data: Record<string, unknown>) =>
    (await apiClient.post('/valid-intersections/groups', data)).data,

  updateGroup: async (groupId: number, data: Record<string, unknown>) =>
    (await apiClient.patch(`/valid-intersections/groups/${groupId}`, data)).data,

  deleteGroup: async (groupId: number) => (await apiClient.delete(`/valid-intersections/groups/${groupId}`)).data,

  duplicateGroup: async (groupId: number) =>
    (await apiClient.post(`/valid-intersections/groups/${groupId}/duplicate`)).data,

  reorderGroups: async (group_ids_in_order: number[]) =>
    (await apiClient.patch('/valid-intersections/group-evaluation-order', { group_ids_in_order })).data,

  listRules: async (groupId: number, params?: { page?: number; page_size?: number }) =>
    (await apiClient.get(`/valid-intersections/groups/${groupId}/rules`, { params })).data,

  createRule: async (groupId: number, data: Record<string, unknown>) =>
    (await apiClient.post(`/valid-intersections/groups/${groupId}/rules`, data)).data,

  getRule: async (groupId: number, ruleId: number) =>
    (await apiClient.get(`/valid-intersections/groups/${groupId}/rules/${ruleId}`)).data,

  updateRule: async (groupId: number, ruleId: number, data: Record<string, unknown>) =>
    (await apiClient.patch(`/valid-intersections/groups/${groupId}/rules/${ruleId}`, data)).data,

  deleteRule: async (groupId: number, ruleId: number) =>
    (await apiClient.delete(`/valid-intersections/groups/${groupId}/rules/${ruleId}`)).data,

  reorderRules: async (groupId: number, rule_ids_in_order: number[]) =>
    (await apiClient.patch(`/valid-intersections/groups/${groupId}/rules/reorder`, { rule_ids_in_order })).data,

  previewMembers: async (data: Record<string, unknown>) =>
    (await apiClient.post('/valid-intersections/preview-members', data)).data,

  exportJsonV1: async (): Promise<Record<string, unknown>> =>
    (await apiClient.get('/valid-intersections/exchange/export-json-v1/sync')).data,

  exportJsonV1Async: async (): Promise<{ job_id: number; task_id: string }> =>
    (await apiClient.get('/valid-intersections/exchange/export-json-v1')).data,

  validateImportJsonV1: async (body: Record<string, unknown>) =>
    (await apiClient.post('/valid-intersections/exchange/import-json-v1/validate', body)).data,

  commitImportJsonV1: async (body: Record<string, unknown>) =>
    (await apiClient.post('/valid-intersections/exchange/import-json-v1/commit', body)).data,

  runViReport: async (body: Record<string, unknown>) =>
    (await apiClient.post('/valid-intersections/reports/run', body)).data,

  listViReportRuns: async (): Promise<unknown[]> =>
    (await apiClient.get('/valid-intersections/reports/runs')).data,
};

// --- Integration Connections API ---------------------------------------------

export const integrationConnectionsApi = {
  list: async (params?: { status?: string; connector_code?: string; record_type?: string }): Promise<any[]> => {
    const response = await apiClient.get('/integration/connections', { params });
    return response.data;
  },
  create: async (data: any): Promise<any> => {
    const response = await apiClient.post('/integration/connections', data);
    return response.data;
  },
  get: async (id: number): Promise<any> => {
    const response = await apiClient.get(`/integration/connections/${id}`);
    return response.data;
  },
  update: async (id: number, data: any): Promise<any> => {
    const response = await apiClient.put(`/integration/connections/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/integration/connections/${id}`);
  },
  // Where is this application used across integrations (Source / Target)?
  getUsage: async (id: number): Promise<{ name: string; count: number; usage: { integration_id: number; integration_name: string; used_as: string }[] }> => {
    const response = await apiClient.get(`/integration/connections/${id}/usage`);
    return response.data;
  },
  test: async (id: number): Promise<{ success: boolean; message: string; latency_ms: number | null }> => {
    const response = await apiClient.post(`/integration/connections/${id}/test`);
    return response.data;
  },
  getLogs: async (id: number): Promise<any[]> => {
    const response = await apiClient.get(`/integration/connections/${id}/logs`);
    return response.data;
  },
  assignEntities: async (id: number, entityIds: number[]): Promise<any> => {
    const response = await apiClient.post(`/integration/connections/${id}/assign-entities`, { entity_ids: entityIds });
    return response.data;
  },
  getConnectorTypes: async (): Promise<any[]> => {
    const response = await apiClient.get('/integration/connections/connector-types');
    return response.data;
  },
};

export const stagingApi = {
  createSession: async (entityId: number, periodId: number, file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    // Backend declares entity_id/period_id as Query params (staging.py),
    // not form fields — sending them in the body returns 422.
    // Force multipart so the FormData body keeps its boundary; without this the
    // client's default 'application/json' header is inherited and the server
    // sees no `file` part → 422 "file: Field required".
    const response = await apiClient.post('/integration/staging/sessions', formData, {
      params: { entity_id: entityId, period_id: periodId },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  listSessions: async (): Promise<any[]> => {
    const response = await apiClient.get('/integration/staging/sessions');
    return response.data;
  },
  getSession: async (sessionId: string, page?: number, limit?: number): Promise<any> => {
    const response = await apiClient.get(`/integration/staging/sessions/${sessionId}`, { params: { page, limit } });
    return response.data;
  },
  getPreview: async (sessionId: string): Promise<any> => {
    const response = await apiClient.get(`/integration/staging/sessions/${sessionId}/preview`);
    return response.data;
  },
  applyMappings: async (sessionId: string): Promise<any> => {
    const response = await apiClient.post(`/integration/staging/sessions/${sessionId}/apply-mappings`);
    return response.data;
  },
  getUnmapped: async (sessionId: string): Promise<any[]> => {
    const response = await apiClient.get(`/integration/staging/sessions/${sessionId}/unmapped`);
    return response.data;
  },
  confirmLoad: async (sessionId: string, forceUnmapped?: boolean, dryRun?: boolean): Promise<any> => {
    const params = new URLSearchParams();
    if (forceUnmapped) params.append('force', 'true');
    if (dryRun) params.append('dry_run', 'true');
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiClient.post(`/integration/staging/sessions/${sessionId}/confirm${query}`);
    return response.data;
  },
  deleteSession: async (sessionId: string): Promise<void> => {
    await apiClient.delete(`/integration/staging/sessions/${sessionId}`);
  },
  // Oracle FCCS §17.1.8 — delete imported data for the current POV.
  deletePeriodData: async (
    periodId: string | number,
    entityId?: string | number,
  ): Promise<{ rows_deleted: number; message: string }> => {
    const params: Record<string, string | number> = { period_id: periodId };
    if (entityId !== undefined && entityId !== '') params.entity_id = entityId;
    const response = await apiClient.delete('/integration/staging/sessions/by-pov', { params });
    return response.data;
  },
};

export const mappingRulesApi = {
  list: async (params?: { dimension?: string; source_system?: string; is_active?: boolean }): Promise<any[]> => {
    const response = await apiClient.get('/integration/mapping-rules', { params });
    return response.data;
  },
  create: async (data: any): Promise<any> => {
    const response = await apiClient.post('/integration/mapping-rules', data);
    return response.data;
  },
  get: async (id: number): Promise<any> => {
    const response = await apiClient.get(`/integration/mapping-rules/${id}`);
    return response.data;
  },
  update: async (id: number, data: any): Promise<any> => {
    const response = await apiClient.put(`/integration/mapping-rules/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/integration/mapping-rules/${id}`);
  },
  bulkImport: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/integration/mapping-rules/bulk-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  exportCsv: async (): Promise<Blob> => {
    const response = await apiClient.get('/integration/mapping-rules/export', { responseType: 'blob' });
    return response.data;
  },
  test: async (dimension: string, values: string[]): Promise<any> => {
    const response = await apiClient.post('/integration/mapping-rules/test', { dimension, values });
    return response.data;
  },
  getGaps: async (): Promise<any[]> => {
    const response = await apiClient.get('/integration/mapping-rules/gaps');
    return response.data;
  },
};

export const periodMappingsApi = {
  list: async (params?: any): Promise<any[]> => {
    const response = await apiClient.get('/integration/period-mappings', { params });
    return response.data;
  },
  create: async (data: any): Promise<any> => {
    const response = await apiClient.post('/integration/period-mappings', data);
    return response.data;
  },
  update: async (id: number, data: any): Promise<any> => {
    const response = await apiClient.put(`/integration/period-mappings/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/integration/period-mappings/${id}`);
  },
  // Accepts EITHER a freshly chosen File OR an inbox_path string referring
  // to a file already uploaded to the tenant's staging inbox. Mirrors the
  // FCCS "File Browser → inbox → import" flow.
  bulkImport: async (input: File | { inboxPath: string }): Promise<any> => {
    const formData = new FormData();
    if (input instanceof File) {
      formData.append('file', input);
    } else {
      formData.append('inbox_path', input.inboxPath);
    }
    // Override the instance's default application/json so axios sets the
    // multipart boundary — otherwise the backend sees no `file`.
    const response = await apiClient.post('/integration/period-mappings/bulk-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  exportCsv: async (params?: any): Promise<Blob> => {
    const response = await apiClient.get('/integration/period-mappings/export', { params, responseType: 'blob' });
    return response.data;
  },
  autoGenerate: async (year: number, scenario?: string): Promise<any> => {
    const response = await apiClient.post('/integration/period-mappings/auto-generate', { year, scenario });
    return response.data;
  },
};

export const importTemplatesApi = {
  list: async (params?: any): Promise<any[]> => {
    const response = await apiClient.get('/integration/import-templates', { params });
    return response.data;
  },
  create: async (data: any): Promise<any> => {
    const response = await apiClient.post('/integration/import-templates', data);
    return response.data;
  },
  get: async (id: number): Promise<any> => {
    const response = await apiClient.get(`/integration/import-templates/${id}`);
    return response.data;
  },
  update: async (id: number, data: any): Promise<any> => {
    const response = await apiClient.put(`/integration/import-templates/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/integration/import-templates/${id}`);
  },
  duplicate: async (id: number, newName: string): Promise<any> => {
    const response = await apiClient.post(`/integration/import-templates/${id}/duplicate`, { name: newName });
    return response.data;
  },
  apply: async (id: number, sessionId: string): Promise<any> => {
    const response = await apiClient.post(`/integration/import-templates/${id}/apply`, { session_id: sessionId });
    return response.data;
  },
};

// --- Data Export API ----------------------------------------------------------

export const dataExportApi = {
  exportData: async (params: any): Promise<Blob> => {
    const response = await apiClient.post('/data-export/export', params, { responseType: 'blob' });
    return response.data;
  },
  getExportStatus: async (limit?: number): Promise<any[]> => {
    const response = await apiClient.get('/data-export/status', { params: limit != null ? { limit } : undefined });
    return response.data;
  },
  exportExchangeRates: async (params?: any): Promise<Blob> => {
    const response = await apiClient.post('/data-export/exchange-rates', params ?? {}, { responseType: 'blob' });
    return response.data;
  },
};

// --- Data Profiles API -------------------------------------------------------

export const dataProfilesApi = {
  list: async (params?: any): Promise<any[]> => {
    const response = await apiClient.get('/data-profiles', { params });
    return response.data;
  },
  create: async (data: any): Promise<any> => {
    const response = await apiClient.post('/data-profiles', data);
    return response.data;
  },
  get: async (id: number): Promise<any> => {
    const response = await apiClient.get(`/data-profiles/${id}`);
    return response.data;
  },
  update: async (id: number, data: any): Promise<any> => {
    const response = await apiClient.put(`/data-profiles/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/data-profiles/${id}`);
  },
  execute: async (id: number): Promise<any> => {
    const response = await apiClient.post(`/data-profiles/${id}/execute`);
    return response.data;
  },
  /** Clear Data: submit a saved profile as a background job. */
  run: async (id: number): Promise<ClearDataRunResult> => {
    const response = await apiClient.post(`/data-profiles/${id}/run`);
    return response.data;
  },
  /** Clear Data: submit a definition that hasn't been saved as a profile yet. */
  runClear: async (payload: {
    clear_definition: any;
    name?: string | null;
    profile_id?: number | null;
  }): Promise<ClearDataRunResult> => {
    const response = await apiClient.post('/data-profiles/clear/run', payload);
    return response.data;
  },
  /** Copy Data: submit a definition that hasn't been saved as a profile yet. */
  runCopy: async (payload: {
    copy_definition: any;
    name?: string | null;
    profile_id?: number | null;
  }): Promise<ClearDataRunResult> => {
    const response = await apiClient.post('/data-profiles/copy/run', payload);
    return response.data;
  },
};

export interface ClearDataRunResult {
  success: boolean;
  message: string;
  job_id: number;
  task_id: string;
  profile_id: number | null;
}

// --- Invalid Intersection API ------------------------------------------------

export const invalidIntersectionApi = {
  generateReport: async (params: any): Promise<any> => {
    const response = await apiClient.post('/data-load/invalid-intersection-report', params);
    return response.data;
  },
  clearInvalid: async (params: any): Promise<any> => {
    const response = await apiClient.post('/data-load/clear-invalid-intersections', params);
    return response.data;
  },
};

// --- Data Reconciliation API -------------------------------------------------

export const dataReconciliationApi = {
  balanceCheck: async (params: { period_id: number; entity_id?: number; scenario?: string }): Promise<any> => {
    const response = await apiClient.get('/data-reconciliation/balance-check', { params });
    return response.data;
  },
  mappedAccounts: async (params: { period_id: number; entity_id?: number }): Promise<any> => {
    const response = await apiClient.get('/data-reconciliation/mapped-accounts', { params });
    return response.data;
  },
  unmappedAccounts: async (params: { period_id: number; entity_id?: number }): Promise<any> => {
    const response = await apiClient.get('/data-reconciliation/unmapped-accounts', { params });
    return response.data;
  },
  icReconciliation: async (params: { period_id: number; scenario?: string }): Promise<any> => {
    const response = await apiClient.get('/data-reconciliation/ic-reconciliation', { params });
    return response.data;
  },
  openingBalance: async (params: { period_id: number; entity_id?: number; scenario?: string }): Promise<any> => {
    const response = await apiClient.get('/data-reconciliation/opening-balance', { params });
    return response.data;
  },
};

// ─── Calculation Manager rules (BRD §3.2.5, §5.4; PDF Ch. 21) ──────────────
// Backs the FCCS-style System View tree + rule editor.  Rules persist to
// the calc_rules / calc_rule_versions tables and can be validated, deployed
// and executed via these endpoints.

export interface CalcRuleResponseDTO {
  id: number;
  tenant_id: number;
  name: string;
  description?: string | null;
  comments?: string | null;
  kind: 'rule' | 'ruleset' | 'template' | 'formula' | 'script' | 'on_demand';
  status: 'draft' | 'validated' | 'deployed' | 'error' | 'disabled';
  hook_point:
  // Canonical 8 FCCS insertion points (fired automatically during Consolidate)
  | 'FCCS_10_After Opening Balance Carry Forward_LocalCurrency'
  | 'FCCS_20_Final_Calculations'
  | 'FCCS_25_Before_FX_Calcs'
  | 'FCCS_30_After Opening Balance Carry Forward'
  | 'FCCS_40_Final_Calculations'
  | 'FCCS_50_After Opening Balance Carry Forward'
  | 'FCCS_60_Final_Calculations'
  | 'FCCS_70_Partner Elimination'
  // Deprecated layer-relative aliases (legacy rows / single-currency 110-140)
  | 'after_obcf' | 'final_calc' | 'before_fx' | 'partner_elimination'
  | 'translation_overrides' | 'configurable_consolidation'
  | 'on_demand' | 'none';
  currency_layer: 'local' | 'translated' | 'consolidated' | 'none';
  // Multi-currency (FCCS_10-70) vs single-currency (FCCS_110-140) insertion point.
  currency_mode: 'multi' | 'single' | 'any';
  application: string;
  cube: string;
  is_system: boolean;
  is_active: boolean;
  sort_order: number;
  enable_notifications: boolean;
  // Auto Create Blocks (PDF Ch.2 §2) — per-rule No/Yes setting.
  auto_create_blocks: boolean;
  active_version_id?: number | null;
  active_version?: number | null;
  script: string;
  last_validated_at?: string | null;
  last_deployed_at?: string | null;
  // 3-state Oracle deploy lifecycle (US-R3.1 AC4).
  deploy_state: 'undeployed' | 'deployed' | 'changed';
  // Launch-surface hooks (US-R6.2).
  fires_post_data_load: boolean;
  fires_post_journal: boolean;
  owner?: string | null;
  last_modified_by?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CalcRuleValidationIssue {
  line: number;
  column: number;
  severity: 'error' | 'warning';
  message: string;
  code?: string | null;
}

export interface CalcRuleValidationResponseDTO {
  rule_id: number;
  is_valid: boolean;
  errors: CalcRuleValidationIssue[];
  warnings: CalcRuleValidationIssue[];
  validated_at: string;
}

export const calcRulesApi = {
  list: async (params?: {
    hook_point?: string;
    currency_layer?: string;
    kind?: string;
    cube?: string;
  }): Promise<CalcRuleResponseDTO[]> => {
    const r = await apiClient.get('/calc-rules', { params });
    return r.data;
  },
  byHook: async (hook_point: string, currency_layer: string): Promise<CalcRuleResponseDTO[]> => {
    const r = await apiClient.get('/calc-rules/by-hook', { params: { hook_point, currency_layer } });
    return r.data;
  },
  get: async (id: number): Promise<CalcRuleResponseDTO> => {
    const r = await apiClient.get(`/calc-rules/${id}`);
    return r.data;
  },
  create: async (payload: Partial<CalcRuleResponseDTO> & { name: string; script?: string }): Promise<CalcRuleResponseDTO> => {
    const r = await apiClient.post('/calc-rules', payload);
    return r.data;
  },
  update: async (id: number, payload: Partial<CalcRuleResponseDTO> & { script?: string }): Promise<CalcRuleResponseDTO> => {
    const r = await apiClient.put(`/calc-rules/${id}`, payload);
    return r.data;
  },
  remove: async (id: number): Promise<void> => {
    await apiClient.delete(`/calc-rules/${id}`);
  },
  validate: async (id: number): Promise<CalcRuleValidationResponseDTO> => {
    const r = await apiClient.post(`/calc-rules/${id}/validate`);
    return r.data;
  },
  deploy: async (id: number): Promise<{ rule_id: number; version: number; status: string; deployed_at: string; message: string }> => {
    const r = await apiClient.post(`/calc-rules/${id}/deploy`);
    return r.data;
  },
  run: async (id: number, prompts?: Record<string, string>) =>
    (await apiClient.post(`/calc-rules/${id}/run`, prompts ? { prompts } : {})).data,
  seed: async (): Promise<CalcRuleResponseDTO[]> => {
    const r = await apiClient.post('/calc-rules/seed');
    return r.data;
  },
  versions: async (id: number) => (await apiClient.get(`/calc-rules/${id}/versions`)).data,

  // ── Insertion-point hook bindings (US-R2.2 / R20) ─────────────────────────
  hookBindings: async (hook_point?: string): Promise<CalcRuleHookBindingDTO[]> =>
    (await apiClient.get('/calc-rules/hook-bindings', { params: hook_point ? { hook_point } : undefined })).data,
  upsertHookBinding: async (ruleId: number, payload: {
    hook_point: string; enabled?: boolean; auto_calc_dim?: boolean; sequence?: number;
  }): Promise<CalcRuleHookBindingDTO> =>
    (await apiClient.put(`/calc-rules/${ruleId}/hook-binding`, payload)).data,
  deleteHookBinding: async (ruleId: number): Promise<void> => {
    await apiClient.delete(`/calc-rules/${ruleId}/hook-binding`);
  },

  // ── Ruleset members (US-R3.1) ─────────────────────────────────────────────
  members: async (rulesetId: number): Promise<CalcRulesetMemberDTO[]> =>
    (await apiClient.get(`/calc-rules/${rulesetId}/members`)).data,
  setMembers: async (rulesetId: number, member_rule_ids: number[]): Promise<CalcRulesetMemberDTO[]> =>
    (await apiClient.put(`/calc-rules/${rulesetId}/members`, { member_rule_ids })).data,

  // ── Template composition (US-R3.2) ────────────────────────────────────────
  templateRefs: async (ruleId: number): Promise<CalcRuleTemplateRefDTO[]> =>
    (await apiClient.get(`/calc-rules/${ruleId}/templates`)).data,
  addTemplateRef: async (ruleId: number, payload: { template_rule_id: number; parameters?: Record<string, string>; sequence?: number }): Promise<CalcRuleTemplateRefDTO> =>
    (await apiClient.post(`/calc-rules/${ruleId}/templates`, payload)).data,
  removeTemplateRef: async (ruleId: number, refId: number): Promise<void> => {
    await apiClient.delete(`/calc-rules/${ruleId}/templates/${refId}`);
  },
};

export interface CalcRuleHookBindingDTO {
  id: number;
  calc_rule_id: number;
  hook_point: string;
  enabled: boolean;
  auto_calc_dim: boolean;
  sequence: number;
  rule_name: string | null;
  rule_status: string | null;
}

export interface CalcRulesetMemberDTO {
  id: number;
  member_rule_id: number;
  member_rule_name: string | null;
  sequence: number;
}

export interface CalcRuleTemplateRefDTO {
  id: number;
  host_rule_id: number;
  template_rule_id: number;
  template_rule_name: string | null;
  parameters: Record<string, string>;
  sequence: number;
}

// ── Designer + debugger (US-R4.1 / US-R4.3) ─────────────────────────────────

export interface DesignerBlockDTO {
  type: 'condition' | 'member_range' | 'formula';
  condition?: string;
  dimension?: string;
  members?: string[];
  target?: Record<string, string>;
  expression?: string;
  children?: DesignerBlockDTO[];
  else_children?: DesignerBlockDTO[];
}

export interface DebugStepDTO {
  session_id: number;
  cursor_statement_index: number;
  cursor_entity_id: number | null;
  entity_label: string | null;
  statement: string | null;
  before: Record<string, string>;
  after: Record<string, string>;
  done: boolean;
  paused_on: string | null;
}

export const calcRuleDesignerApi = {
  get: async (ruleId: number): Promise<{ rule_id: number; version: number; script: string; blocks: DesignerBlockDTO[] | null }> =>
    (await apiClient.get(`/calc-rules/${ruleId}/designer`)).data,
  save: async (ruleId: number, blocks: DesignerBlockDTO[]): Promise<{ rule_id: number; version: number; script: string; blocks: DesignerBlockDTO[] }> =>
    (await apiClient.put(`/calc-rules/${ruleId}/designer`, { blocks })).data,
};

export const calcRuleDebugApi = {
  start: async (ruleId: number, rtps: Record<string, string>, breakpoints: { expr: string; enabled: boolean }[]): Promise<DebugStepDTO> =>
    (await apiClient.post(`/calc-rules/${ruleId}/debug/start`, { rtps, breakpoints })).data,
  step: async (sessionId: number): Promise<DebugStepDTO> =>
    (await apiClient.post(`/calc-rules/debug/${sessionId}/step`)).data,
  resume: async (sessionId: number): Promise<DebugStepDTO> =>
    (await apiClient.post(`/calc-rules/debug/${sessionId}/resume`)).data,
  setBreakpoints: async (sessionId: number, breakpoints: { expr: string; enabled: boolean }[]) =>
    (await apiClient.put(`/calc-rules/debug/${sessionId}/breakpoints`, { breakpoints })).data,
  values: async (sessionId: number): Promise<DebugStepDTO> =>
    (await apiClient.get(`/calc-rules/debug/${sessionId}/values`)).data,
  end: async (sessionId: number) =>
    (await apiClient.post(`/calc-rules/debug/${sessionId}/end`)).data,
  active: async (ruleId: number): Promise<{ active: boolean; session_id: number | null }> =>
    (await apiClient.get(`/calc-rules/${ruleId}/debug/active`)).data,
};

// ─── Consolidation Settings (FCCS Ch. 2/7 — Skip-Translation) ──────────────

export interface ConsolidationSettingsDTO {
  skip_translation_when_currency_matches: boolean;
  // "Disable To-Date View Calculations" (PDF Ch. 5 §4).
  disable_to_date_view_calc: boolean;
  // Configurable-rules finance sign-off (read-only here; set via mcrSignoff).
  mcr_rules_signed_off: boolean;
  mcr_signed_off_by: number | null;
  mcr_signed_off_at: string | null;
  mcr_signoff_note: string | null;
  /**
   * When true, a Consolidate run also runs every DEPLOYED Manage-Consolidation
   * rule-set (pipeline step 11) instead of leaving them to their own Launch.
   * Changes what a consolidation POSTS — see the "Deployed rule-sets" card on
   * the Consolidation Logic tab, which is where it is turned on and off.
   */
  mcr_rules_on_consolidate: boolean;
  // Default Translation Methods (FCCS Manage Defaults). The consolidation
  // engine applies these to accounts with no explicit Exchange Rate Type.
  default_balance_translation_method: 'Periodic' | 'YTD';
  default_balance_rate_account: 'FX Rates - Average' | 'FX Rates - Ending';
  default_flow_translation_method: 'Periodic' | 'YTD';
  default_flow_rate_account: 'FX Rates - Average' | 'FX Rates - Ending';
  // CTA Account option (FCCS Consolidation → Enable Features): where the
  // translation adjustment is collected — FCCS_CTA (Balance Sheet) or
  // FCCS_CICTA (Comprehensive Income). Fixed once translated FX data exists.
  cta_account_option: CtaAccountOption;
  local_gaap_cta_option: LocalGaapCtaOption;
}

export type CtaAccountOption = 'balance_sheet' | 'comprehensive_income';
export type LocalGaapCtaOption = 'cta' | 'cicta';

export interface CtaOptionLockDTO {
  locked: boolean;
  reason: string | null;
}

/** Result of ensuring the CICTA option accounts (FCCS_CICTA, Total OCI, FCCS_OR OBFXCICTA, …). */
export interface CictaAccountsReportDTO {
  created: string[];
  existing: string[];
  warnings: string[];
}

export const consolidationSettingsApi = {
  get: async (): Promise<ConsolidationSettingsDTO> =>
    (await apiClient.get<ConsolidationSettingsDTO>('/consolidation-settings')).data,
  update: async (payload: Partial<ConsolidationSettingsDTO>): Promise<ConsolidationSettingsDTO> =>
    (await apiClient.put<ConsolidationSettingsDTO>('/consolidation-settings', payload)).data,
  /** Whether the CTA Account option can still be changed (settings card only). */
  ctaOptionLock: async (): Promise<CtaOptionLockDTO> =>
    (await apiClient.get<CtaOptionLockDTO>('/consolidation-settings/cta-option-lock')).data,
  /** Create any missing CICTA accounts (Comprehensive Income option only) and report the rest. */
  ensureCictaAccounts: async (): Promise<CictaAccountsReportDTO> =>
    (await apiClient.post<CictaAccountsReportDTO>('/consolidation-settings/cicta-accounts/ensure')).data,
  /**
   * Record (admin) the configurable-rules finance sign-off. When signed off,
   * the engine stops flagging CONFIG_CONSOL postings as unverified and the
   * disclaimers turn green. Pass signed_off:false to re-arm the provisional state.
   */
  mcrSignoff: async (signedOff: boolean, note?: string): Promise<ConsolidationSettingsDTO> =>
    (await apiClient.put<ConsolidationSettingsDTO>('/consolidation-settings/mcr-signoff', {
      signed_off: signedOff,
      note: note ?? null,
    })).data,
};

// ─── Translation Override Rules (FCCS Ch. 21) ─────────────────────────────

export type TranslationMethodDTO = 'periodic' | 'ytd';
export type RateSourceDTO = 'rate_account' | 'specific_amount';
export type ProcessingOptionDTO = 'replace' | 'add' | 'subtract';
export type TransOverrideStatusDTO = 'draft' | 'changed' | 'deployed' | 'undeployed';

export interface DimensionSelectionDTO {
  members?: string[];
  functions?: string[];
}

export interface TranslationOverrideRuleDTO {
  id: number;
  tenant_id: number;
  name: string;
  description: string | null;
  cube: string;
  application: string;
  translation_method: TranslationMethodDTO;
  rate_source: RateSourceDTO;
  rate_account: string | null;
  rate_value: number | null;
  rate_scenario: string | null;
  rate_year: string | null;
  rate_period: string | null;
  source_pov: Record<string, DimensionSelectionDTO>;
  exclusions: Record<string, DimensionSelectionDTO> | null;
  redirect_pov: Record<string, DimensionSelectionDTO> | null;
  reporting_currencies: string[] | null;
  exclude_reporting_currencies: boolean;
  processing_option: ProcessingOptionDTO;
  status: TransOverrideStatusDTO;
  is_deployed: boolean;
  is_active: boolean;
  sort_order: number;
  last_deployed_at: string | null;
  last_modified_by: string | null;
  owner: string | null;
  created_at: string;
  updated_at: string;
}

export type TranslationOverrideRuleCreate = Omit<
  TranslationOverrideRuleDTO,
  'id' | 'tenant_id' | 'status' | 'is_deployed' | 'last_deployed_at'
  | 'last_modified_by' | 'owner' | 'created_at' | 'updated_at'
>;

export const translationOverrideRulesApi = {
  list: async (params?: {
    cube?: string;
    view?: 'deployed' | 'to_be_deployed';
    search?: string;
  }): Promise<TranslationOverrideRuleDTO[]> => {
    const r = await apiClient.get('/translation-override-rules', { params });
    return r.data;
  },
  get: async (id: number): Promise<TranslationOverrideRuleDTO> => {
    const r = await apiClient.get(`/translation-override-rules/${id}`);
    return r.data;
  },
  create: async (
    payload: Partial<TranslationOverrideRuleCreate> & { name: string },
  ): Promise<TranslationOverrideRuleDTO> => {
    const r = await apiClient.post('/translation-override-rules', payload);
    return r.data;
  },
  update: async (
    id: number,
    payload: Partial<TranslationOverrideRuleCreate>,
  ): Promise<TranslationOverrideRuleDTO> => {
    const r = await apiClient.put(`/translation-override-rules/${id}`, payload);
    return r.data;
  },
  remove: async (id: number): Promise<void> => {
    await apiClient.delete(`/translation-override-rules/${id}`);
  },
  deploy: async (rule_ids: number[], cube = 'Consol'): Promise<{
    deployed: number[]; undeployed: number[]; deployed_at: string; message: string;
  }> => {
    const r = await apiClient.post('/translation-override-rules/deploy', {
      rule_ids, cube,
    });
    return r.data;
  },
  seed: async (): Promise<TranslationOverrideRuleDTO[]> => {
    const r = await apiClient.post('/translation-override-rules/seed');
    return r.data;
  },
  // FCCS Ch.6 referential integrity — flag rules referencing accounts / rate
  // accounts that no longer exist (already-dangling rows). Reports only.
  orphans: async (): Promise<{ count: number; orphans: OrphanOverrideRef[] }> => {
    const r = await apiClient.get('/translation-override-rules/orphans');
    return r.data;
  },
};

export interface OrphanOverrideRef {
  rule_id: number;
  rule_name: string;
  status: string;
  is_deployed: boolean;
  missing_accounts: string[];
  missing_rate_account: string | null;
}

// ─── Translation Defaults (per account-type) — FCCS "Manage Defaults" ─────

export interface TranslationDefaultDTO {
  id: number;
  tenant_id: number;
  name: string;
  account_type: string | null;     // asset / liability / equity / revenue / expense
  is_balance_sheet: boolean | null;
  rate_type: string;               // closing / average / historical
  translation_method: string;      // ytd / periodic
  translation_diff_account_id: number | null;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const translationDefaultsApi = {
  list: async (): Promise<TranslationDefaultDTO[]> =>
    (await apiClient.get('/currency-translation-rules')).data,
  create: async (data: Partial<TranslationDefaultDTO>): Promise<TranslationDefaultDTO> =>
    (await apiClient.post('/currency-translation-rules', data)).data,
  update: async (id: number, data: Partial<TranslationDefaultDTO>): Promise<TranslationDefaultDTO> =>
    (await apiClient.put(`/currency-translation-rules/${id}`, data)).data,
  remove: async (id: number): Promise<void> => {
    await apiClient.delete(`/currency-translation-rules/${id}`);
  },
  seed: async (): Promise<{ status: string }> =>
    (await apiClient.post('/currency-translation-rules/seed')).data,
};

// ─── Rate Accounts — FCCS "Manage Rate Accounts" ─────────────────────────

export type RateAccountScopeDTO = 'global' | 'local';

export interface RateAccountDTO {
  id: number;
  tenant_id: number;
  name: string;
  description: string | null;
  /** Alias captured at creation — used in the Default Alias table. */
  alias: string | null;
  scope: RateAccountScopeDTO;
  cube: string;
  is_system: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const rateAccountsApi = {
  list: async (params?: { cube?: string; scope?: RateAccountScopeDTO }):
    Promise<RateAccountDTO[]> =>
    (await apiClient.get('/rate-accounts', { params })).data,
  create: async (data: {
    name: string; description?: string | null; alias?: string | null;
    scope?: RateAccountScopeDTO; cube?: string; is_active?: boolean;
  }): Promise<RateAccountDTO> =>
    (await apiClient.post('/rate-accounts', data)).data,
  update: async (id: number, data: Partial<{
    name: string; description: string | null; alias: string | null;
    scope: RateAccountScopeDTO; is_active: boolean;
  }>): Promise<RateAccountDTO> =>
    (await apiClient.put(`/rate-accounts/${id}`, data)).data,
  remove: async (id: number): Promise<void> => {
    await apiClient.delete(`/rate-accounts/${id}`);
  },
  seed: async (): Promise<RateAccountDTO[]> =>
    (await apiClient.post('/rate-accounts/seed')).data,
  /** Apply pending rate-account changes by running a Refresh Database job. */
  refreshDatabase: async (): Promise<{ job_id: number | null; job_name: string; status: string }> =>
    (await apiClient.post('/rate-accounts/refresh-database')).data,
};

// --- Custom Scripts API ------------------------------------------------------

export const customScriptsApi = {
  list: async (params?: { script_type?: string; language?: string }): Promise<any[]> => {
    const response = await apiClient.get('/integration/scripts', { params });
    return response.data;
  },
  get: async (id: number): Promise<any> => {
    const response = await apiClient.get(`/integration/scripts/${id}`);
    return response.data;
  },
  create: async (data: any): Promise<any> => {
    const response = await apiClient.post('/integration/scripts', data);
    return response.data;
  },
  update: async (id: number, data: any): Promise<any> => {
    const response = await apiClient.put(`/integration/scripts/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/integration/scripts/${id}`);
  },
  execute: async (id: number): Promise<any> => {
    const response = await apiClient.post(`/integration/scripts/${id}/execute`);
    return response.data;
  },
  history: async (id: number): Promise<any> => {
    const response = await apiClient.get(`/integration/scripts/${id}/history`);
    return response.data;
  },
};

// --- Query Definitions API ---------------------------------------------------

export const queryDefinitionsApi = {
  list: async (params?: { query_type?: string; is_public?: boolean }): Promise<any[]> => {
    const response = await apiClient.get('/integration/queries', { params });
    return response.data;
  },
  get: async (id: number): Promise<any> => {
    const response = await apiClient.get(`/integration/queries/${id}`);
    return response.data;
  },
  create: async (data: any): Promise<any> => {
    const response = await apiClient.post('/integration/queries', data);
    return response.data;
  },
  update: async (id: number, data: any): Promise<any> => {
    const response = await apiClient.put(`/integration/queries/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/integration/queries/${id}`);
  },
  execute: async (id: number): Promise<any> => {
    const response = await apiClient.post(`/integration/queries/${id}/execute`);
    return response.data;
  },
};

// --- Integration Records API -------------------------------------------------

/**
 * Poll a queued Data Integration job to completion and return its payload.
 *
 * `GET /api/v1/jobs/{id}` carries the run's `result_summary` — the exact object
 * the synchronous `POST /run` used to return — so every existing caller of
 * `integrationRecordsApi.run` keeps working unchanged.
 *
 * Each poll is a short request, so no single call is exposed to the Cloudflare
 * edge timeout that killed the old long-running POST at ~125s.
 */
/** Jobs-console statuses after which a job will not change again. */
export const JOB_TERMINAL_STATUSES = [
  'completed', 'completed_with_warnings', 'completed_with_errors',
  'completed_with_threshold', 'failed', 'cancelled',
];

/**
 * Poll `GET /api/v1/jobs/{id}` every `intervalMs` until the job reaches a
 * terminal status and return the job row. Used by every launch that was moved
 * off the request path (Rules-card Consolidate/Translate, consolidation run):
 * each poll is a short request, so a 10–15 minute run is never exposed to the
 * 30 s axios default or the Cloudflare edge timeout.
 *
 * Unlike `pollIntegrationJob` this never throws on `failed` — callers read the
 * status and `error_message` themselves so they can render the failure inline.
 */
export async function pollJobUntilTerminal(
  jobId: number,
  opts?: { intervalMs?: number; timeoutMs?: number; onProgress?: (job: any) => void; signal?: AbortSignal },
): Promise<any> {
  const interval = opts?.intervalMs ?? 2000;
  // 60 min ceiling — longer than any observed consolidation, but bounded so a
  // worker that died without writing a terminal status cannot hang the UI.
  const deadline = Date.now() + (opts?.timeoutMs ?? 3600000);
  for (;;) {
    if (opts?.signal?.aborted) throw new Error('Polling cancelled');
    const { data: job } = await apiClient.get(`/jobs/${jobId}`);
    opts?.onProgress?.(job);
    if (JOB_TERMINAL_STATUSES.includes(job?.status)) return job;
    if (Date.now() > deadline) {
      throw new Error(
        `Job ${jobId} is still ${job?.status ?? 'queued'} after 60 minutes — check the Jobs console.`,
      );
    }
    await new Promise((r) => setTimeout(r, interval));
  }
}

const INTEGRATION_JOB_TERMINAL = ['completed', 'completed_with_errors', 'failed', 'cancelled'];

export async function pollIntegrationJob(
  jobId: number | undefined,
  opts?: { intervalMs?: number; timeoutMs?: number; onProgress?: (job: any) => void },
): Promise<any> {
  if (jobId == null) throw new Error('Integration run was not queued (no job id returned).');
  const interval = opts?.intervalMs ?? 2000;
  // 30 min ceiling: longer than any observed load, but bounded so a worker that
  // died without writing a terminal status cannot hang the UI forever.
  const deadline = Date.now() + (opts?.timeoutMs ?? 1800000);

  for (;;) {
    const { data: job } = await apiClient.get(`/jobs/${jobId}`);
    opts?.onProgress?.(job);
    if (INTEGRATION_JOB_TERMINAL.includes(job?.status)) {
      if (job.status === 'failed' || job.status === 'cancelled') {
        throw new Error(job?.error_message || `Integration run ${job.status}.`);
      }
      // completed / completed_with_errors both carry a real result: a run whose
      // Validate stage warned is a *result* to display, not a client error.
      return job.result_summary ?? { detail: 'Integration executed', result: {} };
    }
    if (Date.now() > deadline) {
      throw new Error(
        `Integration run is still ${job?.status ?? 'queued'} after 30 minutes — check the Jobs console (job ${jobId}).`,
      );
    }
    await new Promise((r) => setTimeout(r, interval));
  }
}

export const integrationRecordsApi = {
  list: async (params?: {
    search?: string;
    period?: string;
    sort_by?: string;
    sort_dir?: string;
    type?: string;
  }): Promise<any[]> => {
    const response = await apiClient.get('/integration/records', { params });
    return response.data;
  },
  get: async (id: number): Promise<any> => {
    const response = await apiClient.get(`/integration/records/${id}`);
    return response.data;
  },
  create: async (data: any): Promise<any> => {
    const response = await apiClient.post('/integration/records', data);
    return response.data;
  },
  update: async (id: number, data: any): Promise<any> => {
    const response = await apiClient.put(`/integration/records/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/integration/records/${id}`);
  },
  run: async (id: number, params?: { period?: string; period_id?: number; end_period_id?: number; import_mode?: string; export_mode?: string; file_name?: string }): Promise<any> => {
    // period_id (start) + end_period_id (range) + import_mode + export_mode drive
    // the load POV on the backend (query params). end_period_id enables Oracle §14
    // multi-period file-name-suffix loads. file_name is the run-time selected file
    // (Oracle §17.1.1 step 5) — used when no suffix type is configured.
    const qs = new URLSearchParams();
    if (params?.period_id != null) qs.set('period_id', String(params.period_id));
    if (params?.end_period_id != null) qs.set('end_period_id', String(params.end_period_id));
    if (params?.import_mode) qs.set('import_mode', params.import_mode);
    if (params?.export_mode) qs.set('export_mode', params.export_mode);
    if (params?.file_name) qs.set('file_name', params.file_name);
    const query = qs.toString() ? `?${qs.toString()}` : '';
    // A real Import → Map → Validate → Export pipeline runs for MINUTES (measured
    // 1m09s–2m45s for a 600-row YTD load). Raising the axios timeout was not
    // enough: the app is served through Cloudflare, whose EDGE timeout sits in
    // front of nginx and cannot be raised from the origin, so runs were killed
    // at ~125s with HTTP 499 no matter what this value said — while still
    // pinning one of only two uvicorn workers for the full 125s.
    //
    // The run is therefore queued (HTTP 202 → job id) and polled. The resolved
    // value is the job's `result_summary`, which is byte-for-byte the payload
    // the synchronous POST /run used to return, so callers need no changes.
    const response = await apiClient.post(`/integration/records/${id}/run-async${query}`, undefined);
    return await pollIntegrationJob(response.data?.job_id);
  },
  batchRun: async (payload: { record_ids: number[]; period_id?: number; import_mode?: string; export_mode?: string }): Promise<any> => {
    // Run several integrations together for one period. Queued per record so a
    // batch is not one giant request behind the Cloudflare edge timeout (see
    // `run` above); each record gets its own job and per-record failures stay
    // isolated exactly as the synchronous batch endpoint isolated them.
    const response = await apiClient.post(`/integration/records/batch-run-async`, payload);
    const queued: Array<{ record_id: number; job_id: number }> = response.data?.queued || [];
    const skipped = response.data?.skipped || [];
    const results = await Promise.all(
      queued.map(async (q) => {
        try {
          return { record_id: q.record_id, ...(await pollIntegrationJob(q.job_id)) };
        } catch (e: any) {
          return { record_id: q.record_id, error: e?.message || String(e) };
        }
      }),
    );
    return { results, skipped, queued_count: queued.length, skipped_count: skipped.length };
  },
  copy: async (id: number, opts: { name: string; location_id?: number | null; import_format_id?: number | null }): Promise<any> => {
    const response = await apiClient.post(`/integration/records/${id}/copy`, opts);
    return response.data;
  },
  getProcessDetails: async (id: number): Promise<any[]> => {
    const response = await apiClient.get(`/integration/records/${id}/process-details`);
    return response.data;
  },
  // Process Details monitor across ALL integrations (Oracle FCCS §16.1). Pass a
  // record_id to filter to a single integration; omit it for everything.
  getAllProcessDetails: async (recordId?: number | null): Promise<any[]> => {
    const response = await apiClient.get('/integration/records/process-details', {
      params: recordId != null ? { record_id: recordId } : undefined,
    });
    return response.data;
  },
  // Oracle FDMEE-style process log for one run (Process Details → Show Log).
  getProcessRunLog: async (processId: number): Promise<{ process_id: number; file_name: string; content: string }> => {
    const response = await apiClient.get(`/integration/records/process-runs/${processId}/log`);
    return response.data;
  },
  // Workbench POV + session from the integration's last successful run (FCCS §16).
  getWorkbenchContext: async (id: number, sessionId?: string): Promise<any> => {
    const response = await apiClient.get(`/integration/records/${id}/workbench-context`, {
      params: sessionId ? { session_id: sessionId } : {},
    });
    return response.data;
  },
  // Per-stage Workbench execution (Oracle interactive bar). Each returns
  // { status, steps:{import,validate,export,check}, ... }.
  runImportStage: async (
    id: number,
    periodId?: number | null,
    importMode?: string,
    fileName?: string | null,
  ): Promise<any> => {
    const params: Record<string, any> = {};
    if (periodId != null) params.period_id = periodId;
    if (importMode) params.import_mode = importMode;
    if (fileName) params.file_name = fileName;
    const response = await apiClient.post(`/integration/records/${id}/run/import`, null, { params });
    return response.data;
  },
  runValidateStage: async (id: number, sessionId: string): Promise<any> => {
    const response = await apiClient.post(`/integration/records/${id}/run/validate`, null, { params: { session_id: sessionId } });
    return response.data;
  },
  // Validate-stage error summary — one row per (dimension, unmapped source member)
  // with the failing row count + sample row numbers. Drives the Workbench
  // "Validation Errors" panel and its Fix Maps flow.
  getValidateErrors: async (recordId: number | string, sessionId: string): Promise<{ errors: Array<{ dimension: string; source_member: string; row_count: number; sample_row_numbers: number[]; error_type?: string; description?: string; fixable?: boolean }> }> => {
    const response = await apiClient.get(`/integration/records/${recordId}/validate-errors`, { params: { session_id: sessionId } });
    return response.data;
  },
  runExportStage: async (id: number, sessionId: string, exportMode?: string, confirmUnbalanced?: boolean): Promise<any> => {
    const params: Record<string, any> = { session_id: sessionId };
    if (exportMode) params.export_mode = exportMode;
    if (confirmUnbalanced) params.confirm_unbalanced = true;
    const response = await apiClient.post(`/integration/records/${id}/run/export`, null, { params });
    return response.data;
  },
  // Pre-Export (balance/threshold) Check — the source TB must sum to within the
  // configured band before Export. Read-only; returns pass | needs_confirmation | disabled.
  runPreExportCheckStage: async (id: number, sessionId: string): Promise<any> => {
    const response = await apiClient.post(`/integration/records/${id}/run/pre-export-check`, null, { params: { session_id: sessionId } });
    return response.data;
  },
  runCheckStage: async (id: number, sessionId: string): Promise<any> => {
    const response = await apiClient.post(`/integration/records/${id}/run/check`, null, { params: { session_id: sessionId } });
    return response.data;
  },
  getLocations: async (): Promise<any[]> => {
    const response = await apiClient.get('/integration/records/locations');
    return response.data;
  },
  getMemberMappings: async (integrationId: number, params?: {
    dimension?: string;
    map_type?: string;
    source?: string;
    source_op?: string;
    target?: string;
    target_op?: string;
    processing_order?: string;
    order_op?: string;
    page?: number;
    page_size?: number;
  }): Promise<any> => {
    const response = await apiClient.get(`/integration/records/${integrationId}/member-mappings`, { params });
    return response.data;
  },
  createMemberMapping: async (integrationId: number, data: any): Promise<any> => {
    const response = await apiClient.post(`/integration/records/${integrationId}/member-mappings`, data);
    return response.data;
  },
  // Bulk "Add Member Mappings" — create many in one atomic batch (preview → Submit all).
  bulkCreateMemberMappings: async (integrationId: number, mappings: any[]): Promise<{ created: any[]; count: number }> => {
    const response = await apiClient.post(`/integration/records/${integrationId}/member-mappings/bulk`, { mappings });
    return response.data;
  },
  updateMemberMapping: async (integrationId: number, mappingId: number, data: any): Promise<any> => {
    const response = await apiClient.put(`/integration/records/${integrationId}/member-mappings/${mappingId}`, data);
    return response.data;
  },
  deleteMemberMapping: async (integrationId: number, mappingId: number): Promise<void> => {
    await apiClient.delete(`/integration/records/${integrationId}/member-mappings/${mappingId}`);
  },
  // Export member mappings (CSV or Excel) to the File Browser outbox (Oracle FCCS
  // pattern). Returns the saved-file descriptor; the user then downloads it from
  // the File Browser.
  exportMemberMappings: async (
    integrationId: number,
    scope: 'current' | 'all',
    dimension?: string,
    format: 'csv' | 'excel' = 'csv',
    dimensions?: string[],
    filename?: string,
  ): Promise<{ destination: string; path: string; name: string; size: number }> => {
    const params: Record<string, string> = { scope, format };
    if (scope === 'current' && dimension) params.dimension = dimension;
    // The integration's dimensions → one Excel sheet each (incl. empty ones).
    if (format === 'excel' && scope === 'all' && dimensions && dimensions.length) {
      params.dimensions = dimensions.join(',');
    }
    if (filename && filename.trim()) params.filename = filename.trim();
    const response = await apiClient.get(
      `/integration/records/${integrationId}/member-mappings/export`,
      { params },
    );
    return response.data;
  },
  // Oracle "Restore Mappings" — revert to the snapshot taken before the last
  // bulk import.
  restoreMemberMappings: async (
    integrationId: number,
  ): Promise<{ restored: number; message?: string; taken_at?: string }> => {
    const response = await apiClient.post(
      `/integration/records/${integrationId}/member-mappings/restore`,
    );
    return response.data;
  },
  // Download an empty Excel template (one sheet per standard FCCS dimension).
  // Direct download (destination=download) — the template is a blank to fill in.
  downloadMemberMappingTemplate: async (integrationId: number, dimensions?: string[]): Promise<void> => {
    const params: Record<string, string> = { template: 'true', format: 'excel', destination: 'download' };
    if (dimensions && dimensions.length) params.dimensions = dimensions.join(',');
    const response = await apiClient.get(
      `/integration/records/${integrationId}/member-mappings/export`,
      { params, responseType: 'blob' },
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `member_mappings_template.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
  // Import member mappings from a pipe-delimited CSV. Returns the per-row summary.
  importMemberMappings: async (
    integrationId: number,
    file: File,
    scope: 'current' | 'all',
    dimension?: string,
    mode: 'merge' | 'replace' = 'merge',
  ): Promise<{
    imported: number;
    skipped: number;
    rejected: number;
    location_scope_warning: string | null;
    errors: Array<{ row: number; source: string; message: string }>;
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('scope', scope);
    if (scope === 'current' && dimension) formData.append('dimension', dimension);
    formData.append('mode', mode);
    const response = await apiClient.post(
      `/integration/records/${integrationId}/member-mappings/import`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },
};

// --- Pipelines API -----------------------------------------------------------

export const pipelinesApi = {
  list: async (params?: { search?: string; is_active?: boolean }): Promise<any[]> => {
    const response = await apiClient.get('/integration/pipelines', { params });
    return response.data;
  },
  get: async (id: number): Promise<any> => {
    const response = await apiClient.get(`/integration/pipelines/${id}`);
    return response.data;
  },
  create: async (data: any): Promise<any> => {
    const response = await apiClient.post('/integration/pipelines', data);
    return response.data;
  },
  update: async (id: number, data: any): Promise<any> => {
    const response = await apiClient.put(`/integration/pipelines/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/integration/pipelines/${id}`);
  },
  execute: async (id: number): Promise<any> => {
    const response = await apiClient.post(`/integration/pipelines/${id}/execute`);
    return response.data;
  },
};

// --- Integration Locations API -----------------------------------------------

export const integrationLocationsApi = {
  list: async (params?: any): Promise<any[]> => {
    const response = await apiClient.get('/integration/locations', { params });
    return response.data;
  },
  get: async (id: number): Promise<any> => {
    const response = await apiClient.get(`/integration/locations/${id}`);
    return response.data;
  },
  create: async (data: any): Promise<any> => {
    const response = await apiClient.post('/integration/locations', data);
    return response.data;
  },
  update: async (id: number, data: any): Promise<any> => {
    const response = await apiClient.put(`/integration/locations/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/integration/locations/${id}`);
  },
};

// --- Check Groups (Rule + Entity) API ----------------------------------------
// Backs Oracle FCCS Data Integration → Check Rule Group / Check Entity Group.
// Server: backend/app/routers/check_groups.py
//   prefix /integration/check-groups, mounted under /api/v1.

export interface CheckRuleGroup {
  id: number;
  tenant_id: number;
  name: string;
  description: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface CheckRuleItem {
  id: number;
  group_id: number;
  display_value: string | null;
  description: string | null;
  rule_name: string | null;
  rule_text: string | null;
  category: string | null;
  rule_logic: string | null;
  sequence: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface CheckRuleGroupDetail extends CheckRuleGroup {
  items: CheckRuleItem[];
}

export interface CheckEntityGroup {
  id: number;
  tenant_id: number;
  name: string;
  description: string | null;
  consolidation_value: string | null;
  consolidation_member: string | null;
  entity_input_member: string | null;
  entity_ids: number[] | null;
  created_at: string | null;
  updated_at: string | null;
}

export const checkGroupsApi = {
  // ── Rule groups ──────────────────────────────────────────────
  listRules: async (params?: { search?: string }): Promise<{ items: CheckRuleGroup[]; total: number }> => {
    const response = await apiClient.get('/integration/check-groups/rules', { params });
    return response.data;
  },
  createRule: async (data: { name: string; description?: string | null }): Promise<CheckRuleGroup> => {
    const response = await apiClient.post('/integration/check-groups/rules', data);
    return response.data;
  },
  getRule: async (id: number): Promise<CheckRuleGroupDetail> => {
    const response = await apiClient.get(`/integration/check-groups/rules/${id}`);
    return response.data;
  },
  updateRule: async (id: number, data: Partial<{ name: string; description: string | null }>): Promise<CheckRuleGroup> => {
    const response = await apiClient.put(`/integration/check-groups/rules/${id}`, data);
    return response.data;
  },
  deleteRule: async (id: number): Promise<void> => {
    await apiClient.delete(`/integration/check-groups/rules/${id}`);
  },
  addRuleItem: async (id: number, data: Partial<CheckRuleItem>): Promise<CheckRuleItem> => {
    const response = await apiClient.post(`/integration/check-groups/rules/${id}/items`, data);
    return response.data;
  },
  updateRuleItem: async (id: number, itemId: number, data: Partial<CheckRuleItem>): Promise<CheckRuleItem> => {
    const response = await apiClient.put(`/integration/check-groups/rules/${id}/items/${itemId}`, data);
    return response.data;
  },
  deleteRuleItem: async (id: number, itemId: number): Promise<void> => {
    await apiClient.delete(`/integration/check-groups/rules/${id}/items/${itemId}`);
  },
  // ── Entity groups ───────────────────────────────────────────
  listEntities: async (params?: { search?: string }): Promise<{ items: CheckEntityGroup[]; total: number }> => {
    const response = await apiClient.get('/integration/check-groups/entities', { params });
    return response.data;
  },
  createEntity: async (data: Partial<CheckEntityGroup>): Promise<CheckEntityGroup> => {
    const response = await apiClient.post('/integration/check-groups/entities', data);
    return response.data;
  },
  getEntity: async (id: number): Promise<CheckEntityGroup> => {
    const response = await apiClient.get(`/integration/check-groups/entities/${id}`);
    return response.data;
  },
  updateEntity: async (id: number, data: Partial<CheckEntityGroup>): Promise<CheckEntityGroup> => {
    const response = await apiClient.put(`/integration/check-groups/entities/${id}`, data);
    return response.data;
  },
  deleteEntity: async (id: number): Promise<void> => {
    await apiClient.delete(`/integration/check-groups/entities/${id}`);
  },
};

// --- Category Mappings API ---------------------------------------------------

export const categoryMappingsApi = {
  list: async (params?: { mapping_level?: string; search?: string }): Promise<any[]> => {
    const response = await apiClient.get('/integration/category-mappings', { params });
    return response.data;
  },
  create: async (data: any): Promise<any> => {
    const response = await apiClient.post('/integration/category-mappings', data);
    return response.data;
  },
  update: async (id: number, data: any): Promise<any> => {
    const response = await apiClient.put(`/integration/category-mappings/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/integration/category-mappings/${id}`);
  },
};

// --- Staging Files (File Browser) API ----------------------------------------

export const stagingFilesApi = {
  listFiles: async (path?: string): Promise<{ path: string; items: any[] }> => {
    const response = await apiClient.get('/integration/staging/files', { params: path ? { path } : {} });
    return response.data;
  },
  deleteFile: async (filename: string): Promise<any> => {
    const response = await apiClient.delete(`/integration/staging/files/${encodeURIComponent(filename)}`);
    return response.data;
  },
  createFolder: async (name: string, path?: string): Promise<{ created: boolean; path: string; persisted: boolean }> => {
    const response = await apiClient.post('/integration/staging/files/folder', { name, path: path || '' });
    return response.data;
  },
  // Rename a file/folder within its current folder.
  renameFile: async (path: string, newName: string): Promise<{ renamed: boolean; path: string; name: string }> => {
    const response = await apiClient.post('/integration/staging/files/rename', { path, new_name: newName });
    return response.data;
  },
  // Upload an arbitrary file into the inbox (stored raw — no tabular parsing).
  // Multipart header is required or the default JSON content-type drops the body.
  uploadFile: async (file: File, path?: string): Promise<{ uploaded: boolean; path: string; name: string; size: number }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('path', path || '');
    const response = await apiClient.post('/integration/staging/files', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  // Download a file's bytes and trigger a browser save.
  downloadFile: async (path: string): Promise<void> => {
    const response = await apiClient.get('/integration/staging/files/download', {
      params: { path },
      responseType: 'blob',
    });
    const url = URL.createObjectURL(response.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = path.split('/').pop() || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};

// --- Workbench View Definitions (saved column layouts) ------------------------

export interface WorkbenchViewColumn {
  key: string;
  label?: string | null;
  visible: boolean;
}

export interface WorkbenchView {
  id: number;
  name: string;
  integration_id: number | null;
  columns: WorkbenchViewColumn[];
  sort_key: string | null;
  sort_dir: 'asc' | 'desc' | null;
  is_public: boolean;
  created_by: number | null;
  is_owner: boolean;
}

export interface WorkbenchViewInput {
  name: string;
  integration_id?: number | null;
  columns: WorkbenchViewColumn[];
  sort_key?: string | null;
  sort_dir?: 'asc' | 'desc' | null;
  is_public?: boolean;
}

export const workbenchViewsApi = {
  list: async (integrationId?: number | null): Promise<WorkbenchView[]> => {
    const response = await apiClient.get('/integration/workbench-views', {
      params: integrationId != null ? { integration_id: integrationId } : {},
    });
    return response.data;
  },
  create: async (data: WorkbenchViewInput): Promise<WorkbenchView> => {
    const response = await apiClient.post('/integration/workbench-views', data);
    return response.data;
  },
  update: async (id: number, data: Partial<WorkbenchViewInput>): Promise<WorkbenchView> => {
    const response = await apiClient.put(`/integration/workbench-views/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/integration/workbench-views/${id}`);
  },
};

// --- Metadata Import/Export API -----------------------------------------------

export interface MetadataImportResult {
  imported: number;
  updated: number;
  deleted: number;
  errors: string[];
  /** Columns in the uploaded file that the backend did not recognise (optional). */
  ignored_headers?: string[];
  /** Non-fatal findings (orphan parents, unknown Operation values …). */
  warnings?: string[];
  /** Oracle FCCS shared rows applied as alternate-hierarchy placements. */
  shared_upserts?: number;
  /** Rows rejected by validation — each has a matching entry in errors. */
  skipped_rows?: number;
}

export interface MetadataValidationPreviewRow {
  row_number: number;
  data: Record<string, string>;
  errors: string[];
}

export interface MetadataValidationResult {
  valid: boolean;
  total_rows: number;
  error_count: number;
  headers: string[];
  preview: MetadataValidationPreviewRow[];
  errors: string[];
  /** Non-fatal findings from the dry run (orphan parents, unknown Operation …). */
  warnings?: string[];
  ignored_headers?: string[];
}

export interface MetadataDimensionInfo {
  id: string;
  label: string;
  description: string;
}

export interface MetadataExportParams {
  dimension: string;
  file_type: 'comma_delimited' | 'tab_delimited';
  properties?: string[];
  // "oracle_fccs" = Oracle EPM/FCCS ExportedMetadata layout (full header set +
  // Oracle enum tokens; re-imports here and loads back into Oracle).
  // Omitted/"app" = the app's own property columns.
  format?: 'app' | 'oracle_fccs';
}

export const metadataApi = {
  importFile: async (
    dimension: string,
    file: File,
    options: {
      file_type?: string;
      delimiter?: string;
      clear_members?: boolean;
      clear_shared_members?: boolean;
    } = {},
  ): Promise<MetadataImportResult> => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams();
    params.set('dimension', dimension);
    if (options.file_type) params.set('file_type', options.file_type);
    if (options.delimiter) params.set('delimiter', options.delimiter);
    if (options.clear_members) params.set('clear_members', 'true');
    if (options.clear_shared_members) params.set('clear_shared_members', 'true');
    // Synchronous import: the full ImportResult (row errors, warnings, counts)
    // comes back in THIS response so the user sees problems immediately —
    // the async job endpoint (POST /metadata) buried them in the job record.
    const response = await apiClient.post(`/metadata/sync?${params.toString()}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  validateImport: async (
    dimension: string,
    file: File,
    options: { file_type?: string; delimiter?: string } = {},
  ): Promise<MetadataValidationResult> => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams();
    params.set('dimension', dimension);
    if (options.file_type) params.set('file_type', options.file_type);
    if (options.delimiter) params.set('delimiter', options.delimiter);
    const response = await apiClient.post(`/metadata/import/validate?${params.toString()}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  exportDimension: async (params: MetadataExportParams): Promise<{ job_id?: number; message?: string }> => {
    const response = await apiClient.post('/metadata/export', params);
    return response.data;
  },

  // Sync export (immediate download, used as fallback)
  exportDimensionSync: async (params: MetadataExportParams): Promise<void> => {
    const response = await apiClient.post('/metadata/export/sync', params, {
      responseType: 'blob',
    });
    const contentDisposition = response.headers['content-disposition'] || '';
    const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
    const filename = match?.[1] || `${params.dimension}_metadata.csv`;
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  listDimensions: async (): Promise<MetadataDimensionInfo[]> => {
    const response = await apiClient.get('/metadata/export/dimensions');
    return response.data;
  },
};

// --- Integration Schedules API -----------------------------------------------

export const integrationSchedulesApi = {
  list: async (params?: { search?: string; schedule_type?: string; is_active?: boolean }): Promise<any[]> => {
    const response = await apiClient.get('/integration/schedules', { params });
    return response.data;
  },
  create: async (data: any): Promise<any> => {
    const response = await apiClient.post('/integration/schedules', data);
    return response.data;
  },
  update: async (id: number, data: any): Promise<any> => {
    const response = await apiClient.put(`/integration/schedules/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/integration/schedules/${id}`);
  },
  runNow: async (id: number): Promise<any> => {
    const response = await apiClient.post(`/integration/schedules/${id}/run-now`);
    return response.data;
  },
};

// ── Manage Consolidation Rules (FCCS Configurable Consolidation) ────────────
//
// Backs the modal that opens from Rules › Process › Consolidated ›
// Configurable Consolidation.  The list endpoint auto-seeds the 20 FCCS
// standard rules on the first call per tenant.

export type MCRStatus = 'not_deployed' | 'deployed' | 'modified';

export interface MCRRule {
  id: number;
  name: string;
  description: string | null;
  kind: 'system' | 'epu' | 'custom';
  deployed: boolean;
  // When the ruleset was last deployed; null when not currently deployed.
  deployed_at: string | null;
  // Server-computed lifecycle state: 'modified' = "Changed after Deploy".
  status: MCRStatus;
  sequence: number;
  modified_by: string | null;
  // Set once when the rule-set is created; modified_by changes on every edit.
  created_by?: string | null;
  script: string | null;
  // Application cube the ruleset belongs to (default "Consol"); chosen at
  // creation and read-only afterwards.
  cube: string;
  // Ruleset-level configurable properties (PDF Ch.1 p5 §4-5, roadmap P2) —
  // inherited by every rule in the ruleset.  null = inherit default / Always Run.
  condition: Record<string, unknown> | null;
  factor: Record<string, unknown> | null;
  scope: Record<string, unknown> | null;
  source_pov: Record<string, unknown> | null;
  source_dataset: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

// One problem found by the pre-deploy validation endpoint.
export interface MCRValidationIssue {
  severity: 'error' | 'warning';
  line_id: number | null;
  line_name: string | null;
  message: string;
}

export interface MCRValidationResult {
  ruleset_id: number;
  valid: boolean;
  issues: MCRValidationIssue[];
}

/** Excel or CSV; one sheet / section per table, or everything on one page. */
export type MCRTransferFormat = 'xlsx_sheets' | 'xlsx_single' | 'csv_sheets' | 'csv_single';

export interface MCRImportResult {
  rulesets_created: number;
  rules_created: number;
  ruleset_ids: number[];
  ruleset_names: string[];
  warnings: string[];
}

/** One selectable member of a rule Dataset (source POV) dimension. */
export interface DatasetOption {
  value: string;
  label: string;
  /** True for the "Current …" placeholders resolved at run time. */
  is_token: boolean;
}
export type DatasetOptions = Record<string, DatasetOption[]>;

export const manageConsolidationRulesApi = {
  list: async (): Promise<MCRRule[]> =>
    (await apiClient.get<MCRRule[]>('/manage-consolidation-rules')).data,

  /** Dimension Manager members for every Dataset dimension, in one call. */
  datasetOptions: async (): Promise<DatasetOptions> =>
    (await apiClient.get<DatasetOptions>('/manage-consolidation-rules/dataset-options')).data,

  create: async (payload: Partial<MCRRule> & { name: string }): Promise<MCRRule> =>
    (await apiClient.post<MCRRule>('/manage-consolidation-rules', payload)).data,

  update: async (id: number, payload: Partial<MCRRule>): Promise<MCRRule> =>
    (await apiClient.put<MCRRule>(`/manage-consolidation-rules/${id}`, payload)).data,

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/manage-consolidation-rules/${id}`);
  },

  bulkDeploy: async (ids: number[]): Promise<MCRRule[]> =>
    (await apiClient.post<MCRRule[]>('/manage-consolidation-rules/deploy', { ids })).data,

  bulkUndeploy: async (ids: number[]): Promise<MCRRule[]> =>
    (await apiClient.post<MCRRule[]>('/manage-consolidation-rules/undeploy', { ids })).data,

  /**
   * Run one deployed rule-set against the POV the user picked.
   *
   * Same run-time prompts as the Rules card (Scenario / Year / Period /
   * Entity), resolved by the same server-side validator, and the result shape
   * is shared so `RuleLaunchModal` renders it unchanged.
   */
  launch: async (id: number, prompts: Record<string, string>): Promise<SBRLaunchResult> =>
    (await apiClient.post<SBRLaunchResult>(
      `/manage-consolidation-rules/${id}/launch`, { prompts },
    )).data,

  reorder: async (id: number, direction: 'up' | 'down'): Promise<MCRRule[]> =>
    (await apiClient.post<MCRRule[]>('/manage-consolidation-rules/reorder', { id, direction })).data,

  // ── Lifecycle: Duplicate / Validate / Import-Export ─────────────────────
  duplicate: async (id: number, withLines: boolean): Promise<MCRRule> =>
    (await apiClient.post<MCRRule>(
      `/manage-consolidation-rules/${id}/duplicate`, { with_lines: withLines },
    )).data,

  validate: async (id: number): Promise<MCRValidationResult> =>
    (await apiClient.post<MCRValidationResult>(
      `/manage-consolidation-rules/${id}/validate`,
    )).data,

  // Returns the raw XML document so the caller can trigger a file download.
  /** Export the rulesets as an Excel workbook or a CSV file. */
  exportFile: async (ids: number[], format: MCRTransferFormat): Promise<Blob> =>
    (await apiClient.post('/manage-consolidation-rules/export', { ids, format }, {
      responseType: 'blob',
    })).data as Blob,

  /** Import rulesets from an Excel (.xlsx) or CSV file exported above. */
  importFile: async (file: File): Promise<MCRImportResult> => {
    const form = new FormData();
    form.append('file', file);
    return (await apiClient.post<MCRImportResult>(
      '/manage-consolidation-rules/import', form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )).data;
  },

  // ── Rules (journal lines) inside a ruleset ──────────────────────────────
  listLines: async (rulesetId: number): Promise<MCRRuleLine[]> =>
    (await apiClient.get<MCRRuleLine[]>(`/manage-consolidation-rules/${rulesetId}/lines`)).data,

  createLine: async (
    rulesetId: number,
    payload: Partial<MCRRuleLine> & { name: string },
  ): Promise<MCRRuleLine> =>
    (await apiClient.post<MCRRuleLine>(`/manage-consolidation-rules/${rulesetId}/lines`, payload)).data,

  updateLine: async (
    rulesetId: number,
    lineId: number,
    payload: Partial<MCRRuleLine>,
  ): Promise<MCRRuleLine> =>
    (await apiClient.put<MCRRuleLine>(`/manage-consolidation-rules/${rulesetId}/lines/${lineId}`, payload)).data,

  deleteLine: async (rulesetId: number, lineId: number): Promise<void> => {
    await apiClient.delete(`/manage-consolidation-rules/${rulesetId}/lines/${lineId}`);
  },

  reorderLine: async (
    rulesetId: number,
    id: number,
    direction: 'up' | 'down',
  ): Promise<MCRRuleLine[]> =>
    (await apiClient.post<MCRRuleLine[]>(
      `/manage-consolidation-rules/${rulesetId}/lines/reorder`, { id, direction },
    )).data,
};

// A Rule (journal line) inside a Manage Consolidation ruleset.
export interface MCRRuleLine {
  id: number;
  ruleset_id: number;
  name: string;
  description: string | null;
  sequence: number;
  processing: 'add' | 'subtract';
  partner_elimination: boolean;
  condition: Record<string, unknown> | null;
  factor: Record<string, unknown> | null;
  scope: Record<string, unknown> | null;
  source_pov: Record<string, unknown> | null;
  redirection: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Equity Pickup (EPU) — enable flag + Accounts/Movement configs (PDF Ch.10)
// ---------------------------------------------------------------------------

// One "Accounts and Movement Settings" rule-set config (Ch.10 §2.2).
export interface EPURuleConfig {
  id: number;
  name: string;
  is_default: boolean;
  sequence: number;
  source_collection_account: string;
  account_1: string;
  account_1_movement: string;
  account_2: string;
  account_2_movement: string;
  created_at: string;
  updated_at: string;
}

export interface EPUSettings {
  enabled: boolean;
  enabled_at: string | null;
  modified_by: string | null;
  ownership_management_available: boolean;
  configs: EPURuleConfig[];
}

export type EPURuleConfigInput = Omit<
  EPURuleConfig, 'id' | 'is_default' | 'sequence' | 'created_at' | 'updated_at'
>;

export const equityPickupApi = {
  get: async (): Promise<EPUSettings> =>
    (await apiClient.get<EPUSettings>('/epu')).data,

  setEnabled: async (enabled: boolean): Promise<EPUSettings> =>
    (await apiClient.put<EPUSettings>('/epu', { enabled })).data,

  createConfig: async (payload: EPURuleConfigInput): Promise<EPURuleConfig> =>
    (await apiClient.post<EPURuleConfig>('/epu/configs', payload)).data,

  updateConfig: async (id: number, payload: Partial<EPURuleConfigInput>): Promise<EPURuleConfig> =>
    (await apiClient.put<EPURuleConfig>(`/epu/configs/${id}`, payload)).data,

  deleteConfig: async (id: number): Promise<void> => {
    await apiClient.delete(`/epu/configs/${id}`);
  },
};

// ---------------------------------------------------------------------------
// System Calculations — FCCS built-in calc settings (Consolidation: Process)
// ---------------------------------------------------------------------------

export type SCSettingType = 'toggle' | 'disable_by_scenario' | 'overrides';

// The FCCS two-line "disable by scenario" Member Selector.
export interface SCDisabledScenarios {
  disable: string[];   // scenarios for which the calc is turned off
  exclude: string[];   // scenarios that stay enabled (exclusion line)
}

// One Opening Balance Carry Forward override rule.
export interface SCOverrideRule {
  name: string;
  years: string[];               // empty = all years
  current_scenarios: string[];
  source_scenario: string | null;
}

// Data-source reference for scoping a calc to specific data sources.
export interface SCDataSourceRef {
  code: string;
  label: string;
}

export interface SystemCalcSetting {
  id: number;
  calc_key: string;
  name: string;
  description: string | null;
  setting_type: SCSettingType;
  layer: string;
  sequence: number;
  enabled: boolean;
  disabled_scenarios: SCDisabledScenarios | null;
  overrides: SCOverrideRule[] | null;
  // Data-source scoping — null/empty = all data sources (backward compatible).
  data_source_filter: SCDataSourceRef[] | null;
  created_at: string;
  updated_at: string;
}

export interface SystemCalcUpdate {
  enabled?: boolean;
  disabled_scenarios?: SCDisabledScenarios;
  overrides?: SCOverrideRule[];
  data_source_filter?: SCDataSourceRef[];
}

export const systemCalculationsApi = {
  list: async (): Promise<SystemCalcSetting[]> =>
    (await apiClient.get<SystemCalcSetting[]>('/system-calculations')).data,

  update: async (calcKey: string, payload: SystemCalcUpdate): Promise<SystemCalcSetting> =>
    (await apiClient.put<SystemCalcSetting>(`/system-calculations/${calcKey}`, payload)).data,
};

// ---------------------------------------------------------------------------
// On-Demand Rules — ad-hoc calculation rules (PDF Ch. 11)
// ---------------------------------------------------------------------------

export type ODRStatus = 'draft' | 'deployed';
export type ODRRuleType = 'calc' | 'groovy';
export type ODRRunStatus = 'success' | 'error';
export type ODRFormTiming =
  | 'run_before_load' | 'run_after_load' | 'run_before_save' | 'run_after_save';

// One Run-Time Prompt (replacement variable) on a rule.
export interface ODRRunTimePrompt {
  dimension: string;
  prompt_text: string;
  default_member: string;
  use_override: boolean;
  is_hidden: boolean;
}

export interface ODRFormAttachment {
  form: string;
  timing: ODRFormTiming;
  hide_prompt: boolean;
}

export interface OnDemandRule {
  id: number;
  name: string;
  description: string | null;
  cube: string;
  rule_type: ODRRuleType;
  script: string | null;
  status: ODRStatus;
  deployed_at: string | null;
  run_time_prompts: ODRRunTimePrompt[] | null;
  form_attachments: ODRFormAttachment[] | null;
  launch_permissions: string[] | null;
  modified_by: string | null;
  last_run_status: string | null;
  last_run_at: string | null;
  // US-R3.4 AC3 — FIXPARALLEL by default; false = sequential execution
  // (deploy writes the `<NORMALIZED_NAME>_FP = "False"` Consol-cube sub-var).
  fix_parallel: boolean;
  created_at: string;
  updated_at: string;
}

export interface ODRValidationIssue {
  severity: 'error' | 'warning';
  message: string;
}

export interface ODRValidationResult {
  rule_id: number;
  valid: boolean;
  issues: ODRValidationIssue[];
}

export interface ODRRun {
  id: number;
  rule_id: number;
  status: ODRRunStatus;
  prompts: Record<string, unknown> | null;
  entities_impacted: string[] | null;
  message: string | null;
  launched_by: string | null;
  created_at: string;
  // Jobs-console entry created by the launch (US-R1.4).
  job_id?: number | null;
  task_id?: string | null;
}

// One RTP member-validity violation (US-R3.5 AC3 — names the dimension).
export interface ODRRtpViolation {
  dimension: string;
  message: string;
}

export interface ODROverrideGateResult {
  enabled: boolean;
  name: string;
  value: string;
  cube_name: string;
}

export const onDemandRulesApi = {
  list: async (): Promise<OnDemandRule[]> =>
    (await apiClient.get<OnDemandRule[]>('/on-demand-rules')).data,

  // Deployed ODRs attached to a data form (US-R6.1) — drives "Run rule".
  forForm: async (form: string): Promise<OnDemandRule[]> =>
    (await apiClient.get<OnDemandRule[]>('/on-demand-rules/for-form', { params: { form } })).data,

  create: async (payload: Partial<OnDemandRule> & { name: string }): Promise<OnDemandRule> =>
    (await apiClient.post<OnDemandRule>('/on-demand-rules', payload)).data,

  update: async (id: number, payload: Partial<OnDemandRule>): Promise<OnDemandRule> =>
    (await apiClient.put<OnDemandRule>(`/on-demand-rules/${id}`, payload)).data,

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/on-demand-rules/${id}`);
  },

  validate: async (id: number): Promise<ODRValidationResult> =>
    (await apiClient.post<ODRValidationResult>(`/on-demand-rules/${id}/validate`)).data,

  deploy: async (id: number): Promise<OnDemandRule> =>
    (await apiClient.post<OnDemandRule>(`/on-demand-rules/${id}/deploy`)).data,

  launch: async (id: number, prompts: Record<string, unknown>): Promise<ODRRun> =>
    (await apiClient.post<ODRRun>(`/on-demand-rules/${id}/launch`, { prompts })).data,

  // US-R3.5 AC2 — set ODR_ENABLE_RATE_AMOUNT_OVERRIDE = TRUE on the Consol
  // cube so Amount/Rate Override launches are permitted (admin only).
  enableOverrideGate: async (): Promise<ODROverrideGateResult> =>
    (await apiClient.post<ODROverrideGateResult>('/on-demand-rules/enable-override-gate')).data,

  runs: async (id: number): Promise<ODRRun[]> =>
    (await apiClient.get<ODRRun[]>(`/on-demand-rules/${id}/runs`)).data,

  // Import / Export (PDF Ch. 11 §4)
  exportAll: async (): Promise<ODRExportBundle> =>
    (await apiClient.get<ODRExportBundle>('/on-demand-rules/export')).data,
  exportOne: async (id: number): Promise<ODRExportBundle> =>
    (await apiClient.get<ODRExportBundle>(`/on-demand-rules/${id}/export`)).data,
  import: async (bundle: ODRExportBundle): Promise<ODRImportResult> =>
    (await apiClient.post<ODRImportResult>('/on-demand-rules/import', bundle)).data,
};

export interface ODRExportItem {
  name: string;
  description: string | null;
  cube: string;
  rule_type: ODRRuleType;
  script: string | null;
  run_time_prompts: ODRRunTimePrompt[] | null;
  form_attachments: ODRFormAttachment[] | null;
}

export interface ODRExportBundle {
  schema: string;
  rules: ODRExportItem[];
}

export interface ODRImportResult {
  imported: number;
  skipped: number;
  imported_names: string[];
  skipped_names: string[];
}

// ---------------------------------------------------------------------------
// Standard Business Rules — pre-built consolidation/translation rules (PDF Ch. 5)
// ---------------------------------------------------------------------------

export type SBRCube = 'Consol' | 'Rates';
export type SBRType = 'Script' | 'Rule' | 'Ruleset' | 'Template';

export interface StandardBusinessRule {
  id: number;
  name: string;
  description: string | null;
  cube: SBRCube;
  rule_type: SBRType;
  deployed: boolean;
  enabled: boolean;
  is_system: boolean;
  sort_order: number;
  last_deployed_at: string | null;
  last_run_at: string | null;
  last_run_status: 'success' | 'failed' | 'running' | null;
  last_run_error: string | null;
  modified_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface SBRLaunchResult {
  rule_id: number;
  name: string;
  status: 'success' | 'failed' | 'blocked';
  message: string;
  job_id: number | null;
  task_id: string | null;
  consolidation_run_id: number | null;
}

export interface SBRLaunchQueued {
  rule_id: number;
  name: string;
  status: 'queued';
  message: string;
  job_id: number;
  task_id: string;
  poll_url: string;
}

export interface SBRUsageLocation {
  kind: string;
  name: string;
  detail: string | null;
}

export interface SBRUsageRow {
  rule_id: number;
  name: string;
  cube: SBRCube;
  rule_type: SBRType;
  enabled: boolean;
  locations: SBRUsageLocation[];
}

export interface SBRUsageReport {
  generated_for_cube: string;
  rows: SBRUsageRow[];
}

export const standardBusinessRulesApi = {
  list: async (params?: { cube?: string; rule_type?: string; search?: string }): Promise<StandardBusinessRule[]> =>
    (await apiClient.get<StandardBusinessRule[]>('/standard-business-rules', { params })).data,
  enable: async (id: number): Promise<StandardBusinessRule> =>
    (await apiClient.post<StandardBusinessRule>(`/standard-business-rules/${id}/enable`)).data,
  disable: async (id: number): Promise<StandardBusinessRule> =>
    (await apiClient.post<StandardBusinessRule>(`/standard-business-rules/${id}/disable`)).data,
  deploy: async (id: number): Promise<StandardBusinessRule> =>
    (await apiClient.post<StandardBusinessRule>(`/standard-business-rules/${id}/deploy`)).data,
  undeploy: async (id: number): Promise<StandardBusinessRule> =>
    (await apiClient.post<StandardBusinessRule>(`/standard-business-rules/${id}/undeploy`)).data,
  launch: async (id: number, prompts: Record<string, string> = {}): Promise<SBRLaunchResult> =>
    (await apiClient.post<SBRLaunchResult>(`/standard-business-rules/${id}/launch`, { prompts })).data,
  /**
   * Queued launch (HTTP 202): the Jobs row is written as Queued and the engine
   * runs on the worker. Poll `job_id` with `pollJobUntilTerminal`. Replaces the
   * synchronous `launch` in the dialog — a long Consolidate hit the 30 s axios
   * timeout ("timeout of 30000ms exceeded") while the backend ran to success.
   */
  launchAsync: async (id: number, prompts: Record<string, string> = {}): Promise<SBRLaunchQueued> =>
    (await apiClient.post<SBRLaunchQueued>(`/standard-business-rules/${id}/launch-async`, { prompts })).data,
  usage: async (cube?: string): Promise<SBRUsageReport> =>
    (await apiClient.get<SBRUsageReport>('/standard-business-rules/usage', { params: cube ? { cube } : undefined })).data,
  /** US-INT-BR-03 — run-time prompt rows a rule exposes to an integration (§5). */
  promptTemplate: async (id: number): Promise<SBRPromptTemplate> =>
    (await apiClient.get<SBRPromptTemplate>(`/standard-business-rules/${id}/prompt-template`)).data,
};

// ---------------------------------------------------------------------------
// Integration Business Rules — US-INT-BR (Options → Business Rules grid)
// ---------------------------------------------------------------------------

export type IntegrationRuleEvent =
  | 'before_extract'
  | 'before_import'
  | 'after_import'
  | 'before_load'
  | 'after_load'
  | 'before_check'
  | 'after_check';

export type IntegrationRuleValueType =
  | 'custom'
  | 'pov_category'
  | 'pov_year'
  | 'pov_period'
  | 'all_data_load_entity';

export type IntegrationRuleEntityScope = 'common_parent' | 'loaded_only' | 'top';

export type SBRPromptDimension = 'scenario' | 'year' | 'period' | 'entity' | 'currency';

export interface SBRPromptTemplateRow {
  /** Key sent in SBRLaunchRequest.prompts (Scenario / Year / Period / Entity / Currency). */
  name: string;
  label: string;
  dimension: SBRPromptDimension;
  required: boolean;
  allowed_value_types: IntegrationRuleValueType[];
}

export interface SBRPromptTemplate {
  rule_id: number;
  rule_name: string;
  prompts: SBRPromptTemplateRow[];
}

export interface IntegrationRulePrompt {
  name: string;
  value_type: IntegrationRuleValueType;
  /** Member code when value_type = custom, else null. */
  value: string | null;
}

export interface IntegrationBusinessRulePayload {
  id?: number;
  event: IntegrationRuleEvent;
  rule_type: 'rule';
  standard_business_rule_id: number;
  sequence: number;
  runtime_prompts: IntegrationRulePrompt[];
  entity_scope?: IntegrationRuleEntityScope | null;
  is_active: boolean;
}

export interface IntegrationBusinessRuleOut extends IntegrationBusinessRulePayload {
  rule_name?: string | null;
}

export type IntegrationRulesStatus = 'none' | 'completed' | 'completed_with_errors' | 'skipped';

export interface IntegrationRuleRunDetail {
  event: IntegrationRuleEvent | string;
  rule_name: string;
  sequence?: number | null;
  fy?: string | number | null;
  period_code?: string | null;
  resolved_prompts?: Record<string, unknown> | null;
  entity_scope?: string | null;
  lca_entity_code?: string | null;
  status: 'completed' | 'failed' | 'skipped';
  reason?: string | null;
  /** Jobs row reference — numeric TaskStatus.id or its task_id UUID. */
  task_id?: string | number | null;
  started_at?: string | null;
  completed_at?: string | null;
  note?: string | null;
}

// ---------------------------------------------------------------------------
// Rule launch permissions — Assign Permissions (PDF Ch. 5 §2 / Ch. 11 §5)
// ---------------------------------------------------------------------------

export type RuleKind = 'standard' | 'on_demand';
export type RulePermissionValue = 'launch' | 'no_launch';

export interface AssignableUser {
  user_id: number;
  email: string;
  full_name: string | null;
  role: string | null;
}

export interface RulePermissionEntry {
  user_id: number;
  permission: RulePermissionValue;
}

export interface RulePermissionRead extends RulePermissionEntry {
  email: string | null;
  full_name: string | null;
  role: string | null;
}

export interface RulePermissionListResponse {
  rule_kind: RuleKind;
  rule_id: number;
  permissions: RulePermissionRead[];
}

export const rulePermissionsApi = {
  users: async (): Promise<AssignableUser[]> =>
    (await apiClient.get<AssignableUser[]>('/rule-permissions/users')).data,
  list: async (rule_kind: RuleKind, rule_id: number): Promise<RulePermissionListResponse> =>
    (await apiClient.get<RulePermissionListResponse>('/rule-permissions', { params: { rule_kind, rule_id } })).data,
  set: async (rule_kind: RuleKind, rule_id: number, entries: RulePermissionEntry[]): Promise<RulePermissionListResponse> =>
    (await apiClient.put<RulePermissionListResponse>('/rule-permissions', { rule_kind, rule_id, entries })).data,
};

// ---------------------------------------------------------------------------
// FCCS-style Consolidation Journals
// ---------------------------------------------------------------------------

export type FccsJournalTypeValue = 'Regular' | 'Auto-Reversing';
export type FccsBalanceTypeValue = 'Balanced' | 'Unbalanced';
export type FccsJournalStatusValue =
  | 'Working'
  | 'Completed'
  | 'Submitted'
  | 'Approved'
  | 'Posted'
  | 'Rejected';
export type FccsJournalActionValue =
  | 'Create' | 'Save' | 'Complete' | 'Submit' | 'Reject'
  | 'Approve' | 'Post' | 'Unpost' | 'Delete' | 'Scan' | 'History' | 'Print';

export interface FccsJournalLineDTO {
  id?: number;
  sort_order?: number;
  entity?: string | null;
  account?: string | null;
  icp?: string | null;
  movement?: string | null;
  multi_gaap?: string | null;
  source?: string | null;
  currency?: string | null;
  custom1?: string | null;
  custom2?: string | null;
  custom3?: string | null;
  custom4?: string | null;
  description?: string | null;
  debit?: string | number;
  credit?: string | number;
}

export interface FccsJournalAttachmentDTO {
  id: string;
  filename: string;
  size: number;
  content_type: string | null;
}

export interface FccsJournalHistoryDTO {
  id: number;
  action: FccsJournalActionValue;
  from_status: FccsJournalStatusValue | null;
  to_status: FccsJournalStatusValue | null;
  comment: string | null;
  user_id: number | null;
  user_name: string | null;
  user_email: string | null;
  attachments: FccsJournalAttachmentDTO[];
  created_at: string;
}

export interface FccsJournalDTO {
  id: number;
  // The Journal ID users see: 1, 2, 3… within the journal's POV combination
  // (Scenario + Year + Period + View + Consolidation), never reused. `id` is
  // the internal key for API calls.
  journal_number?: number | null;
  label: string;
  description: string | null;
  journal_type: FccsJournalTypeValue;
  data_source: string;
  group: string | null;
  balance_type: FccsBalanceTypeValue;
  scenario: string;
  year: string;
  period: string;
  view_member: string;
  consolidation: string;
  status: FccsJournalStatusValue;
  post_as: 'Year-to-date' | 'Journal-to-date' | null;
  template_id: number | null;
  reverses_journal_id: number | null;
  created_by: number | null;
  submitted_by: number | null;
  submitted_at: string | null;
  approved_by: number | null;
  approved_at: string | null;
  posted_by: number | null;
  posted_at: string | null;
  modified_by: number | null;
  created_at: string;
  updated_at: string;
  lines: FccsJournalLineDTO[];
  history: FccsJournalHistoryDTO[];
  total_debit: string;
  total_credit: string;
  // Display names for the workflow stamps. null = that step has not been
  // reached, or was undone (Unpost clears posted, Reject clears all three).
  // Follow these rather than the append-only `history` when showing who
  // submitted / approved / posted a journal.
  submitted_by_name: string | null;
  approved_by_name: string | null;
  posted_by_name: string | null;
}

export interface FccsJournalCreatePayload {
  label: string;
  description?: string | null;
  journal_type: FccsJournalTypeValue;
  data_source: string;
  group?: string | null;
  balance_type: FccsBalanceTypeValue;
  scenario: string;
  year: string;
  period: string;
  view_member: string;
  consolidation: string;
  post_as?: 'Year-to-date' | 'Journal-to-date' | null;
  lines: FccsJournalLineDTO[];
}

export interface FccsJournalUpdatePayload extends Partial<FccsJournalCreatePayload> {}

export interface FccsJournalListFilter {
  scenario?: string;
  year?: string;
  period?: string;
  view?: string;
  consolidation?: string;
  status?: FccsJournalStatusValue;
}

export const fccsJournalsApi = {
  list: async (filter?: FccsJournalListFilter): Promise<FccsJournalDTO[]> => {
    const r = await apiClient.get<FccsJournalDTO[]>('/fccs-journals', { params: filter });
    return r.data;
  },

  get: async (id: number): Promise<FccsJournalDTO> => {
    const r = await apiClient.get<FccsJournalDTO>(`/fccs-journals/${id}`);
    return r.data;
  },

  create: async (payload: FccsJournalCreatePayload): Promise<FccsJournalDTO> => {
    const r = await apiClient.post<FccsJournalDTO>('/fccs-journals', payload);
    return r.data;
  },

  // Live valid/invalid-intersection check for an unsaved draft. Resolves every
  // dimension a VI rule can reference (server-side) and returns per-line validity,
  // so the editor can lock Debit/Credit on invalid lines. Same engine + resolver
  // as the Scan gate, so live results and the Submit/Post block agree.
  validateIntersections: async (payload: {
    scenario?: string | null;
    year?: string | null;
    period?: string | null;
    view_member?: string | null;
    consolidation?: string | null;
    data_source?: string | null;
    lines: Record<string, unknown>[];
  }): Promise<{ items: { index: number; valid: boolean; violations: string[] }[] }> => {
    const r = await apiClient.post('/fccs-journals/validate-intersections', payload);
    return r.data;
  },

  update: async (id: number, payload: FccsJournalUpdatePayload): Promise<FccsJournalDTO> => {
    const r = await apiClient.put<FccsJournalDTO>(`/fccs-journals/${id}`, payload);
    return r.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/fccs-journals/${id}`);
  },

  // PDF §11 workflow transitions. The action string must match one of the
  // FccsJournalAction enum values: Complete | Submit | Reject | Approve |
  // Post | Unpost. Backend validates transition + balanced-journal gate.
  action: async (
    id: number,
    action:
      | 'Complete'
      | 'Submit'
      | 'Reject'
      | 'Approve'
      | 'Post'
      | 'Unpost',
    comment?: string,
    files?: File[],
    onProgress?: (pct: number) => void,
    // Auto-Reversing journals only: the period/year the user picked in the Post
    // dialog's "Auto Reversal Option" dropdown. Tells the backend where to
    // create the reversal journal (defaults to the immediate next period when
    // omitted).
    reversal?: { period?: string; year?: string },
  ): Promise<FccsJournalDTO> => {
    // multipart/form-data: the backend accepts the action + optional comment as
    // form fields plus a supporting-document attachment (Oracle FCCS). When a
    // file is attached, onProgress reports upload percentage (0–100) so the
    // dialog can show a progress bar.
    const fd = new FormData();
    fd.append('action', action);
    if (comment) fd.append('comment', comment);
    for (const f of files ?? []) fd.append('files', f);
    if (reversal?.period) fd.append('reversal_period', reversal.period);
    if (reversal?.year) fd.append('reversal_year', reversal.year);
    const r = await apiClient.post<FccsJournalDTO>(
      `/fccs-journals/${id}/actions`,
      fd,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (onProgress && e.total) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        },
      },
    );
    return r.data;
  },

  // Download a workflow-action attachment retained in the journal history.
  // Auth is required, so we fetch the blob through apiClient (which attaches
  // the token) and trigger a browser save rather than using a bare <a href>.
  downloadActionAttachment: async (
    journalId: number,
    fileId: string,
    filename: string,
  ): Promise<void> => {
    const r = await apiClient.get(
      `/fccs-journals/${journalId}/actions/attachments/${fileId}`,
      { responseType: 'blob' },
    );
    const url = window.URL.createObjectURL(r.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  // View a workflow-action attachment inline (opens in a new browser tab)
  // instead of forcing a download. Same auth'd blob fetch as the download
  // helper, but the object URL is opened rather than saved. The content type
  // from the response drives whether the browser renders it (pdf/image/text)
  // or falls back to its own download prompt for unviewable types.
  viewActionAttachment: async (
    journalId: number,
    fileId: string,
  ): Promise<void> => {
    const r = await apiClient.get(
      `/fccs-journals/${journalId}/actions/attachments/${fileId}`,
      { responseType: 'blob' },
    );
    const url = window.URL.createObjectURL(r.data as Blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    // Revoke after a delay so the new tab has time to load the blob.
    setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  },

  copy: async (
    id: number,
    destination?: { scenario?: string; year?: string; period?: string },
  ): Promise<FccsJournalDTO> => {
    const body = destination
      ? {
          destination_scenario: destination.scenario || null,
          destination_year: destination.year || null,
          destination_period: destination.period || null,
        }
      : {};
    const r = await apiClient.post<FccsJournalDTO>(`/fccs-journals/${id}/copy`, body);
    return r.data;
  },

  scan: async (id: number): Promise<FccsScanResult> => {
    const r = await apiClient.post<FccsScanResult>(`/fccs-journals/${id}/scan`);
    return r.data;
  },

  // ─── Oracle FCCS-style Journal Import / Export ──────────────────────────
  // Import: multipart upload. Backend either imports immediately (local /
  // inbox) or registers a job. Returns { success, job_id, task_id, message }.
  importJournals: async (formData: FormData): Promise<FccsJournalImportResult> => {
    const r = await apiClient.post<FccsJournalImportResult>(
      '/fccs-journals/import',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return r.data;
  },

  // Download a blank import template (CSV or Excel) in the required !POV
  // format, with the tenant's custom-dimension columns. Streams a file the
  // user fills in and re-imports.
  downloadImportTemplate: async (fileType: 'csv' | 'excel'): Promise<void> => {
    const response = await apiClient.get('/fccs-journals/import-template', {
      params: { file_type: fileType },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const disposition = response.headers['content-disposition'] as string | undefined;
    const fallback = `journal_import_template.${fileType === 'excel' ? 'xlsx' : 'csv'}`;
    const filename = disposition?.match(/filename="?([^"]+)"?/)?.[1] || fallback;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Export as a job (run_as_job true OR location "outbox"). Returns JSON
  // describing the dispatched job.
  exportJournals: async (body: FccsJournalExportBody): Promise<FccsJournalExportResult> => {
    const r = await apiClient.post<FccsJournalExportResult>('/fccs-journals/export', body);
    return r.data;
  },

  // Local immediate export → server streams a file. Request as a blob and
  // trigger a browser download using the Content-Disposition filename.
  exportDownload: async (body: FccsJournalExportBody): Promise<void> => {
    const response = await apiClient.post('/fccs-journals/export', body, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const disposition = response.headers['content-disposition'] as string | undefined;
    const ext = body.format === 'xlsx'
      ? 'xlsx'
      : body.file_type === 'tab' ? 'txt' : body.file_type === 'comma' ? 'csv' : 'txt';
    const fallback = `journal_export_${new Date().toISOString().slice(0, 10)}.${ext}`;
    const filename = disposition?.match(/filename="?([^"]+)"?/)?.[1] || fallback;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

export interface FccsJournalImportResult {
  success: boolean;
  job_id?: number | string | null;
  task_id?: string | null;
  message?: string;
}

export interface FccsJournalExportResult {
  success: boolean;
  job_id?: number | string | null;
  task_id?: string | null;
  message?: string;
}

export interface FccsJournalExportBody {
  scenario: string;
  year: string;
  period: string;
  view_member: string;
  consolidation: string;
  statuses: string[];
  data_source: string;
  currency: string;
  format?: 'csv' | 'xlsx';
  file_type: 'comma' | 'tab' | 'other';
  delimiter: string;
  location: 'local' | 'outbox';
  run_as_job: boolean;
  job_name: string;
}

// ─── FCCS Journal Templates (Phase 2 — PDF §4) ─────────────────────────────

export interface FccsTemplateLineDTO {
  id?: number;
  sort_order: number;
  entity: string | null;
  account: string | null;
  icp: string | null;
  movement: string | null;
  multi_gaap: string | null;
  source: string | null;
  currency: string | null;
  custom1: string | null;
  custom2: string | null;
  custom3: string | null;
  custom4: string | null;
  description: string | null;
  debit: string;
  credit: string;
}

export interface FccsTemplateDTO {
  id: number;
  label: string;
  description: string | null;
  journal_type: FccsJournalTypeValue;
  data_source: string;
  group: string | null;
  balance_type: FccsBalanceTypeValue;
  is_recurring: boolean;
  recurring_periods: number | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  lines: FccsTemplateLineDTO[];
}

export interface FccsTemplateCreatePayload {
  label: string;
  description?: string | null;
  journal_type: FccsJournalTypeValue;
  data_source: string;
  group?: string | null;
  balance_type: FccsBalanceTypeValue;
  is_recurring?: boolean;
  recurring_periods?: number | null;
  lines: FccsTemplateLineDTO[];
}

export interface FccsJournalGroupDTO {
  id: number;
  name: string;
  description: string | null;
}

// Journal Groups (Manage → Actions → Manage Groups). Persisted per tenant;
// the list used to live only in page state and was lost on refresh.
export const fccsJournalGroupsApi = {
  list: async (): Promise<FccsJournalGroupDTO[]> => {
    const r = await apiClient.get<FccsJournalGroupDTO[]>('/fccs-journal-groups');
    return r.data;
  },
  create: async (payload: { name: string; description?: string }): Promise<FccsJournalGroupDTO> => {
    const r = await apiClient.post<FccsJournalGroupDTO>('/fccs-journal-groups', payload);
    return r.data;
  },
  remove: async (id: number): Promise<void> => {
    await apiClient.delete(`/fccs-journal-groups/${id}`);
  },
};

export const fccsTemplatesApi = {
  list: async (): Promise<FccsTemplateDTO[]> => {
    const r = await apiClient.get<FccsTemplateDTO[]>('/fccs-journal-templates');
    return r.data;
  },
  get: async (id: number): Promise<FccsTemplateDTO> => {
    const r = await apiClient.get<FccsTemplateDTO>(`/fccs-journal-templates/${id}`);
    return r.data;
  },
  create: async (payload: FccsTemplateCreatePayload): Promise<FccsTemplateDTO> => {
    const r = await apiClient.post<FccsTemplateDTO>('/fccs-journal-templates', payload);
    return r.data;
  },
  update: async (id: number, payload: Partial<FccsTemplateCreatePayload>): Promise<FccsTemplateDTO> => {
    const r = await apiClient.put<FccsTemplateDTO>(`/fccs-journal-templates/${id}`, payload);
    return r.data;
  },
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/fccs-journal-templates/${id}`);
  },
  // Server-side validation, mirrors fccsJournalsApi.scan: balance, required
  // dims, account type, entity currency/parent consistency (no period gate).
  scan: async (id: number): Promise<FccsScanResult> => {
    const r = await apiClient.post<FccsScanResult>(`/fccs-journal-templates/${id}/scan`);
    return r.data;
  },
  instantiate: async (
    id: number,
    pov: { scenario: string; year: string; period: string; view_member: string; consolidation: string },
  ): Promise<{ id: number; label: string }> => {
    const r = await apiClient.post(`/fccs-journal-templates/${id}/instantiate`, pov);
    return r.data;
  },
  exportCsv: async (filter?: { scenario?: string; year?: string; period?: string; ids?: number[] }): Promise<Blob> => {
    const { ids, ...rest } = filter ?? {};
    const params: Record<string, string> = { ...(rest as any) };
    if (ids && ids.length) params.ids = ids.join(',');
    const r = await apiClient.get('/fccs-journal-templates/export/csv', {
      params, responseType: 'blob',
    });
    return r.data;
  },
  exportXlsx: async (filter?: { scenario?: string; year?: string; period?: string; ids?: number[] }): Promise<Blob> => {
    const { ids, ...rest } = filter ?? {};
    const params: Record<string, string> = { ...(rest as any) };
    if (ids && ids.length) params.ids = ids.join(',');
    const r = await apiClient.get('/fccs-journal-templates/export/xlsx', {
      params, responseType: 'blob',
    });
    return r.data;
  },
  import: async (file: File): Promise<{ created: number; skipped: number; errors: string[] }> => {
    const fd = new FormData();
    fd.append('file', file);
    const r = await apiClient.post('/fccs-journal-templates/import', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  },
  // Download a blank template-import sample in the required !TEMPLATE format.
  // The server fills in the tenant's custom-dimension column labels so a filled
  // copy imports cleanly. kind = 'csv' | 'excel'.
  downloadImportTemplate: async (kind: 'csv' | 'excel'): Promise<void> => {
    const r = await apiClient.get('/fccs-journal-templates/import-template', {
      params: { file_type: kind },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(r.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = kind === 'excel' ? 'journal_template_import.xlsx' : 'journal_template_import.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
  // Oracle FCCS "Template Export" — exports the template *definitions* as a
  // delimited text file. Standard = non-recurring templates; Recurring =
  // recurring templates. fileType picks the delimiter.
  exportTemplatesFile: async (opts: {
    standard: boolean;
    recurring: boolean;
    fileType: 'comma' | 'tab' | 'other';
    delimiter?: string;
  }): Promise<Blob> => {
    const params: Record<string, string> = {
      standard: String(opts.standard),
      recurring: String(opts.recurring),
      file_type: opts.fileType,
    };
    if (opts.fileType === 'other' && opts.delimiter) params.delimiter = opts.delimiter;
    const r = await apiClient.get('/fccs-journal-templates/export/templates', {
      params, responseType: 'blob',
    });
    return r.data;
  },
  // "Run Export as a Job" / Outbox — builds the file server-side, stores it,
  // and records a completed Job whose View/Download serves the file.
  exportTemplatesAsJob: async (opts: {
    standard: boolean;
    recurring: boolean;
    fileType: 'comma' | 'tab' | 'other';
    delimiter?: string;
    location?: 'local' | 'outbox';
  }): Promise<{ success: boolean; job_id: number; message: string }> => {
    const r = await apiClient.post('/fccs-journal-templates/export/templates/as-job', {
      standard: opts.standard,
      recurring: opts.recurring,
      file_type: opts.fileType,
      delimiter: opts.fileType === 'other' ? (opts.delimiter || '') : '',
      location: opts.location || 'outbox',
    });
    return r.data;
  },
};

// ─── FCCS Period Status (Phase 2 — PDF Step 2) ─────────────────────────────

// A journal period is Open or Closed — nothing else. Open = the full workflow;
// Closed = Create / Save / Complete / Submit only (Approve, Post, Unpost blocked).
export type FccsPeriodStatusState = 'Open' | 'Closed';

// Period-dimension status (Dimension Manager) a journal period is wired to.
export type FccsDimensionPeriodState = 'open' | 'closed' | 'locked';

export interface FccsPeriodStatusDTO {
  // null = no explicit Manage-Periods row; the state is inherited from the
  // Period dimension.
  id: number | null;
  scenario: string;
  year: string;
  period: string;
  // Effective state — exactly what the journal Approve/Post gate enforces.
  state: FccsPeriodStatusState;
  // The two inputs it derives from. `journal_state` is null when Manage Periods
  // has no row for the POV and the state is purely inherited from the dimension.
  journal_state: FccsPeriodStatusState | null;
  dimension_state: FccsDimensionPeriodState | null;
  opened_at: string | null;
  closed_at: string | null;
  opened_by: number | null;
  closed_by: number | null;
}

export const fccsPeriodStatusApi = {
  list: async (filter?: { scenario?: string; year?: string }): Promise<FccsPeriodStatusDTO[]> => {
    const r = await apiClient.get<FccsPeriodStatusDTO[]>('/fccs-period-status', { params: filter });
    return r.data;
  },
  setState: async (
    scenario: string, year: string, period: string,
    action: FccsPeriodStatusState,
  ): Promise<FccsPeriodStatusDTO> => {
    const r = await apiClient.post<FccsPeriodStatusDTO>('/fccs-period-status/action', {
      scenario, year, period, action,
    });
    return r.data;
  },
};

// Server-side scan result (PDF §13). `line` is 1-based; null = header-level.
export interface FccsScanError {
  line: number | null;
  field: string | null;
  message: string;
}
export interface FccsScanResult {
  ok: boolean;
  errors: FccsScanError[];
  total_debit: string;
  total_credit: string;
}

// ── Smart Lists API (standalone typed export) ─────────────────────────────────

export const smartListsApi = {
  list: async (): Promise<SmartList[]> =>
    (await apiClient.get('/smart-lists')).data,
  get: async (id: number): Promise<SmartListWithEntries> =>
    (await apiClient.get(`/smart-lists/${id}`)).data,
  create: async (data: SmartListCreate): Promise<SmartList> =>
    (await apiClient.post('/smart-lists', data)).data,
  update: async (id: number, data: Partial<SmartListCreate> & { is_active?: boolean }): Promise<SmartList> =>
    (await apiClient.put(`/smart-lists/${id}`, data)).data,
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/smart-lists/${id}`);
  },
  listEntries: async (listId: number): Promise<SmartListEntry[]> =>
    (await apiClient.get(`/smart-lists/${listId}/entries`)).data,
  addEntry: async (listId: number, data: { code: string; label: string; numeric_value?: number; sort_order?: number }): Promise<SmartListEntry> =>
    (await apiClient.post(`/smart-lists/${listId}/entries`, data)).data,
  updateEntry: async (listId: number, entryId: number, data: Partial<SmartListEntry>): Promise<SmartListEntry> =>
    (await apiClient.put(`/smart-lists/${listId}/entries/${entryId}`, data)).data,
  deleteEntry: async (listId: number, entryId: number): Promise<void> => {
    await apiClient.delete(`/smart-lists/${listId}/entries/${entryId}`);
  },
  reorderEntries: async (listId: number, order: { id: number; sort_order: number }[]): Promise<void> => {
    await apiClient.post(`/smart-lists/${listId}/entries/reorder`, order);
  },
  getUsage: async (id: number): Promise<SmartListUsage[]> =>
    (await apiClient.get(`/smart-lists/${id}/usage`)).data,
  exportCsv: async (): Promise<Blob> =>
    (await apiClient.get('/smart-lists/export', { responseType: 'blob' })).data,
  importCsv: async (file: File): Promise<{ created: number; updated: number }> => {
    const form = new FormData();
    form.append('file', file);
    return (await apiClient.post('/smart-lists/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  },
};

// --- UDAs (User-Defined Attributes) ------------------------------------------

export const udasApi = {
  list: async (dimensionType: string): Promise<UdaDefinition[]> =>
    (await apiClient.get('/udas', { params: { dimension_type: dimensionType } })).data,
  get: async (id: number): Promise<UdaDefinitionWithMembers> =>
    (await apiClient.get(`/udas/${id}`)).data,
  create: async (data: UdaDefinitionCreate): Promise<UdaDefinition> =>
    (await apiClient.post('/udas', data)).data,
  update: async (id: number, data: Partial<UdaDefinitionCreate>): Promise<UdaDefinition> =>
    (await apiClient.put(`/udas/${id}`, data)).data,
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/udas/${id}`);
  },
  getMembers: async (id: number): Promise<UdaMemberItem[]> =>
    (await apiClient.get(`/udas/${id}/members`)).data,
  getMemberUdas: async (dimensionType: string, memberId: number): Promise<UdaDefinition[]> =>
    (await apiClient.get(`/udas/member/${dimensionType}/${memberId}`)).data,
  setMemberUdas: async (dimensionType: string, memberId: number, udaIds: number[]): Promise<void> => {
    await apiClient.put(`/udas/member/${dimensionType}/${memberId}`, { uda_ids: udaIds });
  },
  addMemberUda: async (dimensionType: string, memberId: number, udaId: number): Promise<void> => {
    await apiClient.post(`/udas/member/${dimensionType}/${memberId}/add`, { uda_id: udaId });
  },
  removeMemberUda: async (dimensionType: string, memberId: number, udaId: number): Promise<void> => {
    await apiClient.delete(`/udas/member/${dimensionType}/${memberId}/${udaId}`);
  },
  resolveToMembers: async (dimensionType: string, udaName: string): Promise<any[]> => {
    const r = await apiClient.get(`/udas/resolve/${dimensionType}/${encodeURIComponent(udaName)}`);
    return r.data?.members ?? r.data ?? [];
  },
  bulkAssign: async (data: { uda_id: number; member_ids: number[]; dimension_type: string }): Promise<void> => {
    await apiClient.post('/udas/bulk-assign', data);
  },
  bulkRemove: async (data: { uda_id: number; member_ids: number[]; dimension_type: string }): Promise<void> => {
    await apiClient.post('/udas/bulk-remove', data);
  },
  exportCsv: async (dimensionType?: string): Promise<Blob> =>
    (await apiClient.get('/udas/export', {
      params: dimensionType ? { dimension_type: dimensionType } : {},
      responseType: 'blob',
    })).data,
  importCsv: async (file: File): Promise<{ definitions_created: number; assignments_created: number }> => {
    const form = new FormData();
    form.append('file', file);
    return (await apiClient.post('/udas/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  },
  sampleCsv: async (): Promise<Blob> =>
    (await apiClient.get('/udas/sample-csv', { responseType: 'blob' })).data,
  resolveMembers: async (dimensionType: string, udaName: string): Promise<UdaMemberItem[]> =>
    (await apiClient.get(`/udas/resolve/${dimensionType}/${encodeURIComponent(udaName)}`)).data,
  availableForDimension: async (dimensionType: string): Promise<{id: number; name: string; member_count: number}[]> =>
    (await apiClient.get(`/udas/available/${dimensionType}`)).data,
};

// --- User Variables ----------------------------------------------------------

export const userVariablesApi = {
  list: async (): Promise<any[]> => {
    const r = await apiClient.get('/user-variables');
    return r.data?.items ?? r.data ?? [];
  },
  create: async (data: { name: string; dimension_type: string; description?: string; use_context?: boolean }) => {
    const r = await apiClient.post('/user-variables', data);
    return r.data;
  },
  get: async (id: number) => (await apiClient.get(`/user-variables/${id}`)).data,
  update: async (id: number, data: Record<string, unknown>) => {
    const r = await apiClient.put(`/user-variables/${id}`, data);
    return r.data;
  },
  delete: async (id: number) => { await apiClient.delete(`/user-variables/${id}`); },
  getMyValues: async (): Promise<any[]> => {
    const r = await apiClient.get('/user-variables/my-values');
    return r.data?.items ?? r.data ?? [];
  },
  setMyValues: async (values: { variable_id: number; member_code: string }[]) => {
    const r = await apiClient.put('/user-variables/my-values', { values });
    return r.data;
  },
};

// --- Substitution Variables --------------------------------------------------

export const substitutionVariablesApi = {
  list: async (params?: Record<string, unknown>): Promise<any[]> => {
    const r = await apiClient.get('/substitution-variables', params ? { params } : undefined);
    return r.data?.items ?? r.data ?? [];
  },
  create: async (data: Record<string, unknown>) => {
    const r = await apiClient.post('/substitution-variables', data);
    return r.data;
  },
  get: async (id: number) => (await apiClient.get(`/substitution-variables/${id}`)).data,
  update: async (id: number, data: Record<string, unknown>) => {
    const r = await apiClient.put(`/substitution-variables/${id}`, data);
    return r.data;
  },
  delete: async (id: number) => { await apiClient.delete(`/substitution-variables/${id}`); },
  seed: async () => {
    const r = await apiClient.post('/substitution-variables/seed');
    return r.data;
  },
};

// --- Custom Attributes API (FCCS §4-5) ----------------------------------------

export const customAttributesApi = {
  list: async (dimensionType?: string): Promise<any[]> => {
    const params = dimensionType ? { dimension_type: dimensionType } : {};
    const r = await apiClient.get('/custom-attributes', { params });
    return r.data?.items ?? r.data ?? [];
  },
  create: async (data: { name: string; dimension_type: string; data_type: string; description?: string }) => {
    return (await apiClient.post('/custom-attributes', data)).data;
  },
  get: async (id: number) => (await apiClient.get(`/custom-attributes/${id}`)).data,
  update: async (id: number, data: Record<string, unknown>) => {
    return (await apiClient.put(`/custom-attributes/${id}`, data)).data;
  },
  delete: async (id: number) => { await apiClient.delete(`/custom-attributes/${id}`); },
  addValue: async (id: number, data: { value: string; sort_order?: number }) => {
    return (await apiClient.post(`/custom-attributes/${id}/values`, data)).data;
  },
  updateValue: async (id: number, vid: number, data: Record<string, unknown>) => {
    return (await apiClient.put(`/custom-attributes/${id}/values/${vid}`, data)).data;
  },
  deleteValue: async (id: number, vid: number) => {
    await apiClient.delete(`/custom-attributes/${id}/values/${vid}`);
  },
  getMemberAssignments: async (dimensionType: string, memberCode: string): Promise<any[]> => {
    const r = await apiClient.get(`/custom-attributes/member/${dimensionType}/${encodeURIComponent(memberCode)}`);
    return r.data?.items ?? r.data ?? [];
  },
  setMemberAssignments: async (dimensionType: string, memberCode: string, assignments: { attribute_value_id: number }[]) => {
    return (await apiClient.put(`/custom-attributes/member/${dimensionType}/${encodeURIComponent(memberCode)}`, { assignments })).data;
  },
  getDimensionTypes: async (): Promise<{ value: string; label: string; dimType: string; group: string }[]> => {
    const r = await apiClient.get('/custom-attributes/dimension-types');
    return r.data ?? [];
  },
  resolveToMembers: async (dimensionType: string, attributeName: string, valueName: string): Promise<{ member_codes: string[]; count: number }> => {
    const r = await apiClient.get(`/custom-attributes/resolve/${dimensionType}/${encodeURIComponent(attributeName)}/${encodeURIComponent(valueName)}`);
    return r.data;
  },
};

// ---------------------------------------------------------------------------
// Entity Calc Status — Oracle FCCS 5-state calc-status grid (US-R5.3)
// ---------------------------------------------------------------------------

// 'System Changed' is the FCCS UI label the API emits since US-DS-FIX D4;
// 'System Change' (Oracle Table 20-1 stored value) is kept for any older payload.
export type CalcStatusValue = 'OK' | 'No Data' | 'Impacted' | 'Needs Translation' | 'System Changed' | 'System Change';

export interface EntityCalcStatusRow {
  entity_id: number;
  entity_code: string;
  entity_name: string;
  currency_code: string;
  status: CalcStatusValue | string;
  set_by: string;
  set_at: string | null;
}

export const entityCalcStatusApi = {
  grid: async (scenario_id: number, period_id: number): Promise<EntityCalcStatusRow[]> =>
    (await apiClient.get<EntityCalcStatusRow[]>('/entity-calc-status', { params: { scenario_id, period_id } })).data,
  recompute: async (scenario_id: number, period_id: number): Promise<{ reset: number; grid: EntityCalcStatusRow[] }> =>
    (await apiClient.post('/entity-calc-status/recompute', { scenario_id, period_id })).data,
  set: async (data: { scenario_id: number; period_id: number; entity_ids: number[]; status: string; currency_code?: string }):
    Promise<{ updated: number; status: string }> =>
    (await apiClient.post('/entity-calc-status/set', data)).data,
};

// ---------------------------------------------------------------------------
// SDM Rates — supplemental rate entry + RefreshSDMCurrencyRates sync (US-R5.1)
// ---------------------------------------------------------------------------

export interface SdmRateEntryDTO {
  id: number;
  rate_account_id: number;
  rate_account_name: string;
  entity_id: number | null;
  entity_code: string | null;
  period_id: number;
  rate: number;
  source: 'manual' | 'import' | string;
  entered_by: string | null;
  entered_at: string | null;
}

export interface SdmSyncSummary {
  synced: number;
  skipped: number;
  total: number;
  skipped_detail: { rate_account: string; reason: string }[];
}

export const sdmRatesApi = {
  list: async (period_id: number): Promise<SdmRateEntryDTO[]> =>
    (await apiClient.get<SdmRateEntryDTO[]>('/sdm-rates', { params: { period_id } })).data,
  save: async (data: { rate_account_id: number; entity_id?: number | null; period_id: number; rate: number | string }):
    Promise<SdmRateEntryDTO> =>
    (await apiClient.post<SdmRateEntryDTO>('/sdm-rates', data)).data,
  importCsv: async (file: File): Promise<{ imported: number; errors: { row: number; error: string }[] }> => {
    const form = new FormData();
    form.append('file', file);
    return (await apiClient.post('/sdm-rates/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  },
  sync: async (period_id: number): Promise<SdmSyncSummary> =>
    (await apiClient.post<SdmSyncSummary>('/sdm-rates/sync', { period_id })).data,
};

// --- Calc Rule XML Transfer API (US-R4.4 import/export) -----------------------

export interface CalcRuleImportReport {
  rules_created: number;
  created: string[];
  skipped: { name: string; action: string }[];
  ignored_deployed: number;
  warnings: { rule: string; member: string; dimension_guess: string }[];
}

export const calcRuleTransferApi = {
  /** Export one rule as XML (Oracle deployed_status semantics). Returns the raw XML text. */
  exportRule: async (ruleId: number): Promise<string> => {
    const r = await apiClient.get(`/calc-rules/${ruleId}/export`, { responseType: 'text' });
    return r.data as string;
  },
  /** Export all tenant rules (optionally filtered by kind / cube) as one XML document. */
  exportAll: async (params?: { kind?: string; cube?: string }): Promise<string> => {
    const r = await apiClient.get('/calc-rules/export-all', { params, responseType: 'text' });
    return r.data as string;
  },
  /** Import an XML export (multipart upload). Only deployed_status="false" entries are processed. */
  importXml: async (file: File): Promise<CalcRuleImportReport> => {
    const form = new FormData();
    form.append('file', file);
    const r = await apiClient.post('/calc-rules/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  },
};

// --- Groovy Script Validator API (US-R4.2 AC3) --------------------------------

export const groovyValidatorApi = {
  /** Gate for the validator action — true when the tenant has ≥1 Groovy rule. */
  hasGroovyRules: async (): Promise<boolean> => {
    const r = await apiClient.get('/groovy-validator/has-groovy-rules');
    return Boolean(r.data?.has_groovy_rules);
  },
  /** Run the validator over all tenant Groovy rules; returns the JSON report. */
  run: async (): Promise<{
    generated_at: string;
    rules: {
      rule_kind: 'on_demand' | 'calc';
      rule_id: number;
      rule_name: string;
      errors: { class_name: string; line: number; message: string }[];
      warnings: string[];
      rtps: string[];
    }[];
    summary: {
      total_rules: number;
      rules_with_errors: number;
      total_errors: number;
      total_warnings: number;
    };
  }> => (await apiClient.post('/groovy-validator/run')).data,
  /** Fetch GroovyValidationReport.html as text (open via a Blob URL — the
   *  endpoint needs the bearer header, so a plain window.open cannot be used). */
  reportHtml: async (): Promise<string> => {
    const r = await apiClient.get('/groovy-validator/report.html', { responseType: 'text' });
    return r.data as string;
  },
};

/**
 * Drop every browser-held cache so the next render re-fetches from the server.
 *
 * Clearing the server's Redis / rollup caches alone does NOT give a fresh page: the tab still
 * holds an in-flight GET de-dup map, sessionStorage scratch caches, Zustand stores and React
 * state, all of which keep serving the values already in memory. This purges the storage layers
 * the app owns; the caller is expected to follow with a hard reload, which is what actually
 * discards in-memory state.
 *
 * Auth tokens are preserved on purpose — "clear the cache" must not log the user out.
 * localStorage is left alone apart from known cache entries because it also holds user
 * PREFERENCES (grid layouts, recent searches, panel state); wiping those would be data loss
 * dressed up as a cache clear.
 */
/**
 * `localStorage` key prefixes that hold re-derivable view state, and are therefore fair game for
 * "Clear cache". Deny-by-default: `localStorage` also holds the auth tokens and real user
 * preferences (settings, column labels, workbench view, dismissed notices), and the Clear-cache
 * dialog promises "You stay signed in, saved preferences are kept" — so a wholesale
 * `localStorage.clear()` would break that promise and sign the user out.
 *
 * `formDataEntry.*` — per-form Page / POV / User-Variable picks. These now live in `sessionStorage`;
 * entries under this prefix are leftovers from when they were persisted permanently. They are
 * exactly what made "Clear cache" look broken: the button dropped `sessionStorage` only, so a stale
 * slice in `localStorage` survived every clear, every Refresh and every browser cache purge, and the
 * form kept opening on a POV holding no data while incognito showed the form's real defaults.
 */
const CACHE_LOCAL_STORAGE_PREFIXES = ['formDataEntry.'] as const;

export function clearClientCaches(): {
  inflightDropped: number;
  sessionKeysDropped: number;
  localKeysDropped: number;
} {
  const inflightDropped = _inflightGets.size;
  _inflightGets.clear();

  let sessionKeysDropped = 0;
  try {
    // sessionStorage is per-tab scratch space — cache by nature, safe to drop wholesale.
    sessionKeysDropped = window.sessionStorage.length;
    window.sessionStorage.clear();
  } catch {
    // Storage can throw in private-browsing / disabled-storage modes; a failed purge here
    // must not block the rest of the clear.
  }

  let localKeysDropped = 0;
  try {
    // Collect first, delete after: removing during the index walk shifts every later key.
    const doomed: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && CACHE_LOCAL_STORAGE_PREFIXES.some((p) => key.startsWith(p))) doomed.push(key);
    }
    for (const key of doomed) window.localStorage.removeItem(key);
    localKeysDropped = doomed.length;
  } catch {
    // Same private-browsing tolerance as above.
  }

  return { inflightDropped, sessionKeysDropped, localKeysDropped };
}

// ─── Historical Translation Overrides ────────────────────────────────────────
// Per-account historical rate/amount overrides for accounts tagged
// exchange_rate_type = historical / historical_rate / historical_amount.
// Stored in consol_fact with consolidation_code = HIST_AMOUNT_OVERRIDE or
// HIST_RATE_OVERRIDE. The translation engine reads these at calc-time.

export interface HistoricalOverrideAccountItem {
  account_id: number;
  code: string;
  name: string;
  account_type: string | null;
  exchange_rate_type: string | null;
  existing_override_type: 'amount' | 'rate' | null;
  existing_override_value: number | null;
}

export interface HistoricalOverrideTemplate {
  entity_id: number;
  entity_code: string | null;
  entity_name: string | null;
  parent_currency: string;
  accounts: HistoricalOverrideAccountItem[];
}

export interface HistoricalOverrideDTO {
  id: number;
  entity_id: number;
  account_id: number;
  period_id: number;
  scenario_id: number;
  fiscal_year_id: number;
  currency_code: string;
  override_type: 'amount' | 'rate';
  override_value: number;
  account_code?: string | null;
  account_name?: string | null;
}

export interface HistoricalOverrideList {
  overrides: HistoricalOverrideDTO[];
  total: number;
}

export const historicalTranslationOverridesApi = {
  /** List all overrides for a given POV (entity + period + scenario + currency). */
  list: async (params: {
    entity_id: number; period_id: number; scenario_id: number; currency_code: string;
  }): Promise<HistoricalOverrideList> => {
    const r = await apiClient.get('/historical-translation-overrides', { params });
    return r.data;
  },

  /** Upsert a single override (insert or update). */
  upsert: async (payload: {
    entity_id: number;
    account_id: number;
    period_id: number;
    scenario_id: number;
    fiscal_year_id: number;
    currency_code: string;
    override_type: 'amount' | 'rate';
    override_value: number | string;
  }): Promise<HistoricalOverrideDTO> => {
    const r = await apiClient.put('/historical-translation-overrides', payload);
    return r.data;
  },

  /** Delete an override for a given POV. */
  remove: async (params: {
    entity_id: number; account_id: number; period_id: number; scenario_id: number; currency_code: string;
  }): Promise<{ deleted: number }> => {
    const r = await apiClient.delete('/historical-translation-overrides', { params });
    return r.data;
  },

  /** Get the template: accounts with exchange_rate_type containing 'historical' for an entity. */
  template: async (params: {
    entity_id: number; period_id: number; scenario_id: number;
  }): Promise<HistoricalOverrideTemplate> => {
    const r = await apiClient.get('/historical-translation-overrides/template', { params });
    return r.data;
  },
};

// ─── Consolidation Logic — transparent, toggleable engine rule catalog ──────

export interface ConsolidationLogicRuleDTO {
  id: number;
  rule_key: string;
  step_no: number;
  sequence: number;
  category: string;
  name: string;
  description: string;
  logic_text: string;
  source: string;
  config_json: Record<string, unknown> | null;
  is_active: boolean;
  is_system: boolean;
  updated_at?: string | null;
}

export interface ConsolidationLogicStepDTO {
  step_no: number;
  step_code: string;
  step_name: string;
  rules: ConsolidationLogicRuleDTO[];
}

export interface ConsolidationLogicListDTO {
  steps: ConsolidationLogicStepDTO[];
  total_rules: number;
  active_rules: number;
}

export interface ConsolidationLogicRunTraceDTO {
  run_id: number;
  status: string;
  started_at?: string | null;
  completed_at?: string | null;
  steps: unknown;
  skipped_rules: string[];
}

export const consolidationLogicApi = {
  list: async (): Promise<ConsolidationLogicListDTO> =>
    (await apiClient.get<ConsolidationLogicListDTO>('/consolidation-logic')).data,
  toggle: async (id: number, is_active: boolean): Promise<ConsolidationLogicRuleDTO> =>
    (await apiClient.patch<ConsolidationLogicRuleDTO>(`/consolidation-logic/${id}/toggle`, { is_active })).data,
  updateConfig: async (id: number, config_json: Record<string, unknown>): Promise<ConsolidationLogicRuleDTO> =>
    (await apiClient.patch<ConsolidationLogicRuleDTO>(`/consolidation-logic/${id}/config`, { config_json })).data,
  resetDefaults: async (): Promise<ConsolidationLogicListDTO> =>
    (await apiClient.post<ConsolidationLogicListDTO>('/consolidation-logic/reset-defaults')).data,
  runTrace: async (runId: number): Promise<ConsolidationLogicRunTraceDTO> =>
    (await apiClient.get<ConsolidationLogicRunTraceDTO>(`/consolidation-logic/run-trace/${runId}`)).data,
};

// ─── Intercompany Matching Report (US-ICPR-001) ────────────────────────────

export interface ICMatchingReportCurrencyLens {
  type: 'entity' | 'parent' | 'reporting';
  currency_code?: string | null;
}

export interface ICMatchingReportEntityScope {
  parent_entity_id: number | null;
  hierarchy_id?: number | null;
  entity_ids?: number[] | null;
  /** Union of ILvl0Descendants for every selected parent (US-ICPR-001 multi-select). */
  parent_entity_ids?: number[] | null;
}

export interface ICMatchingReportPartnerScope {
  select_from_entity: boolean;
  parent_entity_id?: number | null;
  entity_ids?: number[] | null;
  /** Union of ILvl0Descendants for every selected parent (US-ICPR-001 multi-select). */
  parent_entity_ids?: number[] | null;
  /** ICP member ids selecting partners directly (US-ICPR-001 "Choose ICP members" mode). */
  icp_ids?: number[] | null;
}

export interface ICMatchingReportRequest {
  scenario_id: number;
  period_id: number;
  view: 'periodic' | 'ytd';
  currency_lens: ICMatchingReportCurrencyLens;
  entity_scope: ICMatchingReportEntityScope;
  partner_scope: ICMatchingReportPartnerScope;
  movement_code?: string | null;
  data_source_id?: number | null;
  custom1_id?: number | null;
  custom2_id?: number | null;
  custom3_id?: number | null;
  custom4_id?: number | null;
  plug_account_ids?: number[] | null;
  movement_codes?: string[] | null;
  data_source_ids?: number[] | null;
  custom1_ids?: number[] | null;
  custom2_ids?: number[] | null;
  custom3_ids?: number[] | null;
  custom4_ids?: number[] | null;
  suppress_matches: boolean;
  suppress_reversals: boolean;
  suppress_blank_columns: boolean;
  tolerance_value: number;
  tolerance_pct: number;
  tolerance_basis?: 'total' | 'legs';
  scale_factor: number;
  decimal_override: number;
  icp_display: 'with_prefix' | 'without_prefix';
  member_display: 'name' | 'description' | 'both';
  accounts_in_rows: boolean;
  run_id?: number | null;
}

export interface ICMatchingReportPovRow {
  label: string;
  value: string;
  note: string | null;
}

export interface ICMatchingReportHeader {
  application: string;
  generated_at: string;
  user: string;
  cube: string;
  pov: Record<string, unknown>;
  /** Fixed 28-row Oracle-style header block (Application..Accounts in Rows), in order. */
  pov_rows?: ICMatchingReportPovRow[];
  warnings: string[];
  run_id?: number | null;
}

export interface ICMatchingReportColumn {
  key: string;
  leg?: number;
  side?: 'entity' | 'partner';
  account_id?: number;
  label: string;
}

export interface ICMatchingReportEliminatingAccount {
  id: number;
  code: string;
  name: string;
  account_type: string;
  side: 'entity' | 'partner';
}

export interface ICMatchingReportLegAmounts {
  entity_amount: string | null;
  partner_amount: string | null;
}

export interface ICMatchingReportRow {
  kind: 'detail' | 'entity_subtotal' | 'grand_total' | 'pair_total';
  entity: { id: number; code: string; name: string } | null;
  partner: { entity_id: number; icp_code: string; label: string } | null;
  cells: Record<string, string | null>;
  matched: boolean;
  self_pair: boolean;
  currency?: string | null;
  /** Accounts-in-rows mode only (§4.6): the account this detail row pivots on. */
  account_id?: number | null;
  leg1?: ICMatchingReportLegAmounts | null;
  leg2?: ICMatchingReportLegAmounts | null;
}

export interface ICMatchingReportSection {
  plug_account: { id: number; code: string; name: string };
  eliminating_accounts: ICMatchingReportEliminatingAccount[];
  columns: ICMatchingReportColumn[];
  rows: ICMatchingReportRow[];
}

export interface ICMatchingReportResponse {
  header: ICMatchingReportHeader;
  sections: ICMatchingReportSection[];
}

export interface ICMatchingReportDimensionMember {
  id: number;
  code: string;
  name: string;
  /** US-ICPR-002: parent member's code — lets the picker render an Available tree. */
  parent_code?: string | null;
  sort_order?: number | null;
}

export interface ICMatchingReportMovementMember {
  code: string;
  name: string;
  /** US-ICPR-002 tree link (see ICMatchingReportDimensionMember.parent_code). */
  parent_code?: string | null;
  sort_order?: number | null;
}

export interface ICMatchingReportEntityMember {
  id: number;
  code: string;
  name: string;
  kind: 'parent' | 'base';
  /** Count of base entities under this node (ILvl0Descendants); null for a base entity. */
  base_count: number | null;
  /** Tree depth, for indentation — parents appear before their children. */
  depth: number;
  /**
   * US-ICPR-002: code of the nearest OFFERED ancestor (nodes the catalogue skips
   * are bridged), so the picker tree is always connected.
   */
  parent_code?: string | null;
}

export interface ICMatchingReportIcpMember {
  id: number;
  code: string;
  name: string;
  entity_id: number;
  entity_code: string;
  /** US-ICPR-002 tree link (ICP members hang off the FCCS_Intercompany Top node). */
  parent_code?: string | null;
}

export interface ICMatchingReportDimensionMembers {
  plug_accounts: ICMatchingReportDimensionMember[];
  movements: ICMatchingReportMovementMember[];
  data_sources: ICMatchingReportDimensionMember[];
  custom1: ICMatchingReportDimensionMember[];
  custom2: ICMatchingReportDimensionMember[];
  custom3: ICMatchingReportDimensionMember[];
  custom4: ICMatchingReportDimensionMember[];
  /** US-ICPR-001 multi-entity picker catalogue — parents and base entities, tree-ordered. */
  entities?: ICMatchingReportEntityMember[];
  /** US-ICPR-001 "Choose ICP members" partner picker — pairable ICP members only, code-ordered. */
  icp_members?: ICMatchingReportIcpMember[];
}

export const icMatchingReportApi = {
  generate: async (body: ICMatchingReportRequest): Promise<ICMatchingReportResponse> =>
    (await apiClient.post<ICMatchingReportResponse>('/ic-matching-report/generate', body)).data,
  /** US-ICPR-005: dispatch the XLSX build as a background Job; download from the Jobs page. */
  exportJob: async (
    body: ICMatchingReportRequest,
  ): Promise<{ success: boolean; message: string; job_id: number; task_id: string }> =>
    (await apiClient.post('/ic-matching-report/export-job', body)).data,
  exportXlsx: async (body: ICMatchingReportRequest): Promise<Blob> =>
    (await apiClient.post('/ic-matching-report/export', body, {
      params: { format: 'xlsx' },
      responseType: 'blob',
    })).data,
  dimensionMembers: async (): Promise<ICMatchingReportDimensionMembers> =>
    (await apiClient.get<ICMatchingReportDimensionMembers>('/ic-matching-report/dimension-members')).data,
};

export default apiClient;
